import { UnseededRNG } from './rng.js';
import { inventory } from './inventory.js';
import { REEL_3_STYLES, REEL_3_STYLES_MAP } from './slotMachine.js';
import { getGoldCardImage, getCardByName, CARDS_DATA } from '../data/cards.js';
import { getCustomDeathSound } from '../data/unitSounds.js';

// Units use the design's original default speeds scaled for the 1600px
// battlefield canvas. Speed 1.4 = 36.4 px/s, Speed 2.25 (Marine) = 58.5 px/s,
// Speed 3.85 = 100.1 px/s, Speed 4.72 = 122.7 px/s.
const SPEED_MULTIPLIER = 26; // design speed units → canvas px/sec

// Monotonic counter instead of Math.random()-derived IDs - nothing reads
// unit.id for gameplay logic today, but a deterministic sim shouldn't lean
// on Math.random() for anything, even identifiers, on principle.
let unitIdCounter = 0;

// Static pilot card template (Zero-Allocation & invariant #7)
const PARACHUTE_PILOT_CARD = Object.freeze({
  name: "Marine", race: "Human", hp: 45, damage: 6, attackSpeed: 0.4,
  range: 5.0, speed: 2.25, deflectionRating: 0, accuracy: 0.8,
  projectileType: "Bullet", elementType: "Ground",
  targetsAir: true, targetsGround: true, images: ["Marine_1.png"], tier: 1,
  description: "Basic infantry unit", lore: "Why me?"
});

// Sentinal Lock's toxic bite: a lobbed bio-glob (particles.addSporeLob),
// not the fungal-green/violet Apostate Loaf theme it's built on. Default
// glob color when the player hasn't equipped a custom bullet-color style
// (see Unit.getCustomBulletColorHex()) - that per-unit customization, via
// the Slot Machine/Arsenal Reel-3 styles, already overrides the dominant
// glob color for free; this palette only re-themes the secondary trail/
// impact sparks so they read as pure toxic neon yellow instead of
// Apostate's mixed fungal palette.
export const SENTINAL_LOCK_GLOB_COLOR = '#eaff00';
// Passed as addSporeLob()'s `options` - see that function's doc comment.
export const SENTINAL_LOCK_LOB_OPTIONS = Object.freeze({
  palette: Object.freeze({
    trail: ['#eaff00', '#fef08a', '#ccff00', '#a3e600'],
    drip: ['#ccff00', '#eaff00'],
    splatter: ['#eaff00', '#fef08a', '#ccff00', '#a3e600'],
    shockwave: '#eaff00',
    explosion: '#ccff00',
    scorch: 'rgba(60, 70, 10, 0.45)'
  }),
  // Near-horizontal bite, not Apostate's steep mortar lob - ~5° above the
  // muzzle instead of ~45°ish (see addSporeLob's arcAngleDeg handling).
  arcAngleDeg: 5,
  // Half the size of Apostate's fungal clump (addSporeLob's sizeScale).
  sizeScale: 0.5,
  // Unique exception: Sentinal Lock projectiles cause no smoke (no in-flight smoke wake or impact smoke)
  noSmoke: true
});

// Lightens (positive percent) or darkens (negative percent) a '#rrggbb' hex
// color - used to derive the Sentinal Lock smoke/trail palette below from
// whatever glob color is active (default toxic yellow, or an Arsenal/Slot
// Machine skin override), so the smoke always reads as a shade of the actual
// projectile color instead of a fixed hue.
function shadeHexColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const clamp = (v) => Math.max(0, Math.min(255, v));
  const r = clamp((num >> 16) + Math.round(255 * percent));
  const g = clamp(((num >> 8) & 0x00ff) + Math.round(255 * percent));
  const b = clamp((num & 0x0000ff) + Math.round(255 * percent));
  return '#' + (0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1);
}

// Builds a Sentinal Lock smoke/trail palette (same shape as
// SENTINAL_LOCK_LOB_OPTIONS.palette above) tinted around `hex` - called with
// the active glob color so a custom bullet-color skin re-colors the trailing
// spore smoke/splatter/shockwave too, not just the glob body itself.
export function buildSentinalGlobPalette(hex) {
  return {
    trail: [hex, shadeHexColor(hex, 0.35), shadeHexColor(hex, -0.1), shadeHexColor(hex, -0.25)],
    drip: [shadeHexColor(hex, -0.1), hex],
    splatter: [hex, shadeHexColor(hex, 0.35), shadeHexColor(hex, -0.1), shadeHexColor(hex, -0.25)],
    shockwave: hex,
    explosion: shadeHexColor(hex, -0.1),
    scorch: 'rgba(60, 70, 10, 0.45)'
  };
}

// SentinalLock render/units.js draws air units 18px above their logical
// this.y (unitMixin.renderUnit's `renderY = unit.y - 18 + bounceY`), and
// the head itself sits a little further up still inside that sprite (see
// the snout-tip glow offset in render/units.js's SentinalLock branch) - so
// launching the glob from plain `this.y` visibly spawned it on the ground
// under the unit instead of out of its mouth.
export const SENTINAL_LOCK_MUZZLE_OFFSET_Y = -22;

// Marine & Hyper Marine: The C-14 Gauss rifle is held at chest level (row 8.5/9
// in the 24x24 HD sprite grid), ~5.5px above unit center (this.y). Launching
// from plain this.y spawned the muzzle flash, tracer, and shell casing visibly
// under the weapon (at the belt/waist) instead of from the barrel muzzle.
export const MARINE_MUZZLE_OFFSET_Y = -5.5;
export const HYPER_MARINE_MUZZLE_OFFSET_Y = -6.5;
// A9 Piranha sits at air altitude (renderY = unit.y - 18). Its dual wing autocannon
// pylons sit at -2px and +3px relative to air altitude (-20px and -15px from ground anchor).
export const A9_PIRANHA_MUZZLE_OFFSET_Y = -17.5;
export const A9_GRIZZLY_MUZZLE_OFFSET_Y = -13.0;
export const A9_PIGEON_MUZZLE_OFFSET_Y = -16.5;
export const PYLON_OFFSETS = Object.freeze([-20, -15]);
export const A9_PIRANHA_PYLON_OFFSETS = PYLON_OFFSETS;
// Panzer & Deployable Cannon: Main cannon bores sit ~8.5px above unit center / ground tracks
export const PANZER_MUZZLE_OFFSET_Y = -8.5;
export const DEPLOYABLE_CANNON_MUZZLE_OFFSET_Y = -8.5;
// Hyper Devil Patrol: Air hover unit (isAir:true). Grenade launcher nozzle is at sprite
// row 7/24 (≈29% from top). Air units render visually ~18px above this.y (ground anchor).
// Combined offset brings muzzle flash to the weapon barrel tip in world coords.
export const HYPER_DEVIL_PATROL_MUZZLE_OFFSET_Y = -14.5;

let stickmanDeathPoseCounter = 0;

// Pre-allocated scratch array for Glaive Wurm multi-bounce candidate scans
// and Decoy Changeling candidate scans (Paradigm 1: Zero-Allocation)
const _bounceCandidatesScratch = [];
const _decoyCandidatesScratch = [];
const _hitSetScratch = new Set();
// Pre-allocated scratch array for getObstacleAvoidanceTargetY()'s ally
// congestion check - see the collection site below for why this is a safe
// superset of that check's own candidate filter (Paradigm 1: Zero-Allocation,
// docs/PERFORMANCE_AND_STABILITY_AUDIT.md follow-up: this used to be a
// second full O(N) battlefield.units scan on top of the ally-obstacle scan
// directly above it, per-unit per-tick).
const _avoidCongestionScratch = [];

// Reusable zero-allocation Sets and palettes for movement VFX (Invariante #1 & #7)
const MECH_HEAVY_NAMES = new Set(['Panzer', 'HyperPanzer', 'Hyper Panzer']);
const DIESEL_EXHAUST_COLORS = Object.freeze(['#0f172a', '#1e293b', '#334155', '#475569']);
const AIR_THRUSTER_NAMES = new Set(['Rocketship', 'A9Piranha', 'A9Grizzly', 'A9 Grizzly', 'A9Pigeon', 'A9 Pigeon']);
const JET_PLUME_COLORS = Object.freeze(['#00f0ff', '#38bdf8', '#ffffff', '#f97316']);
const WHEELIE_SPARK_PALETTE = Object.freeze(['#fde047', '#fb923c', '#f97316', '#ef4444']);
const STALK_INFANTRY_TYPES = Object.freeze(new Set([
  'Marine', 'HyperMarine', 'Shade', 'HyperShade', 'DevilPatrol',
  'Hyper Marine', 'Hyper Shade', 'Devil Patrol'
]));

function isInfantryUnit(u) {
  if (!u) return false;
  if (u.cardData?.isInfantry === true) return true;
  const type = u.cardData?.unitType || u.unitType;
  if (type && STALK_INFANTRY_TYPES.has(type)) return true;
  const name = u.cardData?.name || u.name;
  if (name && STALK_INFANTRY_TYPES.has(name)) return true;
  return false;
}

export class Unit {
  constructor(cardData, team, x, y, options = {}) {
    const extraArg = arguments.length > 5 ? arguments[5] : null;
    if (typeof options === 'number' && extraArg && typeof extraArg === 'object') {
      options = extraArg;
    } else if (options && typeof options.next === 'function') {
      options = { rng: options, battlefield: (extraArg && extraArg.units) ? extraArg : null };
    } else if (!options || typeof options !== 'object') {
      options = {};
    }
    // Prefer a battlefield-assigned id (Battlefield.nextUnitId(), reset per
    // match) so two peers running the same command sequence assign
    // identical ids to identically-spawned units - CardSystem.executeSpell's
    // clone_unit/upper_hand resolve their target by this id (see
    // Game.enqueueCommand() in js/main.js), so it must match across peers.
    // Falls back to the module counter only for the rare case a Unit is
    // built without a battlefield in scope.
    this.id = options.id || ('unit_' + (unitIdCounter++));
    // Simulation-critical randomness (attack timing, target picks, spawn
    // offsets of child units) goes through this RNG so two clients running
    // the same command sequence with the same seed reach the same state.
    // Purely cosmetic randomness below (wobble, particles) keeps Math.random().
    this._rng = options.rng || UnseededRNG;
    this.battlefield = (options && options.units) ? options : ((extraArg && extraArg.units) ? extraArg : (options.battlefield || null));
    this.cardData = cardData;
    this.name = cardData.name;
    this.race = cardData.race || 'Human';
    this.team = team;
    // Cosmetic special-edition marker (e.g. 'gold'), resolved once at
    // Game.enqueueCommand() from the playing client's own inventory and
    // carried across the network as a PLAY command field - see
    // docs/INVARIANTS.md rule 1. Never re-derived locally.
    this.edition = options.edition || null;
    this.customAuraHex = options.customAuraHex || null;
    this.customBulletHex = options.customBulletHex || null;
    this.spawnEffectPending = (this.edition === 'gold');
    this._midasTrailTimer = 0;
    // Deterministic retarget phase offset (Paradigm 1: Lockstep safe)
    this._targetOffset = (this.id.charCodeAt(this.id.length - 1) || 0) % 6;
    
    // Position & Physics
    this.x = x;
    this.y = y;
    this.targetY = y;
    // Structure & Mobility
    this.isStructure = !!cardData.isStructure || cardData.type === 'Structure';
    this.reloadSkipCount = cardData.reloadSkipCount || (cardData.name === 'Deployable Cannon' ? 4 : 0);
    this.shotCount = 0;
    this.dualMissile = !!cardData.dualMissile || (cardData.name === 'Rocketship');
    this.dualWeapon = !!cardData.dualWeapon;
    this.burstDuration = cardData.burstDuration || 0;
    this.burstTimer = options.burstTimer !== undefined ? options.burstTimer : 0;
    this.reloadDuration = cardData.reloadDuration || 0;
    this.isReloading = options.isReloading !== undefined ? options.isReloading : false;
    this.rocketsFired = options.rocketsFired || 0;
    this.deployProgress = options.deployProgress !== undefined ? options.deployProgress : 0.0;
    this.outOfCombatTimer = 0;
    this.ejectPilotOnDeath = !!cardData.ejectPilotOnDeath;
    this.isParachuting = !!options.isParachuting;
    this.parachuteTimer = options.parachuteTimer || (this.isParachuting ? 2.0 : 0);
    this.parachuteDuration = options.parachuteDuration || 2.0;
    this.parachuteAltitude = options.parachuteAltitude || (this.isParachuting ? 36 : 0);
    // Cinematic actors (docs/game-design-singleplayer-kampagne.md Kapitel
    // 1.3): additive, defaults false, only ever set via
    // spawnRaw(..., {isCinematic:true}) from CutscenePlayer. Excludes a unit
    // from targeting/attacking/marching while a cutscene script drives its
    // x/y directly - structurally identical to isParachuting's own opt-in
    // pattern above.
    this.isCinematic = !!options.isCinematic;
    // Purely a render-branch selector for a permanent, non-actor campaign
    // set-dressing prop (docs/singleplayer implementation soft-foraging-
    // flamingo.md) - a PreserverLock lying on the ground, body-only, no
    // legs, rotated on its side (js/engine/render/units.js's PreserverLock
    // branch). Additive, defaults false; only ever paired with
    // isCinematic:true (js/main.js's startCampaignMission()), so it's
    // already covered by every isCinematic targeting/marching/damage
    // exclusion above - this flag never touches simulation, only drawing.
    this.isCinematicProp = !!options.isCinematicProp;
    this.isRetreating = !!options.isRetreating;
    this.isBastionRearGuard = !!options.isBastionRearGuard;
    this.isPriorityTarget = !!options.isPriorityTarget;
    this.barrelHeat = 0.0; // Thermal barrel glow dissipation factor (1.0 -> 0.0)
    this.dustTimer = 0.0;
    this.driveAudioPending = (this.cardData?.unitType === 'WheelieBomber' || this.name === 'Wheelie Bomber');
    // A9 Piranha: durchgehendes Motor-Loop ab dem ersten Simulationsframe,
    // solange die Einheit lebt (Nutzer-Feedback). motorLoopActive verhindert
    // ein doppeltes stopA9PiranhaMotorLoop() in die(), falls der Loop nie
    // gestartet wurde (z.B. audio-Objekt fehlt).
    this.motorLoopPending = (this.cardData?.unitType === 'A9Piranha' || this.name === 'A9 Piranha');
    this.motorLoopActive = false;

    // Sul Enforcer Vulture: 8-pod independent multi-missile battery & active rocket trophy interception
    this.isEnforcerVulture = (this.name === 'Enforcer Vulture' || this.cardData?.unitType === 'EnforcerVulture');
    if (this.isEnforcerVulture) {
      this.vulturePods = Array.from({ length: 8 }, (_, i) => ({
        index: i,
        reloadTimer: 0,
        reloadDuration: this.attackSpeed || 5.0,
        lastLaunchFlash: 0
      }));
      this.vultureLaunchQueue = [];
      this._vultureLaunchTimer = 0;
      this._vultureRecoil = 0;
    }

    // Sul Looting Vulture: Transmutes first enemy into indestructible golden monument & 50% kill plasma bounty
    this.isLootingVulture = (this.name === 'Looting Vulture' || this.cardData?.unitType === 'LootingVulture');
    if (this.isLootingVulture) {
      this.hasPetrifiedUnit = false;
      this.bountyRatio = this.cardData?.bountyRatio !== undefined ? this.cardData.bountyRatio : 0.5;
    }
    this.isGoldenStatue = false;
    this.isFallingStatue = false;
    this.fallVelocity = 0;
    this.hasGoldPedestal = !!options.hasGoldPedestal;

    // Sul Experimental Metamorphosis: Bio-cocoon
    this.isCocoon = options.isCocoon !== undefined ? !!options.isCocoon : false;
    this.cocoonTimer = options.cocoonTimer !== undefined ? options.cocoonTimer : 0;
    this.isFallingCocoon = options.isFallingCocoon !== undefined ? !!options.isFallingCocoon : false;
    this._cocoonGroundTargetY = options._cocoonGroundTargetY;

    // Sul Parasite Vulture: Flies to enemy unit to take control of it
    this.isParasiteVulture = (this.name === 'Parasite Vulture' || this.cardData?.unitType === 'ParasiteVulture');
    this.hasParasite = options.hasParasite !== undefined ? !!options.hasParasite : this.isParasiteVulture;
    this.isInsane = options.isInsane !== undefined ? !!options.isInsane : false;
    this.insaneSeed = (this.id ? parseInt(this.id.replace(/\D/g, ''), 10) || 1 : 1);
    this.insaneTime = 0;
    this.isParasitized = options.isParasitized !== undefined ? !!options.isParasitized : false;
    this._overtakeAnimTime = 0;

    // Increased radii to make units visibly distinct on the battlefield!
    // Size hierarchy: Massive/Structure keep their fixed footprint regardless
    // of tier; everything else scales with card tier so smaller units and
    // heavier combatants are visually distinguishable instead of all defaulting to ~20.
    const TIER_RADIUS = { 1: 17, 2: 21, 3: 26 };
    let r = TIER_RADIUS[cardData.tier] || 20;
    if (this.isStructure) r = 26;
    if (this.name === 'Deployable Cannon' || this.cardData?.unitType === 'DeployableCannon') r = 21;
    if (cardData.attributes?.includes('Structure')) r = 48;
    if (cardData.attributes?.includes('Massive')) r = 32;
    // Preserver Lock & Monitor Lock: cosmetic upsize to match towering alien worms/colossi in card art
    if (this.name === 'Monitor Lock' || this.cardData?.unitType === 'MonitorLock') r = 28;
    if (this.name === 'Preserver Lock' || this.cardData?.unitType === 'PreserverLock') r = 30;
    if (this.name === 'Hyper Tesla Walker' || this.cardData?.unitType === 'HyperTeslaWalker') r = 23;
    if (this.name === 'Orchestra' || this.cardData?.unitType === 'Orchestra') r = 26.3;
    this.isTheSalvageCompany = (this.name === 'The Salvage Company' || this.cardData?.unitType === 'TheSalvageCompany');
    if (this.isTheSalvageCompany) r = 36;
    if (this.isEnforcerVulture) r = 34;
    if (this.isLootingVulture) r = 34;
    if (this.isParasiteVulture) r = 17; // Compact agile raptor (not bigger than Marine r=17)
    this.isHyperPanzer = (this.name === 'Hyper Panzer' || this.cardData?.unitType === 'HyperPanzer' || this.name === 'HyperPanzer');
    if (this.isHyperPanzer) r = 26;
    this.isAntiShade = (this.name === 'Anti Shade' || this.cardData?.unitType === 'AntiShade' || this.name === 'AntiShade');
    if (this.isAntiShade) r = 16;
    this.isSentinelIdle = false;
    this.isPulp = (this.name === 'Pulp' || this.cardData?.unitType === 'Pulp');
    if (this.isPulp) r = 22.36;
    this.isClaimingPillar = (this.name === 'Claiming Pillar' || this.cardData?.unitType === 'ClaimingPillar');
    if (this.isClaimingPillar) r = 26;
    this.isA9Grizzly = (this.name === 'A9 Grizzly' || this.cardData?.unitType === 'A9Grizzly' || this.name === 'A9Grizzly');
    if (this.isA9Grizzly) r = 20.7;
    this.isA9Pigeon = (this.name === 'A9 Pigeon' || this.cardData?.unitType === 'A9Pigeon' || this.name === 'A9Pigeon' || cardData.id === 47);
    if (this.isA9Pigeon) r = 21;
    this.isStickman = (this.name === 'Stickman' || this.cardData?.unitType === 'Stickman' || cardData.id === 39);
    if (this.isStickman) r = 30;
    this.isHotHand = (this.name === 'Hot Hand' || this.cardData?.unitType === 'HotHand' || cardData.id === 40);
    if (this.isHotHand) r = 17;
    this.isBastionLock = (this.name === 'Bastion Lock' || this.cardData?.unitType === 'BastionLock' || cardData.id === 45);
    if (this.isBastionLock) r = 21;
    this.firstHitDone = options.firstHitDone !== undefined ? !!options.firstHitDone : false;
    this.hasDealtFirstHit = options.hasDealtFirstHit !== undefined ? !!options.hasDealtFirstHit : false;
    this._dodgeAnimTime = 0;
    this._punchAnimTime = 0;
    this._punchVariant = 'jab';
    this.scoutRange = cardData.scoutRange ? cardData.scoutRange * 24 : ((this.isHotHand || this.cardData?.unitType === 'WheelieBomber' || this.name === 'Wheelie Bomber' || this.isKamikaze) ? 288 : 0);
    this.scoutLateralRange = cardData.scoutLateralRange ? cardData.scoutLateralRange * 24 : (this.isKamikaze ? 156 : 0);
    if (this.name === 'Hacker' || this.cardData?.unitType === 'Hacker' || this.isHacker) r = 20;
    this.radius = r;
    this.speed = this.isStructure ? 0 : ((cardData.speed || 2.25) * SPEED_MULTIPLIER);
    this.baseSpeed = this.speed;

    // Dynamic collision mass (Invariante #12: Etabliertes RTS-Massen- & Kollisionshandling)
    // Structures are immovable (Infinity). Super-Heavy units have massive inertia (5.0).
    // Massive units have high inertia (4.0). Armored units have medium inertia (2.2). Light units have baseline inertia (1.0).
    this.mass = this.isStructure ? Infinity
      : (this.isTheSalvageCompany ? 5.0
      : ((this.isHyperPanzer || this.isStickman) ? 4.0
      : (this.isBastionLock ? 3.5
      : (cardData.attributes?.includes('Massive') ? 4.0
      : (cardData.attributes?.includes('Armored') ? 2.2 : 1.0)))));
    this.isEngaged = false;
    // Element Type ('Ground' | 'Air') replaces the old raw isAir boolean as
    // the source of truth - isAir stays as its derived mirror since it's
    // read pervasively elsewhere (positioning, particle FX). Falls back to
    // the legacy isAir field for cards not yet migrated to the new schema.
    this.elementType = cardData.elementType || (cardData.isAir ? 'Air' : 'Ground');
    this.isAir = this.elementType === 'Air';
    // Targets Air/Targets Ground as a general mechanic is gone (see
    // Battlefield.findBestTarget), but a card can still opt out of one side
    // explicitly (e.g. Panzer's "No Air") - defaults to true/true so every
    // other unit keeps attacking/splashing all Element Types unchanged.
    this.targetsAir = cardData.targetsAir !== false && !this.isAntiShade;
    this.targetsGround = cardData.targetsGround !== false;
    if (this.name === 'Orchestra' || cardData.unitType === 'Orchestra') {
      this.targetsAir = false;
      this.targetsGround = true;
    }
    this.cannotAttackBuildings = !!cardData.cannotAttackBuildings || this.isAntiShade;
    this.breathAudioPending = this.isAntiShade;
    this.breathAudioActive = false;
    this._footstepTimer = 0;
    this._slimeTrailTimer = 0;
    this._acidSizzleTimer = 0;
    this._heatShimmerTimer = 0;
    this.wobbleOffset = Math.random() * Math.PI * 2;

    // Vitals - Shields, Armor and Regen were removed as mechanics
    // (Deflection Rating below replaces Armor's role); see
    // Unit.takeDamage() and NEUSTART_PLAN_DEFLECTION notes.
    this.maxHp = cardData.hp || 50;
    this.hp = this.maxHp;

    // Combat Stats - Damage/Range are unified single values now (no more
    // Damage Ground vs Damage Air, or Range Ground vs Range Air), Attack
    // Count (multi-shot) is gone (one shot per attack cycle), and
    // per-attribute Bonus Damage is gone along with the attribute/tag
    // system itself.
    this.damage = cardData.damage || 0;

    this.attackSpeed = cardData.attackSpeed || 1.2; // seconds between attacks (authentic design value)
    this.attackCooldown = (this.damage > 0 || cardData.hasTether || cardData.hasDualTether) ? (this._rng.next() * 0.5) : 0;
    this.range = (cardData.range || 1.0) * 24; // design range units → canvas pixels (scaled up)
    this.splashRadius = (cardData.splashRadius || 0) * 24;
    if (this.isStickman) this.splashRadius = 24;
    if (this.isBastionLock) {
      this.splashRadius = 28;
      this._lastBeamTime = -999;
      this._lastBeamTarget = null;
    }
    this.splashLine = !!cardData.splashLine;

    // Accuracy % / Deflection Rating % / Projectile Type: the new ranged-
    // combat resolution model. hasNewCombatSchema gates performAttack()
    // into resolveNewCombatAttack() below instead of the legacy per-
    // unitType visual switch - only cards that actually set a
    // projectileType (i.e. have been migrated to this schema) opt in; a
    // card without one keeps behaving exactly as before. See
    // Unit.performAttack()/resolveNewCombatAttack() and Unit.takeDamage().
    this.hasNewCombatSchema = !!cardData.projectileType;
    // Fraction of shots that connect at all (1.0 = always hits). Missed
    // shots deal zero damage and never call takeDamage() - see
    // resolveNewCombatAttack().
    this.accuracy = cardData.accuracy !== undefined ? cardData.accuracy : 1.0;
    // Fraction of connecting Bullet/Rocket/Grenade hits this unit bounces
    // off for zero damage. Energy-type attacks ignore this - see
    // Unit.takeDamage().
    this.deflectionRating = cardData.deflectionRating || 0;
    this.projectileType = cardData.projectileType || 'Bullet'; // Bullet | Rocket | Grenade | Energy
    this.deflectionScaling = !!cardData.deflectionScaling || (cardData.name === 'Tesla Walker') || (cardData.name === 'Hyper Tesla Walker') || (cardData.unitType === 'HyperTeslaWalker');

    // Traits & Status
    this.isCloaked = !!cardData.isCloaked;
    this.decloakTimer = 0; // Timer for temporary visibility after attacking
    this.cloakedByAuraTimer = 0; // External cloaking aura timer
    this.smokeCloakTimer = 0; // Smoke grenade cloak timer (< 50% HP)
    this.smokeGrenadeTriggered = false;
    this.deflectionBonus = 0; // Temporary deflection rating boost from smoke screen
    this.executionTimer = 0; // 0.9s melee infantry execution timer
    this.executionDuration = 0;
    this.executionTargetId = null;
    this.executionCooldown = 0; // 8.0s internal cooldown between melee executions
    this.facingDir = null; // Dynamic facing direction (1 for east, -1 for west)
    this.isPincerStalking = false; // Aggressive tactical stalk towards infantry
    this.isUntargetable = false; // Untargetable during melee execution
    this.isHeld = false; // Incapacitated while being executed in the air
    this.isDetector = !!cardData.isDetector || !!cardData.detectsCloak || (cardData.unitType === 'DelvingPillar') || this.isA9Grizzly || this.isParasiteVulture;
    this.globalDetector = !!cardData.globalDetector || this.isA9Grizzly;
    // Detection radius around this unit's own position - positional, not
    // team-wide, so a detector only reveals cloak/burrow near itself.
    this.detectionRange = (cardData.detectCloakRange || cardData.detectionRange || (this.isParasiteVulture ? 20.0 : (cardData.unitType === 'DelvingPillar' ? 30.0 : 7.0))) * 24;

    // Changeling Disguise: copies a currently-alive enemy unit's identity
    // (picked once in update()) and becomes a priority target so it pulls
    // enemy fire away from real threats.
    this.isDecoy = !!cardData.isDecoy;
    this.disguiseUnitType = null;

    // Sul Swarm Traits (Defective Loaf Tether, Dissolving Acid, Rejected Loaf Reflection, Spore Slow, Pillar Disarm & Harvesting Pillar Dual Tether)
    this.hasTether = !!cardData.hasTether || (cardData.name === 'Defective Loaf') || (cardData.unitType === 'DefectiveLoaf');
    this.tetherTargetId = null;
    this.hasDualTether = !!cardData.hasDualTether || (cardData.name === 'Harvesting Pillar') || (cardData.unitType === 'HarvestingPillar');
    this.dualTetherTarget1Id = null;
    this.dualTetherTarget2Id = null;
    this.tether1LockTimer = 0;
    this.dualTetherDps = cardData.dualTetherDps || 20;
    this.dualTetherPhase2Delay = cardData.dualTetherPhase2Delay !== undefined ? cardData.dualTetherPhase2Delay : 5.0;
    this.dualTetherLifestealRatio = cardData.dualTetherLifestealRatio !== undefined ? cardData.dualTetherLifestealRatio : 0.1;
    this.isRooted = false; // Immobilized status inflicted by Harvesting Pillar
    this.acidStacks = cardData.acidStacks || 0;
    this.acidTickTimer = 0;
    this.sporeSlowTimer = 0;
    this.sporeSlowPenalty = 0;
    this.disarmTimer = 0;
    this.overstimulationTimer = options.overstimulationTimer !== undefined ? options.overstimulationTimer : 0;
    this.isOverstimulated = options.isOverstimulated !== undefined ? !!options.isOverstimulated : false;
    this.buffAccuracyPenalty = 0;
    this.inflictsSporeSlow = !!cardData.sporeSlow || (cardData.unitType === 'ApostateLoaf');
    // Sul Preserver Lock: continuous plasma beam lock-on (ground-only).
    // Unlike hasTether, damage is applied every attack cycle but presentation
    // (damage text/sparks/sound) is batched - see resolveNewCombatAttack() -
    // because attackSpeed (0.1s) falls under the "Hochfrequenzwaffen" rule in
    // docs/PERFORMANCE_AND_STABILITY_AUDIT.md (never spawn per-shot VFX/text
    // at > 5 shots/s).
    this.hasBeamTether = !!cardData.hasBeamTether || (cardData.unitType === 'PreserverLock');
    this.beamTargetId = null;
    this._beamDmgAccum = 0; // cosmetic-only, not part of the state snapshot
    this._beamFlushTimer = 0; // cosmetic-only, not part of the state snapshot
    this.sporeSlowDuration = cardData.sporeSlow?.duration || 10;
    this.sporeSlowReduction = cardData.sporeSlow?.speedReduction || 2;
    this.inflictsDisarm = !!cardData.disarmOnHit || (cardData.unitType === 'DelvingPillar');
    this.disarmInflictDuration = cardData.disarmOnHit?.duration || 3;
    this.reflectDamageRatio = cardData.reflectDamageRatio || (cardData.name === 'Rejected Loaf' || cardData.unitType === 'RejectedLoaf' ? 0.5 : 0);
    this.canAttack = cardData.canAttack !== false && (this.damage > 0 || this.hasTether || this.hasDualTether || this.hasBeamTether);
    // Sul Monitor Lock & Claiming Pillar: stationary structures with 3s growth eruption
    this.isMonitorLock = !!(cardData.unitType === 'MonitorLock' || cardData.name === 'Monitor Lock');
    this.growthDuration = cardData.growthDuration !== undefined ? cardData.growthDuration : ((this.isMonitorLock || this.isClaimingPillar) ? 3.0 : 0);
    this.growthTimer = options.growthTimer !== undefined ? options.growthTimer : 0;
    this.isGrowing = this.growthDuration > 0 && this.growthTimer < this.growthDuration;
    this.hpRegenRate = cardData.hpRegenRate || (this.isClaimingPillar ? 2.0 : 0);
    this._hpRegenAccum = 0;
    this._hpRegenFlash = 0;
    this.deathBurstDamage = cardData.deathBurstDamage || (this.isClaimingPillar ? 30 : 0);
    this.deathBurstRange = (cardData.deathBurstRange || (this.isClaimingPillar ? 8 : 0)) * 24;
    this._claimingBurstTriggered = false;
    this.swarmBuffDamage = cardData.swarmBuffDamage !== undefined ? cardData.swarmBuffDamage : 0.30;
    this.swarmBuffHp = cardData.swarmBuffHp !== undefined ? cardData.swarmBuffHp : 0.30;
    this.snapStrikeTimer = options.snapStrikeTimer !== undefined ? options.snapStrikeTimer : 0;
    this.snapTargetId = null;
    this._overwatchTimer = 0;
    this.disguiseRace = null;
    this.isKamikaze = !!cardData.isKamikaze;
    this.isBurrowed = !!cardData.isBurrowed;
    this.hasExplosiveCrow = !!cardData.hasExplosiveCrow || (cardData.unitType === 'HyperShade');
    this.hasIncinerate = !!cardData.hasIncinerate || (cardData.unitType === 'HyperDevilPatrol');
    this.incinerateDuration = cardData.incinerateDuration || 5;
    this.incinerateDps = cardData.incinerateDps || 2;
    this.incinerateStacks = [];

    // Hyper Panzer: Dual-weapon heavy tank (Cannon vs Ground, Rockets vs Air, Overrun, Bulldoze, Death Explosion)
    if (this.isHyperPanzer) {
      this.cannonCooldown = (this._rng.next() * 0.5);
      this.rocketCooldown = (this._rng.next() * 0.5);
      this.groundTarget = null;
      this.airTarget = null;
      this.isStoppedAtBase = false;
      this.turretAimState = 'straight';
      this.deathExplosionDamage = cardData.deathExplosionDamage || 100;
      this.deathExplosionRadius = (cardData.deathExplosionRadius || 5.0) * 24;
      this._hyperPanzerExploded = false;
    }

    // Sul Forgotten Loaf (Spider Mine, ID 41): sprints to frontline, burrows, rises as hot-air balloon on proximity or lethal sprint hit, and detonates for 70 splash damage
    this.isForgottenLoaf = !!(cardData.unitType === 'ForgottenLoaf' || cardData.name === 'Forgotten Loaf' || cardData.id === 41);
    this.mineState = this.isForgottenLoaf ? (options.mineState || 'sprinting') : null;
    this.isBurrowing = !!options.isBurrowing;
    this.burrowTimer = options.burrowTimer !== undefined ? options.burrowTimer : 0;
    this.isMineRising = !!options.isMineRising;
    this.mineRiseTimer = options.mineRiseTimer !== undefined ? options.mineRiseTimer : 0;
    this.wasBurrowedBeforeRising = !!options.wasBurrowedBeforeRising;
    this.burrowAnchorX = options.burrowAnchorX !== undefined ? options.burrowAnchorX : null;
    this.burrowAnchorY = options.burrowAnchorY !== undefined ? options.burrowAnchorY : null;

    // Human Pink Beret: Slick soldier with initial 45° seeking homing rocket & transition to stylish revolver pistol combat
    this.isPinkBeret = !!(cardData.unitType === 'PinkBeret' || cardData.name === 'Pink Beret' || cardData.id === 42);
    this.pinkBeretState = this.isPinkBeret ? (options.pinkBeretState || 'WAITING_FOR_TARGET') : null;
    this.pinkBeretAimTimer = options.pinkBeretAimTimer !== undefined ? options.pinkBeretAimTimer : 0;
    this.pinkBeretTransitionTimer = options.pinkBeretTransitionTimer !== undefined ? options.pinkBeretTransitionTimer : 0;
    this.pinkBeretTargetId = options.pinkBeretTargetId || null;
    this.hasFiredInitialRocket = !!options.hasFiredInitialRocket;
    this.forceHD = !!(options.forceHD || cardData.forceHD || cardData.variant === 'classic' || options.variant === 'classic');
    this.forceUHD = !!(options.forceUHD || cardData.forceUHD);
    this.variant = options.variant || cardData.variant || null;
    if (this.isPinkBeret) {
      this.materializeDuration = 0;
      this.materializeTimer = 0;
      if (this.pinkBeretState !== 'NORMAL') {
        this.speed = 0;
      }
    }

    // Human Hacker: Cyborg mystic, shoots cable harpoon at closest enemy mobile unit, disables it, extracts plasma & hand data
    this.isHacker = !!(cardData.unitType === 'Hacker' || cardData.name === 'Hacker' || cardData.id === 43);
    this.hackerState = this.isHacker ? (options.hackerState || 'IDLE') : null;
    this.hackerTargetId = options.hackerTargetId || null;
    this.hackerLockTimer = options.hackerLockTimer !== undefined ? options.hackerLockTimer : 0;
    this.isHacking = !!options.isHacking;
    this.isTranscendent = !!options.isTranscendent;
    this.transcendentTimer = options.transcendentTimer !== undefined ? options.transcendentTimer : 0;
    this.handRevealTimer = options.handRevealTimer !== undefined ? options.handRevealTimer : 0;
    this.isIndestructible = !!options.isIndestructible;
    this.isDisabled = !!options.isDisabled;
    this.disabledByHackerId = options.disabledByHackerId || null;
    if (this.isHacker) {
      if (this.hackerState !== 'DRIFTING') {
        this.speed = 0;
        this.baseSpeed = 0;
      }
    }

    // Human A9 Pigeon: Heavy VTOL fighter-bomber with one 100-DMG ground bomb, pivoting vector-thrust nozzle,
    // fixed-forward nose Gatling gun (whole-aircraft pitch aim), and reverse-thrust kiting at speed 1
    this.isA9Pigeon = !!(cardData.unitType === 'A9Pigeon' || cardData.name === 'A9 Pigeon' || cardData.id === 47);
    if (this.isA9Pigeon) {
      this.facingDir = this.team === 'western' ? 1 : -1;
    }
    this.hasBomb = this.isA9Pigeon ? (options.hasBomb !== undefined ? !!options.hasBomb : true) : false;
    this.pigeonState = this.isA9Pigeon ? (options.pigeonState || 'ADVANCE') : null;
    this.bombTargetId = options.bombTargetId || null;
    this.bombHoverTimer = options.bombHoverTimer !== undefined ? options.bombHoverTimer : 0;
    this.isFlyingBackward = !!options.isFlyingBackward;
    this.isHovering = !!options.isHovering;
    this.pigeonPitch = options.pigeonPitch !== undefined ? options.pigeonPitch : 0;
    this.nozzleTilt = options.nozzleTilt !== undefined ? options.nozzleTilt : 0;
    this._pigeonVelX = 0;
    this._pigeonVelY = 0;
    this._pigeonBombRecoilY = 0;
    this._wingmanX = undefined;
    this._wingmanY = undefined;
    this._wingmanVx = 0;
    this._wingmanVy = 0;

    // Sul Processing Pillar traits: 3s bubble channeling, ground/air projectile morphing, whip-like ground feelers
    this.isProcessingPillar = (cardData.unitType === 'ProcessingPillar') || (cardData.name === 'Processing Pillar');
    this.prioritizeGround = !!cardData.prioritizeGround || this.isProcessingPillar;
    this.plasmaChargeTimer = 0;
    this.plasmaChargeTarget = null;
    this.plasmaTargetIsAir = false;
    this.plasmaTargetLastX = 0;
    this.plasmaTargetLastY = 0;
    this.plasmaMorphed = false;
    this.whipAgitation = 0;



    // Seconds since this unit was spawned - deterministic (dt-driven, same
    // on both peers), used by cosmetic-only per-unit animation cadences
    // (e.g. Wheelie Bomber's wheelie-pop timing) that need to be relative
    // to "how long has THIS unit existed" rather than the wall-clock time
    // the renderer otherwise uses, so a short-lived unit reliably gets to
    // show its animation instead of depending on when in the match it
    // happened to spawn.
    this.age = 0;

    // Special unit states
    this.blinkCooldown = 0;
    this.voidRayCharge = 1.0;
    this.voidRayChargeLevel = 0; // 0/1/2 -> VOID_RAY_CHARGE_TIERS index
    this.voidRayIdleTime = 0;
    this.voidRayTarget = null;
    this.hitFlashTimer = 0;
    this.underFireTimer = 0;
    this.hitFlinchTimer = 0;
    this.hitFlinchDir = 0;
    this.hitFlinchDist = 0;
    this.hitSquashTimer = 0;
    this.ghostHp = this.hp;
    this.ghostHpTimer = 0;
    this.smokeBarrelTimer = 0;
    this.shortCircuitTimer = 2.0 + Math.random() * 2.0;
    this.wasWalking = false;
    this.treadKickupCooldown = 0;
    this.combatOffset = this._rng.range(-25, 25);
    this.laneYTarget = y;
    this._stuckTicks = 0;
    this._prevProgressX = x;

    // Spawn materialization - a short window right after entering the
    // battlefield during which the renderer plays a race-specific "arriving"
    // effect (warp-in ring, teleport beam, organic burst) instead of the
    // unit just popping into existence.
    // isCinematic also defaults this to 0, like isParachuting: this.update()
    // returns early for an isCinematic unit (see the guard above) before
    // ever reaching this.materializeTimer's own decay further down, so a
    // cutscene actor/prop spawned with the normal 0.45s fade-in default
    // would stay stuck partially transparent for as long as it exists -
    // see docs/singleplayer implementation soft-foraging-flamingo.md.
    this.materializeDuration = options.materializeDuration !== undefined ? options.materializeDuration : ((this.isParachuting || this.isCinematic || this.isPinkBeret) ? 0 : 0.45);
    this.materializeTimer = options.materializeTimer !== undefined ? options.materializeTimer : ((this.isParachuting || this.isCinematic || this.isPinkBeret) ? 0 : 0.45);

    // Buffs & Modifiers
    this.buffs = [];
    this.isDead = false;
    this.target = null;
    this.timedLife = cardData.timedLife || 0; // if > 0, unit dies after this many seconds
    // Gradual decay (e.g. Broodlings): HP drains continuously instead of a
    // hard cutoff, so it visibly withers away and dies faster if it's also
    // taking combat damage in the meantime.
    this.hpDecayRate = cardData.hpDecayRate || 0;

    // Visuals & Art
    const goldImg = (this.edition === 'gold') ? getGoldCardImage(cardData.name) : null;
    this.imageName = goldImg || (cardData.images && cardData.images.length > 0 ? cardData.images[0] : null);
    // NOTE: no card-art <img> is created per unit. Units are drawn from baked
    // sprite bitmaps (js/engine/render/unitSprites.js); an earlier
    // `this.imgElement = new Image()` here was never read by any renderer but
    // downloaded the full-size card PNG (up to ~1 MB, plus the opponent's art
    // for snapshot-spawned units online) on the first spawn of every unit type.
  }

  applyIncinerate(duration = 5.0, dps = 2, attackerTeam = null) {
    if (this.isDead) return;
    if (!this.incinerateStacks) this.incinerateStacks = [];
    this.incinerateStacks.push({
      duration: duration,
      remaining: duration,
      tickTimer: 0.5,
      dps: dps,
      attackerTeam: attackerTeam
    });
  }

  applySporeSlow(duration = 10.0, speedReduction = 2.0) {
    if (this.isDead) return;
    this.sporeSlowTimer = Math.max(this.sporeSlowTimer || 0, duration);
    this.sporeSlowPenalty = Math.max(this.sporeSlowPenalty || 0, speedReduction);
  }

  applyDisarm(duration = 3.0) {
    if (this.isDead) return;
    this.disarmTimer = Math.max(this.disarmTimer || 0, duration);
  }

  applyForcedOverstimulation(battlefield, particles, audio) {
    if (this.isDead || this.isStructure) return;
    if (this.isOverstimulated) {
      this.overstimulationTimer = 10.0;
      if (Array.isArray(this.buffs)) {
        const existing = this.buffs.find(b => b.name === 'ForcedOverstimulation');
        if (existing) existing.duration = 10000;
      }
      return;
    }
    this.isOverstimulated = true;
    this.overstimulationTimer = 10.0;
    this.hp *= 2;
    this.maxHp *= 2;
    if (!this.buffs) this.buffs = [];
    this.buffs.push({
      name: 'ForcedOverstimulation',
      duration: 10000,
      speedMult: 2.0,
      attackSpeedMult: 2.0,
      accuracyPenalty: 0.1
    });
    if (particles?.addShockwave) {
      particles.addShockwave(this.x, this.y, this.radius * 2.2, '#ef4444', 0.25);
    }
  }

  startRetreat(base = null) {
    if (this.isDead || this.isStructure || this.isBuilding || this.isBase || this.isBastionRearGuard || this.isRetreating) return;
    this.isRetreating = true;
    this.target = null;
    this.canAttack = false;
    this.attackCooldown = Infinity;
    this.isEngaged = false;
    this.facingDir = (this.team === 'western' ? -1 : 1);
    this.speed = (this.speed || this.baseSpeed) + 1.0 * SPEED_MULTIPLIER;
    this.baseSpeed = this.speed;
    this.deflectionRating = Math.min(0.95, (this.deflectionRating || 0) + 0.1);
  }

  _chooseHotHandPunch(target) {
    let tailored = 'jab';
    if (target) {
      if (target.isStructure || target.attributes?.includes('Structure')) {
        tailored = 'slam';
      } else if ((target.radius || 17) >= 21 || target.attributes?.includes('Armored') || target.attributes?.includes('Mechanical') || target.attributes?.includes('Massive')) {
        tailored = 'uppercut';
      }
    }
    const r = this._rng.next();
    if (r < 0.8) {
      return tailored;
    }
    const others = ['jab', 'uppercut', 'slam'].filter(v => v !== tailored);
    return others[r < 0.9 ? 0 : 1];
  }

  _triggerClaimingPillarDeathBurst(battlefield, particles, audio) {
    if (this._claimingBurstTriggered) return;
    this._claimingBurstTriggered = true;
    const bf = battlefield || this.battlefield;
    const burstDmg = this.deathBurstDamage || 30;
    const burstRange = this.deathBurstRange || (8 * 24);
    if (bf?.dealSplashDamage) {
      bf.dealSplashDamage(
        this.x, this.y, burstRange, burstDmg, this.team,
        false, true, particles, 'Acid', null, false, this,
        false, 0, 0, burstRange, false
      );
    }
    particles?.addClaimingPillarBurst?.(this);
    audio?.playSample?.('even more sounds/80-CC0-creature-SFX/weird_03.ogg', { volume: 0.85, x: this.x });
  }

  _collapseIntoStickmanObstacle(battlefield, particles, audio) {
    const bf = battlefield || this.battlefield;
    this.hp = 0;
    this.isDead = false;
    this.isGoldenStatue = true;
    this.hasGoldPedestal = false;
    this.isUntargetable = true;
    this.mass = Infinity;
    this.canAttack = false;
    this.attackCooldown = Infinity;
    this.speed = 0;
    this.baseSpeed = 0;
    this.radius = 24;
    this.hitFlashTimer = 0;
    this._kneelingAnimDuration = 3.8;
    this._kneelingAnimTime = 3.8;
    this._stickmanDustSpawned = false;
    this._stickmanWeaponSparksSpawned = false;
    this.deathPose = (stickmanDeathPoseCounter++ % 3) + 1;

    if (bf) {
      if (bf.goldenStatues && !bf.goldenStatues.includes(this)) {
        bf.goldenStatues.push(this);
      }
      bf.enforceDeadStickmanLimit?.(particles, 3);
    }

    const effAudio = audio || bf?.audio || particles?.audio || (typeof window !== 'undefined' ? window.tcgGame?.audio : null);
    this._playDeathSound(effAudio);
    particles?.addStickmanJointSparks?.(this.x, this.y, this.team);
  }

  crumbleObstacle(particles, battlefield = null) {
    const bf = battlefield || this.battlefield;
    this.isDead = true;
    this.isGoldenStatue = false;
    this.hp = 0;
    if (bf) {
      if (Array.isArray(bf.goldenStatues)) {
        const gIdx = bf.goldenStatues.indexOf(this);
        if (gIdx !== -1) bf.goldenStatues.splice(gIdx, 1);
      }
      if (Array.isArray(bf.units)) {
        const uIdx = bf.units.indexOf(this);
        if (uIdx !== -1) bf.units.splice(uIdx, 1);
      }
      if (bf.unitMap) {
        bf.unitMap.delete(this.id);
      }
      bf.addFrameEvent?.({
        type: 'STICKMAN_CRUMBLE',
        unitId: this.id,
        x: Math.round(this.x),
        y: Math.round(this.y)
      });
    }
    particles?.addStickmanObstacleCrumble?.(this);
  }

  vaporizeObstacle(particles, superType = null, battlefield = null) {
    this.crumbleObstacle(particles, battlefield);
  }

  // 🥚 Sul Experimental Metamorphosis: Verwandlung in 500-HP Bio-Kokon
  applyCocoon(battlefield, particles, audio) {
    if (this.isDead || this.isStructure || this.isGoldenStatue || this.isCinematic) return;
    if (this.isCocoon) return; // Invariante: bestehende Kokons behalten ihren Rest-Timer

    this.isCocoon = true;
    this.cocoonTimer = 30.0;
    this.hp = 500;
    this.maxHp = 500;
    this.deflectionRating = 0;
    this.reflectDamageRatio = 0;
    this.bountyRatio = 0;
    this.speed = 0;
    this.baseSpeed = 0;
    this.canAttack = false;
    this.attackCooldown = Infinity;
    this.isUntargetable = false;
    this.isBurrowed = false;
    this.isBurrowing = false;
    this.isMineRising = false;
    this.mineState = null;
    this.mass = Infinity;

    // Aggro & Channeling sauber abbrechen
    this.isHeld = false;
    this.target = null;
    this.groundTarget = null;
    this.airTarget = null;
    this.disarmTimer = 0;
    if (this.hasTether || this.activeTethers) {
      this.activeTethers = [];
    }
    if (this.hasBeamTether) {
      this.beamTargetId = null;
    }
    if (this.hasDualTether) {
      this.dualTetherTarget1Id = null;
      this.dualTetherTarget2Id = null;
      this.tether1LockTimer = 0;
    }

    // Flugeinheiten stürzen wie Goldstatuen ab
    if (this.isAir) {
      this.isFallingCocoon = true;
      this.fallVelocity = 120;
      const laneBottom = battlefield ? (battlefield.laneY + battlefield.laneHeight - 20) : (this.y + 40);
      this._cocoonGroundTargetY = Math.max(this.y + 25, Math.min(laneBottom, this.y + 35));
    }

    if (battlefield?.cocoons && !battlefield.cocoons.includes(this)) {
      battlefield.cocoons.push(this);
    }

    if (particles?.addShockwave) {
      particles.addShockwave(this.x, this.y, this.radius * 2.2, '#c084fc', 0.3);
    }
  }

  // 🥚 Schlupf nach 30s: Zufällige mobile Sul-Einheit schlüpft für dasselbe Team
  hatchCocoon(battlefield, particles, audio) {
    if (this.isDead) return;
    this.isDead = true;
    this.hp = 0;
    this.isCocoon = false;

    particles?.addAcidSplash?.(this.x, this.y, 10);
    particles?.addDebris?.(this.x, this.y, 8, '#3f3f46');
    particles?.addShockwave?.(this.x, this.y, 45, '#d946ef', 0.35);
    audio?.playAcidSplash?.({ x: this.x });

    const SUL_MOBILE_UNITS = [
      'Defective Loaf',
      'Rejected Loaf',
      'Apostate Loaf',
      'Forgotten Loaf',
      'Delving Pillar',
      'Harvesting Pillar',
      'Processing Pillar',
      'Preserver Lock',
      'Sentinal Lock',
      'Enforcer Vulture',
      'Looting Vulture',
      'Parasite Vulture',
      'Anti Shade',
      'Pulp'
    ];

    const rng = battlefield?.rng || battlefield?.cardSystem?.rng;
    const randomIndex = rng ? rng.int(0, SUL_MOBILE_UNITS.length - 1) : Math.floor(Math.random() * SUL_MOBILE_UNITS.length);
    const chosenName = SUL_MOBILE_UNITS[randomIndex];
    const sulCard = getCardByName(chosenName);

    if (sulCard && battlefield?.spawnRaw) {
      const spawned = battlefield.spawnRaw(sulCard, this.team, this.x, this.y);
      if (spawned) {
        spawned.hitFlashTimer = 0.15;
      }
    }

    audio?.playBark?.('Sul', 'spawn', { x: this.x });
    if (battlefield?.cocoons) {
      const cIdx = battlefield.cocoons.indexOf(this);
      if (cIdx !== -1) battlefield.cocoons.splice(cIdx, 1);
    }
  }

  // Unit MVP Spotlight (docs/UNIT_MVP_PLAN.md 2.3/4.2): distributes this
  // unit's cost across whichever enemy units actually damaged it, weighted
  // by their share of the total damage it took - not a last-hit-takes-all
  // credit, so a unit that merely lands the final poke after others did the
  // real work doesn't sweep the whole material value. Friendly-fire damage
  // (dealt by same-team attacker) never registers in damageContributors in
  // the first place (see takeDamage()), so it never earns kill credit either.
  distributeKillCredit(battlefield) {
    const stats = battlefield?.stats;
    if (!stats) return;
    stats.markUnitDeath(this.id);
    const cost = this.cardData.cost || 0;
    if (cost <= 0 || !this.damageContributors || this.damageContributors.size === 0) return;
    let total = 0;
    for (const dmg of this.damageContributors.values()) total += dmg;
    if (total <= 0) return;
    for (const [attackerId, dmg] of this.damageContributors) {
      stats.recordUnitKillShare(attackerId, (dmg / total) * cost);
    }
  }

  // Geteilt zwischen die() und takeDamage()'s eigenem hp<=0-Zweig - beides
  // unabhängige Sterbe-Pfade (siehe Kommentare an beiden Aufrufstellen), die
  // sonst die Override-/Fallback-Logik und die isMachinery-Klassifizierung
  // zweimal pflegen müssten. Ein einziges, definitives Sterbegeräusch pro
  // Einheit: Override aus js/data/unitSounds.js falls vorhanden, sonst die
  // generische Rassen-Bark + Maschinerie/Infanterie-Geräusch (nie beides).
  _playDeathSound(audio) {
    const unitKey = this.cardData?.unitType || this.name;
    const regDeath = audio?.registry?.units?.[unitKey]?.death;
    if (regDeath && Array.isArray(regDeath.files) && regDeath.files.length > 0) {
      audio.playUnitSound(unitKey, 'death', { x: this.x });
      return;
    }
    const override = getCustomDeathSound(this.cardData);
    if (override) {
      audio?.[override.method]?.(...(override.args || []), { x: this.x });
      return;
    }
    audio?.playBark?.(this.race, 'death', { x: this.x });
    const isMachinery = (
      this.isStructure ||
      this.cardData?.isStructure ||
      this.cardData?.type === 'Structure' ||
      this.cardData?.attributes?.includes?.('Mechanical') ||
      this.cardData?.attributes?.includes?.('Structure') ||
      [
        'TeslaWalker', 'HyperTeslaWalker', 'Panzer', 'HyperPanzer', 'DeployableCannon',
        'Rocketship', 'WheelieBomber', 'DevilPatrol', 'HyperDevilPatrol',
        'A9Piranha', 'A9Grizzly', 'A9Pigeon', 'Stickman', 'TheSalvageCompany', 'Orchestra',
        'DelvingPillar', 'HarvestingPillar', 'ProcessingPillar', 'ClaimingPillar',
        'PreserverLock', 'SentinalLock', 'EnforcerVulture'
      ].includes(this.cardData?.unitType || '') ||
      [
        'Tesla Walker', 'Hyper Tesla Walker', 'Panzer', 'Hyper Panzer', 'Deployable Cannon',
        'Rocketship', 'Wheelie Bomber', 'Devil Patrol', 'Hyper Devil Patrol',
        'A9 Piranha', 'A9 Grizzly', 'A9 Pigeon', 'Stickman', 'The Salvage Company', 'Orchestra',
        'Delving Pillar', 'Harvesting Pillar', 'Processing Pillar', 'Claiming Pillar',
        'Preserver Lock', 'Sentinal Lock', 'Enforcer Vulture'
      ].includes(this.name || '')
    );
    if (isMachinery) {
      audio?.playUnitDeathMachinery?.({ x: this.x });
    } else {
      audio?.playUnitDeathInfantry?.({ x: this.x });
    }
  }

  die(battlefield, particles, audio, killer = null) {
    if (this.isDead || this.isGoldenStatue) return;
    if (this.motorLoopActive) {
      audio?.stopA9PiranhaMotorLoop?.();
      this.motorLoopActive = false;
    }
    if (this.cardData?.unitType === 'WheelieBomber' || this.name === 'Wheelie Bomber') {
      audio?.stopWheelieEngine?.(this.driveAudioSoundId);
      this.driveAudioSoundId = null;
    }
    if (this.isAntiShade) {
      const otherAntiShade = battlefield?.units?.some(u => u !== this && !u.isDead && u.isAntiShade);
      if (!otherAntiShade) {
        audio?.stopAntiShadeBreath?.();
      }
      this.breathAudioActive = false;
    }
    if (this.isStickman && !this.isCocoon) {
      const killerTeam = this.team === 'western' ? 'eastern' : 'western';
      const isSplash = !!(killer && ((killer.splashRadius > 0) || killer.splashLine || killer.isKamikaze || killer.projectileType === 'Rocket' || killer.projectileType === 'Grenade'));
      battlefield?.stats?.recordKill(killerTeam, this.cardData.cost || 0, false, isSplash);
      this.distributeKillCredit(battlefield);
      this._collapseIntoStickmanObstacle(battlefield, particles, audio);
      return;
    }
    this.hp = 0;
    this.isDead = true;
    const killerTeam = this.team === 'western' ? 'eastern' : 'western';
    const isSplash = !!(killer && ((killer.splashRadius > 0) || killer.splashLine || killer.isKamikaze || killer.projectileType === 'Rocket' || killer.projectileType === 'Grenade'));
    battlefield?.stats?.recordKill(killerTeam, this.cardData.cost || 0, this.isAir && !this.isStructure, isSplash);
    this.distributeKillCredit(battlefield);
    battlefield?.handleUnitDeath?.(this, particles, killer);
    if (this.isHyperPanzer) {
      this._triggerHyperPanzerDeathExplosion(battlefield, particles, audio);
    } else if (this.isClaimingPillar || this.cardData?.unitType === 'ClaimingPillar' || this.name === 'Claiming Pillar') {
      this._triggerClaimingPillarDeathBurst(battlefield, particles, audio);
    } else if (this.isPulp || this.cardData?.unitType === 'Pulp' || this.name === 'Pulp') {
      particles?.addPulpPencilTrickDeath?.(this, audio);
    } else if (this.cardData?.unitType === 'HyperDevilPatrol' || this.name === 'Hyper Devil Patrol') {
      particles?.addWildRocketDeath?.(this, audio);
    } else if (this.cardData?.unitType === 'HarvestingPillar' || this.name === 'Harvesting Pillar') {
      particles?.addHarvestingBloodPlasmaExplosion?.(this);
    } else if (this.cardData?.unitType === 'SentinalLock') {
      particles?.addSentinalLockTripleShatter?.(this);
    } else if (this.isParasiteVulture || this.cardData?.unitType === 'ParasiteVulture' || this.name === 'Parasite Vulture') {
      particles?.addParasiteVultureDeath?.(this, audio);
    } else if (this.isPinkBeret) {
      particles?.addPinkBeretKissDeath?.(this, audio);
    } else if (this.isLootingVulture || this.cardData?.unitType === 'LootingVulture' || this.name === 'Looting Vulture') {
      particles?.addLootingVultureDeath?.(this, audio);
    } else if (this.isHacker) {
      particles?.addHackerDeath?.(this, audio);
    } else if (this.isA9Pigeon || this.cardData?.unitType === 'A9Pigeon' || this.name === 'A9 Pigeon') {
      particles?.addA9PigeonDeathFlock?.(this, audio);
    } else if (this.edition === 'gold') {
      particles?.addGoldUnitDeath?.(this);
    } else {
      particles?.addUnitDeathShatter?.(this);
    }
    this._playDeathSound(audio);
    if (this.isHacker) {
      this._releaseHackerTarget(battlefield, particles, audio);
    }
    if (this.hasTether && this.tetherTargetId) {
      const tethered = battlefield?.getUnitOrBaseById?.(this.tetherTargetId);
      if (tethered && !tethered.isDead && !tethered.isGoldenStatue) {
        tethered.acidStacks = (tethered.acidStacks || 0) + 1;
        particles?.addDamageText?.(tethered.x, tethered.y - 20, 'ACID DISSOLVE', 'crit');
        particles?.addAcidSplash?.(tethered.x, tethered.y, 16);
      }
      this.tetherTargetId = null;
    }
    if (this.hasBeamTether) {
      this.beamTargetId = null;
    }
    if (this.hasDualTether) {
      if (this.dualTetherTarget1Id) {
        const t1 = battlefield?.getUnitOrBaseById?.(this.dualTetherTarget1Id);
        if (t1) t1.isRooted = false;
        this.dualTetherTarget1Id = null;
      }
      if (this.dualTetherTarget2Id) {
        const t2 = battlefield?.getUnitOrBaseById?.(this.dualTetherTarget2Id);
        if (t2) t2.isRooted = false;
        this.dualTetherTarget2Id = null;
      }
      this.tether1LockTimer = 0;
    }
    if (this.isProcessingPillar) {
      audio?.stopProcessingPillar?.();
      this.plasmaChargeTimer = 0;
      this.plasmaChargeTarget = null;
      this.plasmaMorphed = false;
    }
    if (this.isMonitorLock) {
      if (battlefield?.units) {
        for (let i = 0; i < battlefield.units.length; i++) {
          const u = battlefield.units[i];
          if (u.team === this.team && u._monitorLockBuffApplied) {
            u.maxHp = Math.max(1, u.maxHp - (u._monitorLockHpBonus || 0));
            u.hp = Math.min(u.hp, u.maxHp);
            u._monitorLockBuffApplied = false;
            u._monitorLockHpBonus = 0;
          }
        }
      }
    }
    if (this.cardData.isBunker) {
      this.spawnBunkerCrew(battlefield);
    }
    if (this.ejectPilotOnDeath || this.cardData.unitType === 'A9Piranha') {
      this.spawnParachutePilot(battlefield);
    }
    if (this.isEnforcerVulture) {
      this.spawnVultureBrood(battlefield);
    }
  }

  releaseBeamTether(battlefield = null) {
    this.beamTargetId = null;
    this._beamDmgAccum = 0;
  }

  spawnBunkerCrew(battlefield) {
    if (!battlefield || !Array.isArray(battlefield.units)) return;
    const marineCard = {
      name: "Marine", race: "Human", hp: 45, damage: 6, attackSpeed: 0.4,
      range: 5.0, speed: 2.25, armor: 0, attributes: ["Light", "Biological"],
      targetsAir: true, targetsGround: true, images: ["Marine_1.png"], tier: 1
    };
    for (let m = 0; m < (this.cardData.maxGarrison || 2); m++) {
      const mX = this.x + this._rng.range(-15, 15);
      const mY = this.y + this._rng.range(-15, 15);
      battlefield.spawnRaw(marineCard, this.team, mX, mY);
    }
  }

  spawnParachutePilot(battlefield) {
    if (!battlefield || !Array.isArray(battlefield.units)) return;
    battlefield.spawnRaw(PARACHUTE_PILOT_CARD, this.team, this.x, this.y, {
      isParachuting: true,
      parachuteTimer: 2.0,
      parachuteDuration: 2.0,
      parachuteAltitude: 36,
      materializeTimer: 0,
      materializeDuration: 0
    });
  }

  spawnVultureBrood(battlefield) {
    if (!battlefield || !Array.isArray(battlefield.units)) return;
    const loafCard = CARDS_DATA.find(c => c.name === 'Defective Loaf');
    if (loafCard) {
      battlefield.spawnRaw(loafCard, this.team, this.x - 14, this.y + 10);
      battlefield.spawnRaw(loafCard, this.team, this.x + 14, this.y + 10);
    }
  }

  update(dt, battlefield, particles, audio) {
    if (this.isDead) return;
    this.age += dt;
    if (this._overtakeAnimTime > 0) {
      this._overtakeAnimTime = Math.max(0, this._overtakeAnimTime - dt);
    }
    if (this._dodgeAnimTime > 0) {
      this._dodgeAnimTime = Math.max(0, this._dodgeAnimTime - dt);
    }
    if (this._punchAnimTime > 0) {
      this._punchAnimTime = Math.max(0, this._punchAnimTime - dt);
    }

    // 🏛️ Golden Statue / Monument & Dead Stickman Obstacle: Completely immutable frozen monument
    if (this.isGoldenStatue) {
      this.speed = 0;
      this.baseSpeed = 0;
      this.canAttack = false;
      this.attackCooldown = Infinity;
      this.isUntargetable = true;
      this.mass = Infinity;
      this.hitFlashTimer = 0;

      if (this.isStickman && this._kneelingAnimTime > 0) {
        this._kneelingAnimTime = Math.max(0, this._kneelingAnimTime - dt);
        const elapsed = (this._kneelingAnimDuration || 3.8) - this._kneelingAnimTime;
        if (elapsed < 1.6 && (dt >= 0.25 || Math.random() < 0.35)) {
          particles?.addStickmanJointSparks?.(this.x, this.y, this.team);
        }
        if (elapsed >= 0.65 && elapsed <= 2.6 && (dt >= 0.1 || Math.random() < 0.5)) {
          particles?.addStickmanShoulderSparkShower?.(this.x, this.y, this.team, 1.0);
        }
        if (elapsed >= 1.6 && !this._stickmanDustSpawned) {
          this._stickmanDustSpawned = true;
          const forwardDir = this.facingDir || (this.team === 'western' ? 1 : -1);
          particles?.addStickmanDeathDustCloud?.(this.x, this.y + 20, forwardDir, 64);
          const weaponX = this.x + forwardDir * 28;
          particles?.addStickmanWeaponImpactSparks?.(weaponX, this.y + 24, forwardDir);
          (audio || battlefield?.audio || particles?.audio)?.playStickmanWreck?.({ x: this.x });
        }
      }

      // Airborne statue falling physics (falls with gravity and crashes onto ground)
      if (this.isFallingStatue) {
        this.fallVelocity = (this.fallVelocity || 0) + 980 * dt;
        this.y += this.fallVelocity * dt;
        const groundTargetY = this._statueGroundTargetY !== undefined
          ? this._statueGroundTargetY
          : (battlefield ? Math.min(battlefield.laneY + battlefield.laneHeight - 20, this.y + 35) : (this.y + 35));
        if (this.y >= groundTargetY) {
          this.y = groundTargetY;
          this.isFallingStatue = false;
          this.isAir = false;
          this.elementType = 'Ground';
          this.fallVelocity = 0;
          particles?.addStatueGroundCrash?.(this.x, this.y);
          audio?.playStatueMetallicCrash?.({ x: this.x });
        }
      }
      return;
    }

    // 🥚 Sul Bio-Cocoon (Experimental Metamorphosis, 500 HP, targetable)
    if (this.isCocoon) {
      this.speed = 0;
      this.baseSpeed = 0;
      this.canAttack = false;
      this.attackCooldown = Infinity;
      this.isUntargetable = false;
      this.mass = Infinity;

      if (this.isFallingCocoon) {
        this.fallVelocity = (this.fallVelocity || 0) + 980 * dt;
        this.y += this.fallVelocity * dt;
        const groundTargetY = this._cocoonGroundTargetY !== undefined
          ? this._cocoonGroundTargetY
          : (battlefield ? Math.min(battlefield.laneY + battlefield.laneHeight - 20, this.y + 35) : (this.y + 35));
        if (this.y >= groundTargetY) {
          this.y = groundTargetY;
          this.isFallingCocoon = false;
          this.isAir = false;
          this.elementType = 'Ground';
          this.fallVelocity = 0;
          particles?.addStatueGroundCrash?.(this.x, this.y);
          audio?.playStatueMetallicCrash?.({ x: this.x });
        }
      }

      this.cocoonTimer = Math.max(0, (this.cocoonTimer || 0) - dt);
      if (this.cocoonTimer <= 0) {
        this.hatchCocoon(battlefield, particles, audio);
      }
      return;
    }

    // 🔲 Disabled unit (inflicted by Hacker cable harpoon)
    if (this.isDisabled) {
      if (this.disabledByHackerId) {
        const bf = battlefield || this.battlefield || (typeof window !== 'undefined' ? window.tcgGame?.battlefield : null);
        const hacker = bf?.getUnitOrBaseById?.(this.disabledByHackerId) || bf?.getUnitById?.(this.disabledByHackerId) || bf?.units?.find(u => u.id === this.disabledByHackerId);
        if (!hacker || hacker.isDead || hacker.hackerTargetId !== this.id || !hacker.isHacking) {
          this.reactivateFromHack(bf, particles, audio);
        } else {
          this.speed = 0;
          this.isWalking = false;
          this.isEngaged = false;
          this.attackCooldown = Math.max(this.attackCooldown || 0.1, 0.5);
          return;
        }
      } else {
        this.speed = 0;
        this.isWalking = false;
        this.isEngaged = false;
        this.attackCooldown = Math.max(this.attackCooldown || 0.1, 0.5);
        return;
      }
    }

    // 0. Gold Edition Drop-In effect on first simulation frame
    if (this.spawnEffectPending) {
      this.spawnEffectPending = false;
      const groundPointY = this.y + (this.isAir ? 24 : 6);
      particles?.addDropInEffect?.(this.x, groundPointY, this.radius, 'gold');
      audio?.playGoldDropIn?.({ x: this.x });
    }

    // Wheelie Bomber engine rev & acceleration on drive-off (first simulation frame)
    if (this.driveAudioPending) {
      this.driveAudioPending = false;
      this.driveAudioSoundId = audio?.playWheelieEngine?.(0.7, { x: this.x }) ?? null;
    }

    // A9 Piranha: Rotor-/Motor-Loop startet ab dem ersten Simulationsframe (first simulation frame)
    if (this.motorLoopPending) {
      this.motorLoopPending = false;
      if (audio?.startA9PiranhaMotorLoop) {
        audio.startA9PiranhaMotorLoop();
        this.motorLoopActive = true;
      }
    }

    // Anti Shade: Globaler Schattenschleifen-Atem startet ab dem ersten Simulationsframe
    if (this.breathAudioPending) {
      this.breathAudioPending = false;
      this.breathAudioActive = true;
      audio?.playAntiShadeBreath?.();
    }

    // Decay thermal barrel glow & spawn rising thermal air shimmer
    if (this.barrelHeat > 0) {
      this.barrelHeat = Math.max(0, this.barrelHeat - dt * 0.65);
      if (this.barrelHeat > 0.35 && particles) {
        this._heatShimmerTimer = (this._heatShimmerTimer || 0) + dt;
        if (this._heatShimmerTimer >= 0.22) {
          this._heatShimmerTimer = 0;
          const uType = this.cardData?.unitType || this.name || '';
          const hasHeavyBarrel = (uType === 'Panzer' || uType === 'HyperPanzer' || uType === 'A9Grizzly' || uType === 'A9Piranha' || uType === 'A9Pigeon' || uType === 'DeployableCannon' || uType === 'Deployable_Cannon');
          if (hasHeavyBarrel) {
            const dir = this.facingDir || (this.team === 'western' ? 1 : -1);
            const muzzleX = this.x + dir * (this.radius * 0.85);
            const muzzleY = this.y - 6;
            particles?.addHeatShimmer?.(muzzleX, muzzleY, 14, 10, 0.45);
          }
        }
      }
    }
    // takeDamage() has no battlefield parameter, but death effects (bunker crew)
    // need one - keep the current reference here.
    this.battlefield = battlefield;

    if (this.isParachuting) {
      this.attackCooldown = 0.5;
      this.parachuteTimer -= dt;
      this.parachuteAltitude = Math.max(0, (this.parachuteTimer / this.parachuteDuration) * 36);
      const fDir = this.team === 'western' ? 1 : -1;
      this.x += fDir * (this.speed * 0.4) * dt;
      if (this.parachuteTimer <= 0) {
        this.isParachuting = false;
        this.parachuteAltitude = 0;
        particles?.addGroundDustKickup?.(this.x, this.y + this.radius * 0.45, fDir, this.radius * 0.5);
        if (this.cardData?.spriteRecolor === 'gold') {
          particles?.addShockwave?.(this.x, this.y, 22, '#facc15', 0.22);
        }
      }
      return;
    }

    if (this.isCinematic) {
      this.attackCooldown = 0.5; // keeps the isWalking() render heuristic true (no target, no attack windup)
      return; // no marchForward(), no targeting/attack - a cutscene script sets x/y directly
    }

    // 🏃 Sul Retreat Protocol: Units cease fighting, turn around, flee to home base
    if (this.isRetreating) {
      this.target = null;
      this.canAttack = false;
      this.attackCooldown = Infinity;
      this.isWalking = true;
      this.isEngaged = false;
      const retreatDir = this.team === 'western' ? -1 : 1;
      this.facingDir = retreatDir;
      const moveStep = retreatDir * (this.speed || this.baseSpeed) * dt;
      this.x += moveStep;

      // Base arrival check
      const homeBase = battlefield?.bases?.[this.team];
      const baseThresholdX = homeBase
        ? homeBase.x + (this.team === 'western' ? 35 : -35)
        : (this.team === 'western' ? 120 : (battlefield?.width || 1600) - 120);
      const reachedBase = this.team === 'western' ? (this.x <= baseThresholdX) : (this.x >= baseThresholdX);

      if (reachedBase) {
        battlefield?.handleUnitRetreat?.(this);
        return;
      }

      this.emitMovementEffects?.(particles, dt);
      return;
    }

    if (this.timedLife > 0) {
      this.timedLife -= dt;
      if (this.timedLife <= 0) {
        this.die(battlefield, particles, audio);
        return;
      }
    }

    if (this.hpDecayRate > 0) {
      this.hp -= this.hpDecayRate * dt;
      if (this.hp <= 0) {
        this.hp = 0;
        this.die(battlefield, particles, audio);
        return;
      }
    }

    // Changeling Disguise: locks onto a random currently-alive enemy unit's
    // identity the first chance it gets (keeps retrying each frame until an
    // enemy actually exists), then keeps that borrowed shape for life.
    if (this.isDecoy && !this.disguiseUnitType) {
      const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
      _decoyCandidatesScratch.length = 0;
      if (battlefield?.units) {
        for (let i = 0; i < battlefield.units.length; i++) {
          const u = battlefield.units[i];
          if (u.team === enemyTeam && !u.isDead && !u.isDecoy) {
            _decoyCandidatesScratch.push(u);
          }
        }
      }
      if (_decoyCandidatesScratch.length > 0) {
        const pick = this._rng.pick(_decoyCandidatesScratch);
        this.disguiseUnitType = pick.cardData.unitType || pick.name;
        this.disguiseRace = pick.race;
      }
    }

    // 1B. Wounded state - escalates in two tiers instead of a single 25%
    // cutoff, so HP loss reads as visible damage building up rather than a
    // switch that flips late. Below 50%: light intermittent smoke. Below
    // 25%: heavier smoke plus occasional sparks (with a brief flicker light,
    // reusing the same reactive-light system explosions use).
    if (particles && this.hp > 0) {
      const hpRatio = this.hp / this.maxHp;
      if (hpRatio < 0.5 && Math.random() < 0.12) {
        if (particles.addWoundedSmoke) {
          particles.addWoundedSmoke(this.x, this.y, this.radius, false);
        }
      }
      if (hpRatio < 0.25 && Math.random() < 0.35) {
        if (particles.addWoundedSmoke) {
          particles.addWoundedSmoke(this.x, this.y, this.radius, true);
        }
        if (Math.random() < 0.4) {
          particles.addLight?.(this.x, this.y - this.radius * 0.3, this.radius * 1.8, '#f97316', 0.12);
          if (particles.addWoundedSpark) {
            particles.addWoundedSpark(this.x, this.y, this.radius);
          }
        }
      }
    }

    // 2. Update Buffs / Temporary Effects
    this.speed = this.baseSpeed;
    if (!this.isStructure && (battlefield || this.battlefield)?.moduleSystem?.hasCongressBuff(this.team, 'panzerschokolade')) {
      this.speed += 1.5 * SPEED_MULTIPLIER;
    }
    this.buffDamageMult = 1.0;
    this.buffAccuracyPenalty = 0;
    this.isStunned = false;
    let attackSpeedMultiplier = 1.0;
    for (let i = this.buffs.length - 1; i >= 0; i--) {
      const b = this.buffs[i];
      b.duration -= dt * 1000;
      if (b.speedMult) this.speed *= b.speedMult;
      if (b.attackSpeedMult) attackSpeedMultiplier *= b.attackSpeedMult;
      if (b.dmgMult) this.buffDamageMult *= b.dmgMult;
      if (b.accuracyPenalty) this.buffAccuracyPenalty += b.accuracyPenalty;
      if (b.name === 'Stun') this.isStunned = true;
      if (b.duration <= 0) {
        this.buffs.splice(i, 1);
      }
    }

    // 2b. Incinerate Stacking DoT (5s duration, 2 dps, stacks per grenade hit)
    if (this.incinerateStacks && this.incinerateStacks.length > 0 && !this.isDead) {
      for (let i = this.incinerateStacks.length - 1; i >= 0; i--) {
        const stack = this.incinerateStacks[i];
        stack.remaining -= dt;
        stack.tickTimer -= dt;
        if (stack.tickTimer <= 0) {
          stack.tickTimer += 0.5;
          const tickDmg = stack.dps * 0.5;
          this.takeDamage(tickDmg, particles, 'Energy', false);
          if (this.isDead) break;
        }
        if (stack.remaining <= 0) {
          this.incinerateStacks.splice(i, 1);
        }
      }
      if (!this.isDead && particles?.addRisingEmbers && Math.random() < 0.35) {
        particles.addRisingEmbers(this.x, this.y, 2, 8);
      }
    }

    // 2c. Sul Dissolving Acid (Permanent 1 dmg/sec per stack until unit dies)
    if (this.acidStacks > 0 && !this.isDead) {
      this.acidTickTimer = (this.acidTickTimer || 0) + dt;
      this._acidSizzleTimer = (this._acidSizzleTimer || 0) + dt;
      if (this._acidSizzleTimer >= 0.45) {
        this._acidSizzleTimer = 0;
        particles?.addAcidSizzle?.(this.x, this.y, true);
      }
      while (this.acidTickTimer >= 1.0) {
        this.acidTickTimer -= 1.0;
        this.takeDamage(this.acidStacks * 1.0, particles, 'Energy', true);
        if (particles?.addAcidSplash && Math.random() < 0.4) {
          particles.addAcidSplash(this.x, this.y, 6);
        }
        if (this.isDead) break;
      }
    }

    // 2d. Sul Spore Slow (10s speed reduction, clamped to minimum 0.5 * SPEED_MULTIPLIER)
    if (this.sporeSlowTimer > 0 && !this.isDead) {
      this.sporeSlowTimer = Math.max(0, this.sporeSlowTimer - dt);
      const penalty = (this.sporeSlowPenalty || 2.0) * SPEED_MULTIPLIER;
      this.speed = Math.max(0.5 * SPEED_MULTIPLIER, this.speed - penalty);
      if (this.sporeSlowTimer <= 0) {
        this.sporeSlowPenalty = 0;
      }
      if (particles?.addAcidSplash && Math.random() < 0.2) {
        particles.addAcidSplash(this.x, this.y, 2);
      }
    }

    // 2e. Sul Delving Pillar Disarm (3s weapon shutdown / blind)
    if (this.disarmTimer > 0 && !this.isDead) {
      this.disarmTimer = Math.max(0, this.disarmTimer - dt);
    }

    // 2f. Sul Forced Overstimulation (10s frenzy: 2x HP/Speed/AttackSpeed, -0.1 Acc, instant death at 0s)
    if (this.overstimulationTimer > 0 && !this.isDead) {
      this.overstimulationTimer = Math.max(0, this.overstimulationTimer - dt);
      this.isOverstimulated = this.overstimulationTimer > 0;
      if (this.overstimulationTimer <= 0) {
        if (particles?.addAcidSplash) {
          particles.addAcidSplash(this.x, this.y, 8);
        }
        if (particles?.addShockwave) {
          particles.addShockwave(this.x, this.y, this.radius * 2.0, '#ef4444', 0.25);
        }
        this.die(battlefield, particles, audio, null);
        return;
      }
    }

    if (this.isOverstimulated && !this.isDead && particles?.addRisingEmbers && Math.random() < 0.25) {
      particles.addRisingEmbers(this.x, this.y, 1, 6);
    }

    // Silenced/Stunned: skip abilities, healing and combat/movement entirely
    // (cooldown timers below still tick down passively).
    if (this.isStunned || this.isDisabled) {
      if (this.isDisabled) {
        this.speed = 0;
        this.isWalking = false;
        this.isEngaged = false;
        this.attackCooldown = Math.max(this.attackCooldown || 0.1, 0.5);
      } else if (this.attackCooldown > 0) {
        this.attackCooldown -= dt;
      }
      return;
    }

    // 3. Spawner Trait
    if (this.spawner) {
      this.spawnTimer += dt;
      if (this.spawnTimer >= this.spawnInterval) {
        this.spawnTimer = 0;
        battlefield.spawnUnitByName(this.spawner, this.team, this.x, this.y);
      }
    }

    // 2. Cooldowns & Timers
    if (this.attackCooldown > 0) this.attackCooldown -= dt;
    if (this.disarmTimer > 0 && this.attackCooldown < 0.2) {
      this.attackCooldown = 0.2;
    }
    if (this.isHyperPanzer) {
      if (this.cannonCooldown > 0) this.cannonCooldown -= dt;
      if (this.rocketCooldown > 0) this.rocketCooldown -= dt;
      if (this.disarmTimer > 0) {
        if (this.cannonCooldown < 0.2) this.cannonCooldown = 0.2;
        if (this.rocketCooldown < 0.2) this.rocketCooldown = 0.2;
      }
    }
    if (this.decloakTimer > 0) this.decloakTimer -= dt;
    if (this.cloakedByAuraTimer > 0) this.cloakedByAuraTimer -= dt;
    if (this.blinkCooldown > 0) this.blinkCooldown -= dt;
    if (this.hitFlashTimer > 0) this.hitFlashTimer -= dt;
    if (this.underFireTimer > 0) this.underFireTimer = Math.max(0, this.underFireTimer - dt);
    if (this.hitFlinchTimer > 0) this.hitFlinchTimer = Math.max(0, this.hitFlinchTimer - dt);
    if (this.hitSquashTimer > 0) this.hitSquashTimer = Math.max(0, this.hitSquashTimer - dt);
    if (this.smokeBarrelTimer > 0) this.smokeBarrelTimer = Math.max(0, this.smokeBarrelTimer - dt);
    if (this.ghostHpTimer > 0) {
      this.ghostHpTimer = Math.max(0, this.ghostHpTimer - dt);
    }
    if (this.ghostHpTimer <= 0 && this.ghostHp > this.hp) {
      this.ghostHp = Math.max(this.hp, this.ghostHp - Math.max(25, this.maxHp * 1.8) * dt);
    }
    if (this.hp > this.ghostHp) {
      this.ghostHp = this.hp;
    }
    if (this.materializeTimer > 0) this.materializeTimer = Math.max(0, this.materializeTimer - dt);

    // ⚡ Critical HP short-circuit electrical sparks for mechanical units (< 30% HP)
    const isMech = !this.isStructure && (this.attributes?.includes('Mechanical') || this.cardData?.attributes?.includes?.('Mechanical') || [
      'TeslaWalker', 'HyperTeslaWalker', 'Panzer', 'HyperPanzer', 'DeployableCannon',
      'Rocketship', 'WheelieBomber', 'DevilPatrol', 'HyperDevilPatrol',
      'A9Piranha', 'A9Grizzly', 'A9Pigeon', 'Stickman', 'TheSalvageCompany', 'Orchestra', 'EnforcerVulture'
    ].includes(this.cardData?.unitType || '') || [
      'Tesla Walker', 'Hyper Tesla Walker', 'Panzer', 'Hyper Panzer', 'Deployable Cannon',
      'Rocketship', 'Wheelie Bomber', 'Devil Patrol', 'Hyper Devil Patrol',
      'A9 Piranha', 'A9 Grizzly', 'A9 Pigeon', 'Stickman', 'The Salvage Company', 'Orchestra', 'Enforcer Vulture'
    ].includes(this.name || ''));

    if (isMech && this.hp < (this.maxHp || 100) * 0.30 && !this.isDead) {
      if (this.shortCircuitTimer === undefined) {
        this.shortCircuitTimer = 2.0 + Math.random() * 2.0;
      }
      this.shortCircuitTimer -= dt;
      if (this.shortCircuitTimer <= 0) {
        this.shortCircuitTimer = 2.0 + Math.random() * 2.0;
        particles?.addShortCircuitSpark?.(this.x, this.y - (this.radius || 15) * 0.2);
      }
    }

    // 💨 Tread kickup & exhaust backfire when tracked vehicles accelerate from standstill
    const uType = this.unitType || this.cardData?.unitType;
    const isTracked = (uType === 'Panzer' || uType === 'HyperPanzer' || uType === 'A9Grizzly' || this.name === 'Panzer' || this.name === 'Hyper Panzer' || this.name === 'A9 Grizzly');
    if (isTracked) {
      if (this.treadKickupCooldown > 0) {
        this.treadKickupCooldown = Math.max(0, this.treadKickupCooldown - dt);
      }
      const isPhysicallyWalking = !this.isStructure && !this.isHeld && !this.isDisabled && (this.isHyperPanzer ? !this.isStoppedAtBase : (this.attackCooldown <= 0 || !this.target || !battlefield?.isInRange?.(this, this.target)));
      if (!this.wasWalking && isPhysicallyWalking && this.treadKickupCooldown <= 0) {
        const dir = this.facingDir || (this.team === 'western' ? 1 : -1);
        particles?.addTreadKickup?.(this.x, this.y, dir);
        this.treadKickupCooldown = 1.5;
      }
      this.wasWalking = isPhysicallyWalking;
    }

    if (this.smokeCloakTimer > 0) {
      this.smokeCloakTimer = Math.max(0, this.smokeCloakTimer - dt);
      if (this.smokeCloakTimer <= 0) {
        this.deflectionBonus = 0;
      }
    }

    // 🕷️ Sul Forgotten Loaf (Spider Mine, ID 41): Sprints to frontline (territoryDivider), burrows in place,
    // waits untargetable for any enemy (ground or air) within range 4 (96px), rises for 1.0s as an unstoppable
    // hot-air balloon, and detonates for 70 splash damage.
    if (this.isForgottenLoaf && !this.isDead && !this.isCocoon && !this.isGoldenStatue) {
      if (this.burrowAnchorX !== null && this.burrowAnchorX !== undefined && Number.isFinite(this.burrowAnchorX)
        && (this.isBurrowed || this.mineState === 'burrowed' || this.mineState === 'burrowing' || this.mineState === 'rising' || this.isMineRising)) {
        this.x = this.burrowAnchorX;
        if (this.burrowAnchorY !== null && this.burrowAnchorY !== undefined && Number.isFinite(this.burrowAnchorY)) {
          this.y = this.burrowAnchorY;
        }
      }

      if (this.mineState === 'sprinting') {
        this.isBurrowed = false;
        this.isBurrowing = false;
        this.isMineRising = false;
        this.isUntargetable = false;
        const dividerX = (battlefield?.territoryDivider !== undefined && battlefield?.territoryDivider !== null)
          ? battlefield.territoryDivider
          : ((battlefield?.width || 1600) / 2);
        const dir = this.team === 'western' ? 1 : -1;
        this.facingDir = dir;
        const nextX = this.x + dir * this.speed * dt;
        const reachedDivider = (this.team === 'western' && nextX >= dividerX) || (this.team === 'eastern' && nextX <= dividerX);
        if (reachedDivider) {
          this.x = dividerX;
          this.burrowAnchorX = dividerX;
          this.burrowAnchorY = this.y;
          this.mineState = 'burrowing';
          this.isBurrowing = true;
          this.burrowTimer = 0.75;
          this.speed = 0;
          this.baseSpeed = 0;
          this.isWalking = false;
          this.mass = Infinity;
          particles?.addGroundDustKickup?.(this.x, this.y, dir, 12);
          audio?.playBurrow?.({ x: this.x });
        } else {
          this.x = nextX;
          this.isWalking = true;
          this.emitMovementEffects?.(particles, dt);
        }
        return;
      } else if (this.mineState === 'burrowing') {
        this.speed = 0;
        this.baseSpeed = 0;
        this.isWalking = false;
        this.mass = Infinity;
        if (this.burrowAnchorX === null || this.burrowAnchorX === undefined) {
          this.burrowAnchorX = this.x;
          this.burrowAnchorY = this.y;
        }
        this.x = this.burrowAnchorX;
        if (Number.isFinite(this.burrowAnchorY)) this.y = this.burrowAnchorY;
        this.burrowTimer = Math.max(0, (this.burrowTimer !== undefined ? this.burrowTimer : 0.75) - dt);
        if (this.burrowTimer <= 0) {
          this.burrowTimer = 0;
          this.isBurrowing = false;
          this.isBurrowed = true;
          this.isUntargetable = true;
          this.mineState = 'burrowed';
        }
        return;
      } else if (this.mineState === 'burrowed' || (this.isBurrowed && !this.isMineRising)) {
        this.mineState = 'burrowed';
        this.speed = 0;
        this.baseSpeed = 0;
        this.isWalking = false;
        this.isBurrowed = true;
        this.isBurrowing = false;
        this.isUntargetable = true;
        this.mass = Infinity;
        if (this.burrowAnchorX === null || this.burrowAnchorX === undefined) {
          this.burrowAnchorX = this.x;
          this.burrowAnchorY = this.y;
        }
        this.x = this.burrowAnchorX;
        if (Number.isFinite(this.burrowAnchorY)) this.y = this.burrowAnchorY;

        // Scan for any enemy unit (ground or air) within trigger range (range 4 = 96px)
        const triggerRadius = this.range || 96;
        const triggerRadiusSq = triggerRadius * triggerRadius;
        let triggered = false;
        if (battlefield?.units) {
          for (let i = 0; i < battlefield.units.length; i++) {
            const u = battlefield.units[i];
            if (!u || u.isDead || u.team === this.team) continue;
            if (u.isBase || u.isCinematic || u.isParachuting || u.isGoldenStatue || u.isCocoon || u.isUntargetable) continue;
            const dx = u.x - this.x;
            const dy = u.y - this.y;
            if (dx * dx + dy * dy <= triggerRadiusSq) {
              triggered = true;
              break;
            }
          }
        }
        if (triggered) {
          this.mineState = 'rising';
          this.isMineRising = true;
          this.mineRiseTimer = 1.0;
          this.wasBurrowedBeforeRising = true;
          this.isIndestructible = true;
          this.isUntargetable = true;
          audio?.playBalloonInflate?.({ x: this.x });
          particles?.addGroundDustKickup?.(this.x, this.y, 1, 10);
          particles?.addGroundDustKickup?.(this.x, this.y, -1, 10);
        }
        return;
      } else if (this.mineState === 'rising' || this.isMineRising) {
        this.mineState = 'rising';
        this.isMineRising = true;
        this.speed = 0;
        this.baseSpeed = 0;
        this.isWalking = false;
        this.isUntargetable = true;
        this.isIndestructible = true;
        this.mass = Infinity;
        if (this.burrowAnchorX === null || this.burrowAnchorX === undefined) {
          this.burrowAnchorX = this.x;
          this.burrowAnchorY = this.y;
        }
        this.x = this.burrowAnchorX;
        if (Number.isFinite(this.burrowAnchorY)) this.y = this.burrowAnchorY;

        this.mineRiseTimer = Math.max(0, (this.mineRiseTimer !== undefined ? this.mineRiseTimer : 1.0) - dt);
        if (this.mineRiseTimer <= 0) {
          this._detonateForgottenLoaf(battlefield, particles, audio);
        }
        return;
      }
    }

    // 🎖️ Human Pink Beret: Slick soldier with initial 45° seeking homing rocket & transition to stylish revolver pistol combat
    if (this.isPinkBeret && !this.isDead) {
      if (this.pinkBeretState === 'WAITING_FOR_TARGET') {
        this.speed = 0;
        this.isWalking = false;
        const furthestEnemy = this._findFurthestEnemyUnit(battlefield);
        if (furthestEnemy) {
          this.pinkBeretTargetId = furthestEnemy.id;
          this.pinkBeretAimTimer = 2.0;
          this.pinkBeretState = 'AIMING';
          this.facingDir = Math.sign(furthestEnemy.x - this.x) || (this.team === 'western' ? 1 : -1);
        }
        return;
      } else if (this.pinkBeretState === 'AIMING') {
        this.speed = 0;
        this.isWalking = false;
        this.pinkBeretAimTimer = Math.max(0, this.pinkBeretAimTimer - dt);

        let target = this.pinkBeretTargetId ? (battlefield?.getUnitOrBaseById ? battlefield.getUnitOrBaseById(this.pinkBeretTargetId) : battlefield?.units?.find(u => u.id === this.pinkBeretTargetId)) : null;
        if (!target || target.isDead) {
          target = this._findFurthestEnemyUnit(battlefield);
          if (target) {
            this.pinkBeretTargetId = target.id;
          }
        }
        if (target) {
          this.facingDir = Math.sign(target.x - this.x) || (this.team === 'western' ? 1 : -1);
        }

        if (this.pinkBeretAimTimer <= 0) {
          const fireDir = this.facingDir || (this.team === 'western' ? 1 : -1);
          const launchX = this.x + fireDir * 12;
          const launchY = this.y - 10;
          const rocketTarget = target || { x: this.x + fireDir * 400, y: this.y };

          if (audio?.playPinkBeretRocketLaunch) {
            audio.playPinkBeretRocketLaunch({ x: this.x });
          } else {
            audio?.playRocketLaunch?.('heavy', { x: this.x, unitType: 'PinkBeret' });
          }
          const customRocketTrail = this.getCustomRocketTrailHex?.() || '#ff2a85';

          battlefield?.addFrameEvent?.({
            type: 'ATTACK',
            unitType: 'PinkBeret',
            race: this.race || 'Human',
            x: launchX,
            y: launchY,
            tx: rocketTarget.x,
            ty: rocketTarget.y,
            team: this.team,
            projType: 'Rocket',
            attackerUnitId: this.id,
            targetUnitId: target?.id ?? null,
            customRocketTrail
          });

          battlefield?.addFrameEvent?.({
            type: 'PINK_BERET_ROCKET',
            startX: Math.round(launchX),
            startY: Math.round(launchY),
            targetUnitId: target?.id ?? null,
            targetX: Math.round(rocketTarget.x),
            targetY: Math.round(rocketTarget.y),
            speed: 480,
            trailColor: customRocketTrail
          });

          const dist = Math.hypot(rocketTarget.x - launchX, rocketTarget.y - launchY);
          const flightDuration = Math.max(0.40, Math.min(1.5, 0.20 + dist / 480));
          const flightTicks = Math.max(1, Math.round(flightDuration * 60));
          if (battlefield?.addProjectileSim) {
            battlefield.addProjectileSim({
              flightTicks,
              targetX: rocketTarget.x,
              targetY: rocketTarget.y,
              targetUnitId: target?.id ?? null,
              damage: 40,
              splashRadius: 0,
              team: this.team,
              projectileType: 'Rocket',
              sourceUnit: this,
              isGoldAttacker: this.edition === 'gold'
            });
          }

          if (particles?.addPinkBeretHomingRocket) {
            particles.addPinkBeretHomingRocket(launchX, launchY, rocketTarget, 480, () => {
              if (target && !target.isDead && !battlefield?.addProjectileSim) {
                target.takeDamage(40, particles, 'Rocket', true, this.edition === 'gold', this);
              }
            }, customRocketTrail, flightDuration, audio);
          }

          this.hasFiredInitialRocket = true;
          this.pinkBeretState = 'DISCARDING';
          this.pinkBeretTransitionTimer = 0.8;
        }
        return;
      } else if (this.pinkBeretState === 'DISCARDING') {
        this.speed = 0;
        this.isWalking = false;
        this.pinkBeretTransitionTimer = Math.max(0, this.pinkBeretTransitionTimer - dt);
        if (this.pinkBeretTransitionTimer <= 0) {
          this.pinkBeretState = 'NORMAL';
          const normalSpeed = (this.cardData?.speed || 3) * SPEED_MULTIPLIER;
          this.baseSpeed = normalSpeed;
          this.speed = normalSpeed;
        }
        return;
      }
    }

    // 🌐 Human Hacker: Cyber mystic data-harpoon & hyper-transcendent xlink
    if (this.isHacker && !this.isDead) {
      // 1. Dual-Hacker clash check (Hyper Transcendent XLink)
      if (!this.isTranscendent && this.hackerState !== 'EXHAUSTED' && battlefield?.units) {
        const opposingHacker = battlefield.units.find(u =>
          u && u !== this && !u.isDead && u.isHacker && u.team !== this.team && !u.isTranscendent && u.hackerState !== 'EXHAUSTED'
        );
        if (opposingHacker) {
          this._enterTranscendentState(battlefield, particles, audio);
          opposingHacker._enterTranscendentState(battlefield, particles, audio);
        }
      }

      // 2. Active Transcendent state: neutral, indestructible, drains 5 plasma/sec from owner, dies after 10s
      if (this.isTranscendent) {
        this.speed = 0;
        this.baseSpeed = 0;
        this.isStationary = true;
        this.isIndestructible = true;
        this.transcendentTimer = Math.max(0, (this.transcendentTimer || 10.0) - dt);
        if (this._transcendentHpGrace) {
          this._transcendentHpGrace = false;
        } else {
          this.hp = Math.max(0.1, (this.transcendentTimer / 10.0) * (this.maxHp || 35));
        }

        // Drain 5 plasma per second from the owner
        const eco = battlefield?.economy || battlefield?.game?.economy || (typeof window !== 'undefined' ? window.tcgGame?.economy : null);
        if (eco?.resources) {
          if (this.team === 'western' || this.team === 'eastern') {
            const resMain = eco.resources[this.team];
            if (resMain && typeof resMain.plasma === 'number') {
              resMain.plasma = Math.max(0, resMain.plasma - 5 * dt);
            }
            const r1 = eco.resources[this.team === 'western' ? 'w1' : 'e1'];
            const r2 = eco.resources[this.team === 'western' ? 'w2' : 'e2'];
            if (r1 && typeof r1.plasma === 'number') r1.plasma = Math.max(0, r1.plasma - 5 * dt);
            if (r2 && typeof r2.plasma === 'number') r2.plasma = Math.max(0, r2.plasma - 5 * dt);
          } else if (eco.resources[this.team] && typeof eco.resources[this.team].plasma === 'number') {
            eco.resources[this.team].plasma = Math.max(0, eco.resources[this.team].plasma - 5 * dt);
          }
        }
        const cardSys = battlefield?.cardSystem || battlefield?.game?.cardSystem;
        if (cardSys?.plasmas && cardSys.plasmas[this.team] !== undefined) {
          cardSys.plasmas[this.team] = Math.max(0, cardSys.plasmas[this.team] - 5 * dt);
        }

        if (this.transcendentTimer <= 0) {
          this.isTranscendent = false;
          this.isIndestructible = false;
          this.hackerState = 'EXHAUSTED';
          this.die(battlefield, particles, audio);
        }
        return;
      }

      // 3. Regular Hacker logic
      if (this.hackerState === 'IDLE') {
        this.speed = 0;
        this.baseSpeed = 0;
        const target = this._findClosestEnemyMobileUnit(battlefield);
        if (target) {
          this.hackerTargetId = target.id;
          this.isHacking = true;
          this.hackerState = 'HACKING';
          this.hackerLockTimer = 0;
          this.handRevealTimer = 0;
          this._harpoonLatched = false;
          target.isDisabled = true;
          target.disabledByHackerId = this.id;
          audio?.playHackerHarpoon?.({ x: this.x });
        }
        return;
      } else if (this.hackerState === 'HACKING') {
        this.speed = 0;
        this.baseSpeed = 0;
        const target = this.hackerTargetId ? battlefield?.getUnitOrBaseById?.(this.hackerTargetId) : null;
        if (!target || target.isDead || target.isGoldenStatue || target.isUntargetable || target.isIndestructible) {
          // Connected target died or became invalid!
          if (target) {
            target.isDisabled = false;
            target.disabledByHackerId = null;
          }
          this.hackerTargetId = null;
          this.isHacking = false;
          this._harpoonLatched = false;
          this.hackerState = 'DRIFTING';
          this.speed = (this.cardData.speed || 2) * SPEED_MULTIPLIER;
          this.baseSpeed = this.speed;
          const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
          this.target = battlefield?.getBase ? battlefield.getBase(enemyTeam) : battlefield?.bases?.[enemyTeam];
          particles?.addDamageText?.(this.x, this.y - 20, 'SIGNAL LOST', 'warn');
        } else {
          target.isDisabled = true;
          target.disabledByHackerId = this.id;
          this.hackerLockTimer += dt;

          // Harpoon flight to impact trigger
          const dist = Math.hypot(target.x - this.x, target.y - this.y);
          const flightDuration = Math.max(0.12, Math.min(0.40, dist / 1000));
          if (this.hackerLockTimer >= flightDuration && !this._harpoonLatched) {
            this._harpoonLatched = true;
            particles?.addDamageText?.(target.x, target.y - 18, 'DISRUPTED', 'crit');
            particles?.addHitSparks?.(target.x, target.y, 'plasma', 12);
          }

          // Cyclic hand reveal: 10s analyzing, 5s reveal (15s total cycle)
          const cycle = this.hackerLockTimer % 15.0;
          if (cycle >= 10.0) {
            if (this.handRevealTimer <= 0) {
              audio?.playHackerRevealChime?.({ x: this.x });
              particles?.addDamageText?.(this.x, this.y - 28, 'HAND REVEALED', 'crit');
            }
            this.handRevealTimer = 15.0 - cycle;
          } else {
            this.handRevealTimer = 0;
          }
        }
        return;
      } else if (this.hackerState === 'DRIFTING') {
        // Drifting toward enemy base
        const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
        const enemyBase = battlefield?.getBase ? battlefield.getBase(enemyTeam) : battlefield?.bases?.[enemyTeam];
        if (enemyBase && !enemyBase.isDead) {
          this.target = enemyBase;
          const dist = Math.hypot(enemyBase.x - this.x, enemyBase.y - this.y);
          if (dist <= (this.radius + (enemyBase.radius || 48) + 12)) {
            particles?.addShockwave?.(this.x, this.y, 60, '#00f0ff', 0.4);
            particles?.addDamageText?.(enemyBase.x, enemyBase.y - 30, 'EMP OVERLOAD', 'crit');
            enemyBase.takeDamage?.(30, particles, 'Energy', true, false, this);
            this.die(battlefield, particles, audio);
            return;
          }
        }
      }
    }

    // Hyper Tesla Walker Smoke Grenade Trigger (< 50% HP)
    if (this.cardData?.unitType === 'HyperTeslaWalker' || this.name === 'Hyper Tesla Walker') {
      if (!this.smokeGrenadeTriggered && this.hp < this.maxHp * 0.5 && !this.isDead) {
        this.smokeGrenadeTriggered = true;
        this.smokeCloakTimer = 5.0;
        this.deflectionBonus = 0.6;
        particles?.addSmokeScreen?.(this.x, this.y, 60);
      }
      if (this.smokeCloakTimer > 0) {
        // Cyber-Prowl: Only triggers if enemy infantry is within Pincer Stalk range (<= 120px)
        let hasInfantryInStalkRange = false;
        if (battlefield?.units) {
          const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
          for (let i = 0; i < battlefield.units.length; i++) {
            const eu = battlefield.units[i];
            if (eu.team === enemyTeam && !eu.isDead && !eu.isStructure && !eu.isAir) {
              const isInf = eu.cardData?.isInfantry === true ||
                STALK_INFANTRY_TYPES.has(eu.cardData?.unitType || eu.unitType) ||
                STALK_INFANTRY_TYPES.has(eu.cardData?.name || eu.name);
              if (isInf) {
                const dSq = (eu.x - this.x) * (eu.x - this.x) + (eu.y - this.y) * (eu.y - this.y);
                if (dSq <= 120 * 120) {
                  hasInfantryInStalkRange = true;
                  break;
                }
              }
            }
          }
        }
        if (hasInfantryInStalkRange) {
          this.speed *= 1.26; // Cyber-Prowl speed boost (1.5 -> 1.9)
        }
      }
    }

    // Sul Enforcer Vulture: 8-pod reload timers, active rocket interception, and staggered launch queue
    if (this.isEnforcerVulture && !this.isDead && this.vulturePods) {
      // 0. Decay launch recoil
      if (this._vultureRecoil > 0) {
        this._vultureRecoil = Math.max(0, this._vultureRecoil - dt * 10.0);
      }

      // 1. Tick pod reload timers & launch flashes
      for (let i = 0; i < this.vulturePods.length; i++) {
        if (this.vulturePods[i].reloadTimer > 0) {
          this.vulturePods[i].reloadTimer -= dt;
        }
        if (this.vulturePods[i].lastLaunchFlash > 0) {
          this.vulturePods[i].lastLaunchFlash = Math.max(0, this.vulturePods[i].lastLaunchFlash - dt);
        }
      }

      // 2. Active rocket trophy interception
      if (battlefield?.pendingProjectiles && (!this.disarmTimer || this.disarmTimer <= 0)) {
        const bPending = battlefield.pendingProjectiles;
        for (let pIdx = 0; pIdx < bPending.length; pIdx++) {
          const p = bPending[pIdx];
          if (p && !p.isIntercepted && p.team !== this.team && p.projectileType === 'Rocket') {
            const dist = Math.hypot(p.targetX - this.x, p.targetY - this.y);
            if (dist <= this.range * 24 + 100) {
              let readyPod = null;
              for (let podIdx = 0; podIdx < this.vulturePods.length; podIdx++) {
                const pod = this.vulturePods[podIdx];
                if (pod.reloadTimer > 0) continue;
                let queued = false;
                for (let qIdx = 0; qIdx < this.vultureLaunchQueue.length; qIdx++) {
                  if (this.vultureLaunchQueue[qIdx].podIndex === pod.index) { queued = true; break; }
                }
                if (!queued) { readyPod = pod; break; }
              }
              if (readyPod) {
                p.isIntercepted = true;
                p.damage = 0;
                this.vultureLaunchQueue.push({
                  podIndex: readyPod.index,
                  target: { x: (this.x + p.targetX) / 2, y: Math.min(this.y, p.targetY) - 35 },
                  isIntercept: true,
                  interceptProjectile: p
                });
                break;
              }
            }
          }
        }
      }

      // 3. Staggered launch queue (paced at 0.32s per ball so the 8 missiles rise in a majestic fountain)
      if (this.vultureLaunchQueue.length > 0) {
        this._vultureLaunchTimer -= dt;
        if (this._vultureLaunchTimer <= 0) {
          this._vultureLaunchTimer = 0.32;
          const launchItem = this.vultureLaunchQueue.shift();
          this._executeVulturePodLaunch(launchItem, battlefield, particles, audio);
        }
      }
    }

    // Sul Looting Vulture: Transmute first enemy unit in range into an indestructible golden statue
    if (this.isLootingVulture && !this.isDead) {
      // 0. Continuous passive gold dust drift - throttled to every 3rd sim
      // tick (20 Hz instead of 60 Hz) with 3x the particles per burst so the
      // steady-state spawn rate (and visual density) is unchanged; gated on
      // the particle budget the same way every other cosmetic spawn is
      // (docs/PERFORMANCE_AND_ONLINE_STABILITY_PLAN_V2_2026-09.md §5.3).
      this._goldDustTickCounter = (this._goldDustTickCounter || 0) + 1;
      if (this._goldDustTickCounter >= 3) {
        this._goldDustTickCounter = 0;
        if (particles?.canSpawnCosmetic?.()) {
          particles.addGoldDust(this.x, this.y + (this.isAir ? 12 : 0), 3);
        }
      }

      // 1. One-time Midas petrification on first enemy in range
      if (!this.hasPetrifiedUnit && battlefield?.units) {
        const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
        let firstEnemy = null;
        let minDistance = Infinity;

        for (let i = 0; i < battlefield.units.length; i++) {
          const u = battlefield.units[i];
          if (u.team === enemyTeam && !u.isDead && !u.isUntargetable && !u.isGoldenStatue && !u.isCocoon && !u.isStructure && !u.isCinematic) {
            if (battlefield.isInRange(this, u)) {
              const d = Math.hypot(u.x - this.x, u.y - this.y);
              if (d < minDistance) {
                minDistance = d;
                firstEnemy = u;
              }
            }
          }
        }

        if (firstEnemy) {
          this.hasPetrifiedUnit = true;
          firstEnemy.isGoldenStatue = true;
          firstEnemy.hasGoldPedestal = true;
          firstEnemy.canAttack = false;
          firstEnemy.attackCooldown = Infinity;
          firstEnemy.speed = 0;
          firstEnemy.baseSpeed = 0;
          firstEnemy.isUntargetable = true;
          firstEnemy.mass = Infinity;
          firstEnemy.isRooted = false;
          if (battlefield?.goldenStatues && !battlefield.goldenStatues.includes(firstEnemy)) {
            battlefield.goldenStatues.push(firstEnemy);
          }

          // Sever any active allied tethers / locks pointing to the transmuted statue
          if (battlefield?.units) {
            for (let ui = 0; ui < battlefield.units.length; ui++) {
              const ally = battlefield.units[ui];
              if (!ally || ally.isDead) continue;
              if (ally.tetherTargetId === firstEnemy.id) {
                ally.tetherTargetId = null;
                if (ally.target === firstEnemy) ally.target = null;
              }
              if (ally.dualTetherTarget1Id === firstEnemy.id || ally.dualTetherTarget2Id === firstEnemy.id) {
                ally.dualTetherTarget1Id = null;
                ally.dualTetherTarget2Id = null;
                ally.tether1LockTimer = 0;
                if (ally.target === firstEnemy) ally.target = null;
              }
              if (ally.beamTargetId === firstEnemy.id) {
                ally.beamTargetId = null;
                if (ally.target === firstEnemy) ally.target = null;
              }
            }
          }

          if (firstEnemy.isAir) {
            firstEnemy.isFallingStatue = true;
            firstEnemy.fallVelocity = 120;
            const laneBottom = battlefield ? (battlefield.laneY + battlefield.laneHeight - 20) : (firstEnemy.y + 40);
            firstEnemy._statueGroundTargetY = Math.max(firstEnemy.y + 25, Math.min(laneBottom, firstEnemy.y + 35));
          }

          const apexX = this.x + (this.facingDir || (this.team === 'western' ? 1 : -1)) * 16;
          const apexY = this.y - 24;
          particles?.addMidasTransmutationBeam?.(apexX, apexY, firstEnemy.x, firstEnemy.y);
          audio?.playMidasTransmute?.({ x: this.x });
          battlefield?.addFrameEvent?.({ type: 'SOUND', name: 'playMidasTransmute', x: Math.round(this.x), y: Math.round(this.y) });
        }
      }
    }

    // Sul Parasite Vulture: Scan for closest eligible enemy unit and execute overtake
    if (this.isParasiteVulture && this.hasParasite && !this.isDead && battlefield?.units) {
      const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
      let closestTarget = null;
      let minDistanceSq = Infinity;
      // 360px leap range ensures full cross-lane coverage across the 320px lane height
      const maxRangePx = Math.max(this.range || 240, 360);

      let closestFieldTarget = null;
      let minFieldDistSq = Infinity;

      for (let i = 0; i < battlefield.units.length; i++) {
        const u = battlefield.units[i];
        if (u.team !== enemyTeam || u.isDead) continue;
        if (u.isCinematic || u.isGoldenStatue || u.isCocoon) continue;

        // Never overtake planetary fortress bases
        if (u.isBase || u.name?.includes('Fortress') || u.name?.includes('Base')) continue;

        // Not already parasitized or currently targeted by another parasite mid-air
        if (u.isParasitized || u._targetedByParasite) continue;

        const dx = u.x - this.x;
        const dy = u.y - this.y;
        const distSq = dx * dx + dy * dy;

        // Prioritize mobile combat units over stationary field structures when finding targets
        const isMobileUnit = !u.isStructure && !u.cardData?.isStructure;
        const targetWeight = isMobileUnit ? 1.0 : 1.35;
        const weightedDistSq = distSq * targetWeight;

        if (weightedDistSq < minFieldDistSq) {
          minFieldDistSq = weightedDistSq;
          closestFieldTarget = u;
        }

        const effectiveRadius = (this.radius || 17) + (u.radius || 20);
        const maxAllowedDist = maxRangePx + effectiveRadius;
        if (distSq <= maxAllowedDist * maxAllowedDist) {
          if (weightedDistSq < minDistanceSq) {
            minDistanceSq = weightedDistSq;
            closestTarget = u;
          }
        }
      }

      if (closestTarget) {
        this._executeParasiteOvertake(closestTarget, battlefield, particles, audio);
      } else if (closestFieldTarget) {
        this.target = closestFieldTarget;
        this.facingDir = Math.sign(closestFieldTarget.x - this.x) || (this.team === 'western' ? 1 : -1);
      } else {
        this.target = null;
      }
    }

    // Sul Claiming Pillar: 3s growth eruption, radial displacement of overlapping ground units, and 2 HP/s self-regeneration
    if (this.isClaimingPillar && !this.isDead) {
      if (this._hpRegenFlash > 0) {
        this._hpRegenFlash = Math.max(0, this._hpRegenFlash - dt);
      }
      if (this.hpRegenRate > 0) {
        this._hpRegenAccum = (this._hpRegenAccum || 0) + dt;
        while (this._hpRegenAccum >= 1.0) {
          this._hpRegenAccum -= 1.0;
          if (this.hp > 0 && this.hp < this.maxHp) {
            this.hp = Math.min(this.maxHp, this.hp + this.hpRegenRate);
            this._hpRegenFlash = 0.45;
            particles?.addHealingSparkles?.(this.x, this.y, 4);
            particles?.addDamageText?.(this.x, this.y - this.radius - 8, `+${Math.round(this.hpRegenRate)}`, 'heal');
          }
        }
      }
      if (this.isGrowing) {
        this.growthTimer += dt;
        const growthProgress = Math.min(1.0, this.growthTimer / this.growthDuration);
        if (growthProgress >= 0.15 && !this._eruptionBreachTriggered) {
          this._eruptionBreachTriggered = true;
          audio?.playSample?.('baked/sul_spawn.m4a', { volume: 0.85, x: this.x });
          particles?.addGroundDustKickup?.(this.x, this.y, 1, 8);
          particles?.addGroundDustKickup?.(this.x, this.y, -1, 8);
          particles?.addStructureDebris?.(this.x, this.y, 20);
        }
        if (this.growthTimer >= this.growthDuration) {
          this.isGrowing = false;
          this.growthTimer = this.growthDuration;
        } else if (battlefield?.units) {
          for (let i = 0; i < battlefield.units.length; i++) {
            const u = battlefield.units[i];
            if (!u || u === this || u.isDead || u.isAir || u.isStructure || u.isGoldenStatue || u.isCocoon) continue;
            const dx = u.x - this.x;
            const dy = u.y - this.y;
            const minDist = (this.radius || 22) + (u.radius || 17) + 2;
            const distSq = dx * dx + dy * dy;
            if (distSq < minDist * minDist) {
              const dist = Math.sqrt(distSq);
              if (dist > 0.001) {
                const push = minDist - dist;
                u.x += (dx / dist) * push;
                u.y += (dy / dist) * push;
              } else {
                const dir = u.team === 'western' ? -1 : 1;
                u.x += dir * minDist;
              }
            }
          }
        }
      }
    }

    // Sul Processing Pillar: Feeler Agitation & 3s Plasma Channeling
    if (this.isProcessingPillar && !this.isDead) {
      // 1. Feeler Agitation: Proximity sensor for nearby enemies (< 70px).
      // NOTE: deliberately left unthrottled, unlike the Aura Processing pass
      // below - a ~100ms-throttled version (mirroring that pattern) was
      // tried here and reverted: tests/processing_pillar_test.mjs asserts
      // whipAgitation reacts to an enemy moving into range within a single
      // dt=0.1 update() call, which a 100ms throttle window misses on
      // exactly that cadence (the scan lands in its "skip" half of the
      // window). Processing Pillars are buildings, not typically numerous,
      // so this scan's cost is low severity to begin with - not worth
      // changing observable/tested reaction latency for.
      let nearEnemy = false;
      if (battlefield?.units) {
        const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
        for (let i = 0; i < battlefield.units.length; i++) {
          const eu = battlefield.units[i];
          if (eu.team === enemyTeam && !eu.isDead && !eu.isUntargetable) {
            const dSq = (eu.x - this.x) * (eu.x - this.x) + (eu.y - this.y) * (eu.y - this.y);
            if (dSq <= 70 * 70) {
              nearEnemy = true;
              break;
            }
          }
        }
      }
      if (nearEnemy) {
        this.whipAgitation = Math.min(1.0, (this.whipAgitation || 0) + dt * 5.0);
      } else {
        this.whipAgitation = Math.max(0.0, (this.whipAgitation || 0) - dt * 2.5);
      }

      // 2. Plasma Bubble Channeling & Morphing Cycle
      if (this.plasmaChargeTimer > 0) {
        this.plasmaChargeTimer += dt;
        this.isEngaged = true;

        let targetCandidate = (this.plasmaChargeTarget && !this.plasmaChargeTarget.isDead) ? this.plasmaChargeTarget : null;
        if (targetCandidate) {
          this.plasmaTargetLastX = targetCandidate.x;
          this.plasmaTargetLastY = targetCandidate.y;
          this.plasmaTargetIsAir = !!targetCandidate.isAir;
        }

        const faceDir = (Math.sign(this.plasmaTargetLastX - this.x) || (this.team === 'western' ? 1 : -1));
        this.facingDir = faceDir;
        const spawnX = this.x + faceDir * (this.plasmaTargetIsAir ? 22 : 28);
        const spawnY = this.y - (this.plasmaTargetIsAir ? 30 : 6);

        // Phase 1: 0.0s to 3.0s (Plasma Bubbles gathering)
        if (this.plasmaChargeTimer < 3.0) {
          if (!targetCandidate) {
            // Target died or was lost during first 3 seconds: Cancel attack & fizzle!
            audio?.stopProcessingPillar?.();
            particles?.addPlasmaFizzle?.(spawnX, spawnY);
            this.plasmaChargeTimer = 0;
            this.plasmaChargeTarget = null;
            this.plasmaMorphed = false;
            this.attackCooldown = 0.5;
          } else {
            const progress = this.plasmaChargeTimer / 3.0;
            particles?.addPlasmaBubbleGather?.(spawnX, spawnY, progress, faceDir);
          }
        } else if (!this.plasmaMorphed) {
          // Phase 2: Morph and Launch at 3.0s!
          this.plasmaMorphed = true;
          const isGold = this.edition === 'gold';
          const targetObj = targetCandidate || {
            x: this.plasmaTargetLastX,
            y: this.plasmaTargetLastY,
            id: null,
            isDead: false,
            isStructure: false,
            isAir: this.plasmaTargetIsAir,
            takeDamage: () => {}
          };

          if (this.plasmaTargetIsAir) {
            // Air target: Elastic piercing plasma spear (+10% damage = 44)
            const airDmg = Math.round(this.damage * 1.1); // 44
            if (audio?.playProcessingPillar) audio.playProcessingPillar({ x: this.x, durationMs: 2000 });
            else audio?.playLaser ? audio.playLaser('normal', { x: this.x }) : null;
            const targetFlightY = this.plasmaTargetLastY - 18;
            const dist = Math.hypot(this.plasmaTargetLastX - spawnX, targetFlightY - spawnY);
            const flightTimeSec = Math.max(0.18, Math.min(0.38, dist / 750));
            const flightTicks = Math.max(1, Math.round(flightTimeSec * 60));

            particles?.addElasticPlasmaSpear?.(spawnX, spawnY, targetObj, airDmg, this.team, this, flightTimeSec);

            battlefield?.addProjectileSim({
              flightTicks: flightTicks,
              targetX: this.plasmaTargetLastX,
              targetY: this.plasmaTargetLastY,
              targetUnitId: targetCandidate ? targetCandidate.id : null,
              damage: airDmg,
              splashRadius: 0,
              team: this.team,
              projectileType: 'Energy',
              sourceUnit: this,
              isGoldAttacker: isGold
            });
          } else {
            // Ground target: Slow homing plasma sphere with splash (40 damage, 50px splash)
            if (audio?.playProcessingPillar) audio.playProcessingPillar({ x: this.x, durationMs: 2000 });
            else audio?.playExplosion ? audio.playExplosion('small', { x: this.x }) : null;
            const sphereDmg = this.damage;
            const splashRad = this.splashRadius || 50;
            const dist = Math.hypot(this.plasmaTargetLastX - spawnX, this.plasmaTargetLastY - spawnY);
            const flightTimeSec = Math.max(0.4, dist / 130);
            const flightTicks = Math.max(1, Math.round(flightTimeSec * 60));

            particles?.addHomingPlasmaSphere?.(spawnX, spawnY, targetObj, splashRad, sphereDmg, this.team, this, flightTimeSec);

            battlefield?.addProjectileSim({
              flightTicks: flightTicks,
              targetX: this.plasmaTargetLastX,
              targetY: this.plasmaTargetLastY,
              targetUnitId: targetCandidate ? targetCandidate.id : null,
              damage: sphereDmg,
              splashRadius: splashRad,
              team: this.team,
              projectileType: 'Energy',
              sourceUnit: this,
              isGoldAttacker: isGold
            });
          }
        } else if (this.plasmaChargeTimer >= 5.0) {
          // Full 5.0s cycle complete
          audio?.stopProcessingPillar?.();
          this.plasmaChargeTimer = 0;
          this.plasmaChargeTarget = null;
          this.plasmaMorphed = false;
          this.attackCooldown = 0;
        }
      }
    }

    // Sul Monitor Lock: 3s growth eruption & Swarm Overwatch aura
    if (this.isMonitorLock && !this.isDead) {
      if (this.snapStrikeTimer > 0) {
        this.snapStrikeTimer = Math.max(0, this.snapStrikeTimer - dt);
      }

      if (this.isGrowing) {
        this.growthTimer += dt;
        const growthProgress = Math.min(1.0, this.growthTimer / this.growthDuration);

        // Ground breach event: trigger high-impact particle burst, screen shake & audio once at breach (p >= 0.12)
        if (growthProgress >= 0.12 && !this._eruptionBreachTriggered) {
          this._eruptionBreachTriggered = true;
          audio?.playSample?.('baked/sul_spawn.m4a', { volume: 0.85, x: this.x });
          particles?.addShockwave?.(this.x, this.y, 75, '#00f0ff', 0.45);
          particles?.addDistortionRing?.(this.x, this.y, 60, 0.40, '#38bdf8');
          particles?.addScreenShake?.(5.0, 0.32);
          particles?.addStructureDebris?.(this.x, this.y, 28);
          particles?.addGroundDustKickup?.(this.x, this.y, 1, 9);
          particles?.addGroundDustKickup?.(this.x, this.y, -1, 9);
        }

        if (this.growthTimer >= this.growthDuration) {
          this.isGrowing = false;
          this.growthTimer = this.growthDuration;
        } else {
          // Collision check with enemy units during growth
          const currentEruptRadius = Math.max(16, this.radius * growthProgress + 6);
          if (battlefield?.units) {
            const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
            for (let i = 0; i < battlefield.units.length; i++) {
              const eu = battlefield.units[i];
              if (eu.team === enemyTeam && !eu.isDead && !eu.isUntargetable && (eu.executionTimer || 0) <= 0) {
                const dx = eu.x - this.x;
                const dy = eu.y - this.y;
                const totalR = currentEruptRadius + eu.radius;
                if (dx * dx + dy * dy <= totalR * totalR) {
                  const isMassiveOrStructure = eu.isStructure || eu.cardData?.attributes?.includes('Massive') || eu.attributes?.includes('Massive');
                  const crushDmg = isMassiveOrStructure ? 150 : (eu.hp + 9999);
                  eu.takeDamage(crushDmg, particles, 'Melee', true, this.edition === 'gold', this);
                  if (audio?.playMonitorLockCrush) audio.playMonitorLockCrush({ x: this.x });
                  else audio?.playExplosion?.('small', { x: this.x });
                  particles?.addHitSparks?.(eu.x, eu.y, 'plasma', 8);
                  particles?.addBloodSplatter?.(eu.x, eu.y, 12, '#991b1b');
                }
              }
            }
          }
        }
      } else {
        // Overwatch Swarm Buff pass (every 0.2s)
        this._overwatchTimer = (this._overwatchTimer || 0) - dt;
        if (this._overwatchTimer <= 0) {
          this._overwatchTimer = 0.2;
          if (battlefield?.units) {
            for (let i = 0; i < battlefield.units.length; i++) {
              const u = battlefield.units[i];
              if (u.team === this.team && !u.isDead && u.cardData?.race === 'Sul') {
                // Damage boost buff
                let existing = null;
                for (let bi = 0; bi < u.buffs.length; bi++) {
                  if (u.buffs[bi].name === 'SulOverwatch') { existing = u.buffs[bi]; break; }
                }
                if (existing) {
                  existing.duration = 500;
                } else {
                  u.buffs.push({ name: 'SulOverwatch', dmgMult: 1.0 + this.swarmBuffDamage, duration: 500 });
                }

                // HP boost (+30% Max & Current HP)
                if (!u._monitorLockBuffApplied) {
                  u._monitorLockBuffApplied = true;
                  u._monitorLockHpBonus = Math.round(u.maxHp * this.swarmBuffHp);
                  u.maxHp += u._monitorLockHpBonus;
                  u.hp += u._monitorLockHpBonus;
                  particles?.addDamageText?.(u.x, u.y - u.radius - 8, '+30% HP', 'heal');
                }
              }
            }
          }
        }
      }
    }

    // Execution Cooldown Decrement
    if (this.executionCooldown > 0) {
      this.executionCooldown = Math.max(0, this.executionCooldown - dt);
    }

    // Dramatic Melee Infantry Execution Update
    if (this.executionTimer > 0) {
      this.executionTimer = Math.max(0, this.executionTimer - dt);
      this.isUntargetable = true;

      let victim = battlefield?.getUnitById ? battlefield.getUnitById(this.executionTargetId) : null;
      if (!victim && battlefield?.units) {
        victim = battlefield.units.find(u => u.id === this.executionTargetId);
      }
      if (victim && victim.isDead) victim = null;
      if (victim) {
        victim.isHeld = true;
        if (victim.groundY === undefined) {
          victim.groundY = victim.y;
        }
        const totalDur = this.executionDuration || 1.45;
        const progress = Math.min(1.0, Math.max(0, 1.0 - (this.executionTimer / totalDur)));
        const dir = this.facingDir || (this.team === 'western' ? 1 : -1);

        if (progress < 0.14) {
          // Phase 1: Aggressive Hydraulic Pounce & Clamp (0.00s - 0.20s)
          const p = progress / 0.14;
          victim.x = this.x + dir * (26 + (1 - p) * 16);
          victim.y = victim.groundY;
          if (progress < 0.04 && !victim._grabShaked) {
            victim._grabShaked = true;
            particles?.addScreenShake?.(2.5, 0.12);
            particles?.addShockwave?.(victim.x, victim.y, 24, '#00f0ff', 0.16);
          }
        } else if (progress < 0.38) {
          // Phase 2: Heavy Mechanical Hoist (0.20s - 0.55s)
          const liftNorm = (progress - 0.14) / 0.24;
          const easeLift = Math.sin(liftNorm * Math.PI * 0.5);
          victim.x = this.x + dir * 26;
          victim.y = victim.groundY - (easeLift * 32);
          if (((progress * 100) % 4) < 1) {
            particles?.addTeslaArc?.(this.x + dir * 8, this.y - 34, victim.x, victim.y, '#00f0ff');
          }
        } else if (progress < 0.72) {
          // Phase 3: The Zenith Hold / Suspense Pause (0.55s - 1.05s) - 0.50s sustained suspense
          const holdNorm = (progress - 0.38) / 0.34;
          victim.x = this.x + dir * 26;
          victim.y = victim.groundY - 32;
          // High-voltage dielectric crackle arcs accelerate as cleave strikes
          if (((holdNorm * 12) % 3) < 2) {
            particles?.addTeslaArc?.(this.x + dir * 8, this.y - 34, victim.x, victim.y, '#00f0ff');
          }
        } else if (progress < 0.86) {
          // Phase 4: Hydraulic Guillotine Cleave (Instant Death & Shatter) (1.05s - 1.25s)
          victim.x = this.x + dir * 26;
          victim.y = victim.groundY - 32;
          if (!victim._cleaved) {
            victim._cleaved = true;
            audio?.playExplosion?.('small', { x: this.x });
            audio?.playGauss ? audio.playGauss({ x: this.x }) : null;
            particles?.addScreenShake?.(7.5, 0.30);
            particles?.addShockwave?.(victim.x, victim.y, 48, '#00f0ff', 0.28);
            particles?.addUnitSplitShatter?.(victim);
            particles?.addDamageText?.(victim.x, victim.groundY - 20, 'EXECUTED', 'crit');
            victim.isDead = true;
            battlefield?.stats?.markUnitDeath(victim.id);
            battlefield.handleUnitDeath(victim, particles);
          }
        }
      }

      if (this.executionTimer <= 0) {
        this.executionTimer = 0;
        this.executionTargetId = null;
        this.isUntargetable = false;
        this.isPincerStalking = false;
        this.facingDir = null;
      }
    } else if (!this.isBurrowed && !this.isMineRising && !this.isGoldenStatue) {
      this.isUntargetable = false;
    }

    // Incapacitated while being executed in the air: frozen in place
    if (this.isHeld) {
      return;
    }

    // Void Ray Prismatic Beam charge: authentic mod behavior steps the charge
    // up once per completed shot (the weapon fires once per second, and the
    // charge-tier tick runs on that same 1s period) rather than ramping
    // smoothly. If the beam isn't actively connected to the same target for
    // more than ~1s, the charge drops back to the base tier.
    if (this.cardData.unitType === 'VoidRay' || this.cardData.unitType === 'Mohandar') {
      const activeTarget = (this.target && !this.target.isDead && battlefield.isInRange(this, this.target)) ? this.target : null;
      if (activeTarget && this.voidRayTarget === activeTarget) {
        this.voidRayIdleTime = 0;
      } else {
        this.voidRayIdleTime += dt;
        if (this.voidRayIdleTime >= 1.0) {
          this.voidRayChargeLevel = 0;
          this.voidRayCharge = 1.0;
          this.voidRayTarget = null;
        }
      }
    }

    // Cloaking state: Smoke screen cloak protects unit and allows firing without decloaking ("continues fighting")
    if (this.isAntiShade) {
      let enemyThreatInRange = false;
      const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
      if (battlefield?.units) {
        for (let i = 0; i < battlefield.units.length; i++) {
          const u = battlefield.units[i];
          if (u.team !== enemyTeam || u.isDead || u.isParachuting || u.isCinematic || u.isGoldenStatue || u.isCocoon) continue;
          const dx = u.x - this.x;
          const dy = u.y - this.y;
          const distSq = dx * dx + dy * dy;
          const triggerDist = Math.max((u.range || 24) + 48, 150);
          if (distSq <= triggerDist * triggerDist) {
            enemyThreatInRange = true;
            break;
          }
        }
      }
      if (!enemyThreatInRange && battlefield?.bases?.[enemyTeam]) {
        const eb = battlefield.bases[enemyTeam];
        if (eb && !eb.isDead && eb.hp > 0) {
          const dx = eb.x - this.x;
          const dy = eb.y - this.y;
          const baseTriggerDist = Math.max((eb.range || 240) + 48, 220);
          if (dx * dx + dy * dy <= baseTriggerDist * baseTriggerDist) {
            enemyThreatInRange = true;
          }
        }
      }
      this.isCloaked = enemyThreatInRange;
      this.decloakTimer = 0;
    } else {
      this.isCloaked = (!!this.cardData.isCloaked || this.cloakedByAuraTimer > 0 || this.smokeCloakTimer > 0) && (this.smokeCloakTimer > 0 || this.decloakTimer <= 0);
    }

    // Hyper Shade: Explosive Crow (Single-use tactical hunter strike on first Common/Uncommon in range)
    if (this.hasExplosiveCrow) {
      const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
      const maxCrowRange = this.range;
      let crowTarget = null;
      let minTargetDistSq = maxCrowRange * maxCrowRange;

      // Zero-allocation in-place search over battlefield.units
      for (const e of battlefield.units) {
        if (e.team !== enemyTeam || e.isDead || e.isUntargetable || e.isGoldenStatue || e.isCocoon) continue;
        if (e.isStructure || e.cardData?.isBuilding || e.cardData?.type === 'Structure') continue;
        const rarity = e.cardData?.rarity;
        const tier = e.cardData?.tier || 1;
        const isCommonOrUncommon = rarity ? (rarity === 'Common' || rarity === 'Uncommon') : (tier <= 2);
        if (!isCommonOrUncommon) continue;
        if (e.isCloaked && !battlefield.isPositionDetected(this.team, e.x, e.y)) continue;

        const dx = e.x - this.x;
        const dy = e.y - this.y;
        const dSq = dx * dx + dy * dy;
        if (dSq <= minTargetDistSq) {
          minTargetDistSq = dSq;
          crowTarget = e;
        }
      }

      if (crowTarget) {
        this.hasExplosiveCrow = false;
        const startX = this.x;
        const startY = this.y - 6;
        const targetRef = crowTarget;

        if (audio?.playSuppressedShot) audio.playSuppressedShot({ x: this.x });
        else audio?.playGauss?.({ x: this.x });
        particles?.addExplosiveCrow?.(startX, startY, targetRef, () => {
          if (!targetRef.isDead) {
            targetRef.takeDamage(targetRef.hp + 99999, particles, 'Energy');
            particles?.addDamageText?.(targetRef.x, targetRef.y - 20, "INSTANT KILL", 'crit');
          }
          particles?.addExplosion?.(targetRef.x, targetRef.y, 45, '#ef4444', 28);
          particles?.addShockwave?.(targetRef.x, targetRef.y, 50, '#ff0033', 0.28);
          particles?.addScreenShake?.(3.5, 0.16);
          audio?.playExplosion?.('large', { x: targetRef.x });
        });
      }
    }

    // 6. Sul Parasite Vulture (Insane Mode): Wanders in deterministic Lissajous curves as a decoy
    if (this.isParasiteVulture && this.isInsane && !this.isDead) {
      this._updateInsaneFlight(dt, battlefield);
      return;
    }

    // 7. Combat & Target Acquisition
    if (this.isHyperPanzer) {
      const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
      const enemyBase = battlefield?.bases?.[enemyTeam];
      const isBaseInRange = enemyBase && !enemyBase.isDead && battlefield.isInRange(this, enemyBase);

      // 1. Acquire / validate ground target (Ground units, structures, enemy base)
      if (isBaseInRange) {
        // Reached firing distance to the enemy base: stop advancing and pound the base
        this.isStoppedAtBase = true;
        if (!this.groundTarget || this.groundTarget.isDead || this.groundTarget === enemyBase || !battlefield.isInRange(this, this.groundTarget)) {
          this.groundTarget = enemyBase;
        }
      } else {
        this.isStoppedAtBase = false;
        if (!this.groundTarget || this.groundTarget.isDead || this.groundTarget.isAir || this.groundTarget.isGoldenStatue ||
            this.groundTarget.isUntargetable || (this.groundTarget.executionTimer && this.groundTarget.executionTimer > 0) ||
            (this.groundTarget.isCloaked && !battlefield.isPositionDetected(this.team, this.groundTarget.x, this.groundTarget.y))) {
          this.groundTarget = battlefield.findBestTarget(this, 'Ground');
        }
      }

      // 2. Acquire / validate air target (Air units only, continuous tracking even while stopped at base)
      if (!this.airTarget || this.airTarget.isDead || !this.airTarget.isAir || this.airTarget.isGoldenStatue ||
          this.airTarget.isUntargetable || (this.airTarget.executionTimer && this.airTarget.executionTimer > 0) ||
          (this.airTarget.isCloaked && !battlefield.isPositionDetected(this.team, this.airTarget.x, this.airTarget.y))) {
        this.airTarget = battlefield.findBestTarget(this, 'Air');
      }

      // 3. Fire cannon at ground target if in range and ready
      const canFireCannon = (!this.disarmTimer || this.disarmTimer <= 0) && this.groundTarget && battlefield.isInRange(this, this.groundTarget);
      if (canFireCannon && this.cannonCooldown <= 0) {
        this.fireHyperPanzerCannon(this.groundTarget, battlefield, particles, audio, attackSpeedMultiplier);
        this.cannonCooldown = (this.attackSpeed || 2.5) / attackSpeedMultiplier;
      }

      // 4. Fire rockets at air target if in range and ready (even if spawned after stopping at base)
      const canFireRockets = (!this.disarmTimer || this.disarmTimer <= 0) && this.airTarget && battlefield.isInRange(this, this.airTarget);
      if (canFireRockets && this.rocketCooldown <= 0) {
        this.fireHyperPanzerRockets(this.airTarget, battlefield, particles, audio, attackSpeedMultiplier);
        this.rocketCooldown = (this.attackSpeed || 2.5) / attackSpeedMultiplier;
      }

      // Update 5-state dynamic turret aim state
      if (this.groundTarget && !this.groundTarget.isDead) {
        const forwardDir = (this.team === 'western' ? 1 : -1);
        const dx = (this.groundTarget.x - this.x) * forwardDir;
        const dy = this.groundTarget.y - this.y;
        const deg = Math.atan2(dy, dx) * (180 / Math.PI);
        if (dx < 0 && Math.abs(dy) < 18) {
          this.turretAimState = 'straight';
        } else if (deg <= -55) {
          this.turretAimState = 'up';
        } else if (deg <= -18) {
          this.turretAimState = 'left';
        } else if (deg >= 55) {
          this.turretAimState = 'down';
        } else if (deg >= 18) {
          this.turretAimState = 'right';
        } else {
          this.turretAimState = 'straight';
        }
      } else {
        this.turretAimState = 'straight';
      }

      // 5. Movement handling:
      // Stops once in firing range to the enemy base; otherwise continues rolling forward across the lane!
      this.isEngaged = !!(canFireCannon || canFireRockets || this.isStoppedAtBase);
      this.target = this.groundTarget || this.airTarget;
      this.facingDir = (this.team === 'western' ? 1 : -1);
      this.attackCooldown = Math.min(this.cannonCooldown, this.rocketCooldown);

      if (!this.isStoppedAtBase) {
        this.targetY = this.getObstacleAvoidanceTargetY(battlefield, this.laneYTarget);
        this.marchForward(dt, battlefield, particles);
      }
      return;
    }

    // 🕊️ Human A9 Pigeon: Heavy VTOL Fighter-Bomber
    // - One-time 100-DMG ground bomb (Unit first, else field Structure, never Base; only if no enemy Air unit is closer)
    // - Quick bomb approach at speed 4.5 (while firing Gatling), brief 0.25s VTOL hover & bomb drop
    // - Dynamically faces towards its enemy target (ground or air) + whole-aircraft pitch aim
    // - Active reverse-thrust kiting at speed 1 to maintain maximum range (13) against ground, stand-and-fight against air
    if (this.isA9Pigeon && !this.isDead) {
      const defaultDir = this.team === 'western' ? 1 : -1;
      let facingDir = (this.target && !this.target.isDead)
        ? (Math.sign(this.target.x - this.x) || defaultDir)
        : defaultDir;
      this.facingDir = facingDir;

      // Smooth 3-frame ease-in & critically damped recovery for mass-loss bomb recoil
      if ((this._pigeonRecoilTimer || 0) > 0) {
        this._pigeonRecoilTimer = Math.max(0, this._pigeonRecoilTimer - dt);
        const recoilProg = 1.0 - (this._pigeonRecoilTimer / 0.35); // 0 to 1 over 0.35s
        if (recoilProg < 0.16) {
          // 3-frame smooth ease-in kick (0.056s): 0 -> -2.5 -> -5.0 -> -6.0px (no 1-frame snap)
          this._pigeonBombRecoilY = -6.0 * Math.sin((recoilProg / 0.16) * Math.PI * 0.5);
        } else {
          // Smooth critically damped recovery back to hover level
          const recoveryProg = (recoilProg - 0.16) / (1.0 - 0.16);
          this._pigeonBombRecoilY = -6.0 * Math.pow(1.0 - recoveryProg, 2.0);
        }
      } else {
        this._pigeonBombRecoilY = 0;
      }

      // 1. Active Bomb Hover & Drop Phase (high-altitude VTOL hover directly above ground target)
      if (this.pigeonState === 'BOMB_HOVER_DROP') {
        this.speed = 0;
        this.isWalking = false;
        this.isHovering = true;
        this.isFlyingBackward = false;
        this.isEngaged = true;
        this._pigeonVelX = 0;

        // Smooth 3-frame settlement into locked hover coordinates and neutral level pitch
        if (this.bombTargetId) {
          const bCandidate = battlefield?.getUnitOrBaseById ? battlefield.getUnitOrBaseById(this.bombTargetId) : battlefield?.units?.find(u => u.id === this.bombTargetId);
          if (bCandidate && !bCandidate.isDead) {
            facingDir = Math.sign(bCandidate.x - this.x) || defaultDir;
            this.facingDir = facingDir;
            const hX = bCandidate.x - facingDir * 2;
            const hY = bCandidate.y - 36;
            this.x += (hX - this.x) * Math.min(1, dt * 16);
            this.y += (hY - this.y) * Math.min(1, dt * 16);
          }
        }
        this.nozzleTilt += (1.0 - (this.nozzleTilt || 0)) * Math.min(1, dt * 14);
        this.pigeonPitch += (0.0 - (this.pigeonPitch || 0)) * Math.min(1, dt * 12);

        const bTarget = this.bombTargetId ? (battlefield?.getUnitOrBaseById ? battlefield.getUnitOrBaseById(this.bombTargetId) : battlefield?.units?.find(u => u.id === this.bombTargetId)) : null;
        if (bTarget && !bTarget.isDead && (!this.disarmTimer || this.disarmTimer <= 0) && this.attackCooldown <= 0 && this.damage > 0) {
          this.performAttack(bTarget, battlefield, particles, audio, attackSpeedMultiplier);
        }

        this.bombHoverTimer = Math.max(0, (this.bombHoverTimer || 0) - dt);
        // Crisp, readable hover targeting pause:
        // Hovers steadily for ~0.20s in locked high VTOL position over target to aim, then releases bomb at bombHoverTimer <= 0.32!
        if (this.hasBomb && this.bombHoverTimer <= 0.32) {
          this.hasBomb = false;
          this._pigeonRecoilTimer = 0.35; // Triggers smooth 3-frame ease-in recoil!
          const dropX = (bTarget && !bTarget.isDead) ? bTarget.x : this.x;
          const dropY = (bTarget && !bTarget.isDead) ? bTarget.y : (this.y + 36);
          const bombDmg = Number(this.cardData?.bombDamage) || 100;
          const bombSplash = Number(this.cardData?.bombSplashRadius) || 65;

          // Start coordinates precisely match the unlatched belly bomb sprite in the preceding frame
          const bombStartX = Math.round(this.x + facingDir * 1.0);
          const bombStartY = Math.round(this.y - 11);
          const bombDestX = Math.round(dropX);
          const bombDestY = Math.round(dropY + 4);

          if (audio?.playA9PigeonBombDrop) {
            audio.playA9PigeonBombDrop({ x: this.x });
          } else {
            audio?.playRocketLaunch?.('heavy', { x: this.x, unitType: 'A9Pigeon' });
          }

          battlefield?.addFrameEvent?.({
            type: 'PIGEON_BOMB_DROP',
            unitId: this.id,
            team: this.team,
            race: this.race || 'Human',
            startX: bombStartX,
            startY: bombStartY,
            targetX: bombDestX,
            targetY: bombDestY,
            duration: 0.38,
            targetUnitId: bTarget?.id ?? null
          });

          particles?.addA9PigeonBombDrop?.(bombStartX, bombStartY, bombDestX, bombDestY, this.team, 0.38, audio);

          const isGold = this.edition === 'gold';
          const sourceRef = this;
          if (battlefield?.addProjectileSim) {
            battlefield.addProjectileSim({
              flightTicks: 23, // ~0.38s at 60Hz
              targetX: bombDestX,
              targetY: bombDestY,
              targetUnitId: bTarget?.id ?? null,
              damage: bombDmg,
              splashRadius: bombSplash,
              team: this.team,
              projectileType: 'Grenade',
              sourceUnit: this,
              isGoldAttacker: isGold,
              onImpact: (effParticles, effAudio) => {
                const liveTarget = bTarget && !bTarget.isDead && !bTarget.isAir ? bTarget : null;
                const impX = liveTarget ? liveTarget.x : bombDestX;
                const impY = liveTarget ? liveTarget.y : bombDestY;
                if (liveTarget) {
                  liveTarget.takeDamage(bombDmg, effParticles, 'Grenade', true, isGold, sourceRef);
                }
                battlefield.dealSplashDamage(impX, impY, bombSplash, bombDmg, sourceRef.team, false, true, effParticles, 'Grenade', liveTarget, false, sourceRef);
                effParticles?.addExplosion?.(impX, impY, bombSplash, '#f97316', 30);
                effParticles?.addShockwave?.(impX, impY, bombSplash + 8, '#fbbf24', 0.35);
                effParticles?.addScorchMark?.(impX, impY, 18);
                effParticles?.addArtillerySpall?.(impX, impY);
                effParticles?.addScreenShake?.(4.5, 0.22);
                if (effAudio?.playA9PigeonBombExplosion) {
                  effAudio.playA9PigeonBombExplosion({ x: impX });
                } else {
                  effAudio?.playExplosion?.('heavy', { x: impX });
                }
              }
            });
          } else {
            if (bTarget && !bTarget.isDead && !bTarget.isAir) {
              bTarget.takeDamage(bombDmg, particles, 'Grenade', true, isGold, this);
            }
            battlefield?.dealSplashDamage?.(bombDestX, bombDestY, bombSplash, bombDmg, this.team, false, true, particles, 'Grenade', bTarget, false, this);
          }
        }

        if (this.bombHoverTimer <= 0) {
          this.pigeonState = this.isRooted ? 'HOVER_RANGE' : 'KITE_BACK';
          this.bombTargetId = null;
          this.isHovering = !!this.isRooted;
          this.isFlyingBackward = !this.isRooted;
          this._pigeonVelX = 0;
        }
        return;
      }

      // 2. Check for Bombing Run eligibility if still carrying the ground bomb
      // Cannot initiate bombing run if immobilized (e.g. tethered by Harvesting Pillar tentacles)
      if (this.hasBomb && !this.isRooted) {
        const bombCandidate = this._findPigeonBombTarget(battlefield);
        if (bombCandidate) {
          if (this.isRooted) {
            // Immobilized: cannot dash forward towards bomb target
            this.pigeonState = 'HOVER_RANGE';
            this.bombTargetId = null;
            this._pigeonApproachSpeed = 0;
            this.speed = 0;
            this.isHovering = true;
            this.isWalking = false;
            this.nozzleTilt = Math.min(1.0, (this.nozzleTilt || 0) + dt * 8.5);
            return;
          }
          this.pigeonState = 'BOMB_APPROACH';
          this.bombTargetId = bombCandidate.id;
          this.target = bombCandidate;
          facingDir = Math.sign(bombCandidate.x - this.x) || defaultDir;
          this.facingDir = facingDir;
          this.isFlyingBackward = false;
          this.isHovering = false;
          this.isWalking = true;
          this.isEngaged = true;

          // Target hover station: directly above the victim at high altitude
          // FacingDir offset -2 aligns fuselage center and belly bomb rack right above the target
          const targetHoverX = bombCandidate.x - facingDir * 2;
          const targetHoverY = bombCandidate.y - 36;
          const dx = targetHoverX - this.x;
          const dy = targetHoverY - this.y;
          const dist = Math.hypot(dx, dy);
          const distForward = (targetHoverX - this.x) * facingDir;

          // Decisive & Agile Ingress: Fast approach dash (Speed 4.5) with a crisp 42px flare & braking zone
          const maxDashSpeed = 4.5 * SPEED_MULTIPLIER;
          const brakeZoneDist = 42;

          if (distForward > brakeZoneDist) {
            // Rapid ingress dash
            this._pigeonApproachSpeed = maxDashSpeed;
            this.speed = maxDashSpeed;
            // Engine vector nozzle horizontal (0° aft thrust)
            this.nozzleTilt = Math.max(0.0, (this.nozzleTilt || 0) - dt * 10.0);
            // Shallow dive aim toward target
            const dxAim = Math.max(16, Math.abs(bombCandidate.x - this.x));
            const dyAim = (bombCandidate.y - (this.y - 18));
            const desiredPitch = Math.max(-0.25, Math.min(0.35, Math.atan2(dyAim, dxAim)));
            this.pigeonPitch += (desiredPitch - (this.pigeonPitch || 0)) * Math.min(1, dt * 12);
          } else {
            // Crisp Aerodynamic Flare & Vector Tilt (over the last 42px)
            const brakeRatio = Math.max(0, distForward / brakeZoneDist);
            const brakeProgress = 1.0 - brakeRatio;

            // Decelerate smoothly without stalling or getting stuck
            this._pigeonApproachSpeed = maxDashSpeed * (0.35 + 0.65 * brakeRatio);
            this.speed = this._pigeonApproachSpeed;

            // Vector engine nozzle tilts smoothly from 0 to 90°
            const targetNozzle = Math.min(1.0, Math.pow(brakeProgress, 1.15));
            this.nozzleTilt += (targetNozzle - (this.nozzleTilt || 0)) * Math.min(1, dt * 12.0);

            // Aerodynamic flare: nose pitches up into airflow to shed speed
            const flarePitch = -0.16 * Math.sin(brakeProgress * Math.PI);
            this.pigeonPitch += (flarePitch - (this.pigeonPitch || 0)) * Math.min(1, dt * 12.0);
          }

          // Gatling Gun is allowed to fire during bomb approach
          if ((!this.disarmTimer || this.disarmTimer <= 0) && this.attackCooldown <= 0 && this.damage > 0 && battlefield.isInRange(this, bombCandidate)) {
            this.performAttack(bombCandidate, battlefield, particles, audio, attackSpeedMultiplier);
          }

          // Robust arrival trigger: once over the target position (distForward <= 6 or passed), lock into hover!
          if (distForward <= 6) {
            this.pigeonState = 'BOMB_HOVER_DROP';
            this.bombHoverTimer = 0.52; // 0.20s targeting hover + bomb drop + 0.32s post-drop hold
            this.speed = 0;
            this._pigeonApproachSpeed = 0;
            this.isHovering = true;
            return;
          }

          // Fly along vector towards hover station
          const currentSpeed = this.speed;
          const step = Math.min(dist, currentSpeed * dt);
          if (dist > 0.001) {
            this.x += (dx / dist) * step;
            this.y += (dy / dist) * step;
          }
          this.emitMovementEffects(particles, dt);
          return;
        } else if (this.pigeonState === 'BOMB_APPROACH') {
          // Ground target died before drop or an enemy air unit got closer: abort & keep bomb!
          this.pigeonState = 'ADVANCE';
          this.bombTargetId = null;
          this._pigeonApproachSpeed = 0;
        }
      }

      // 3. Standard Combat & Reverse-Thrust Kiting (Move & Shoot simultaneously)
      // Generell blickt die Pigeon in Richtung ihres Gegners:
      const targetInvalid = !this.target || this.target.isDead || this.target.hp <= 0 || this.target.state === 'DEAD' || this.target.isUntargetable || this.target.isGoldenStatue ||
        (this.target.executionTimer && this.target.executionTimer > 0) ||
        (this.target.isCloaked && !battlefield.isPositionDetected(this.team, this.target.x, this.target.y));

      if (targetInvalid) {
        this.target = battlefield.findBestTarget(this);
      } else if (!battlefield.isInRange(this, this.target)) {
        if ((battlefield.tickCount + this._targetOffset) % 6 === 0) {
          this.target = battlefield.findBestTarget(this);
        }
      }

      // Baseline fallback to enemy base if at enemy baseline
      if (!this.target || !battlefield.isInRange(this, this.target)) {
        const isAtEnemyBaseline = (this.team === 'western')
          ? (this.x >= (battlefield?.bases?.eastern?.x ? battlefield.bases.eastern.x - 39 : 1515))
          : (this.x <= (battlefield?.bases?.western?.x ? battlefield.bases.western.x + 39 : 85));
        if (isAtEnemyBaseline) {
          const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
          const enemyBase = battlefield?.bases?.[enemyTeam];
          if (enemyBase && !enemyBase.isDead) {
            this.target = enemyBase;
          }
        }
      }

      // Generell blickt die Pigeon in Richtung Gegner:
      if (this.target && !this.target.isDead) {
        facingDir = Math.sign(this.target.x - this.x) || defaultDir;
        this.facingDir = facingDir;
      } else {
        facingDir = defaultDir;
        this.facingDir = facingDir;
      }

      const inRange = !!(this.target && battlefield.isInRange(this, this.target));
      this.isEngaged = inRange;

      if (inRange) {
        // Whole-aircraft pitch aim so the nose Gatling points straight at the target
        const dxAim = Math.max(16, Math.abs(this.target.x - this.x));
        const dyAim = (this.target.y - (this.target.isAir ? 18 : 0)) - (this.y - 18);
        const desiredPitch = Math.max(-0.36, Math.min(0.42, Math.atan2(dyAim, dxAim)));
        this.pigeonPitch += (desiredPitch - (this.pigeonPitch || 0)) * Math.min(1, dt * 12);

        // Fire Gatling Gun while moving / hovering
        if ((!this.disarmTimer || this.disarmTimer <= 0) && this.attackCooldown <= 0 && this.damage > 0) {
          this.performAttack(this.target, battlefield, particles, audio, attackSpeedMultiplier);
        }

        // Distance management: fly backward at Speed 1 to maintain longest distance (Range 13)
        const dist = Math.hypot(this.target.x - this.x, this.target.y - this.y);
        const maxReach = this.range + this.radius + (this.target.radius || 16);
        const minX = battlefield?.bases?.western?.x ? battlefield.bases.western.x + 39 : 85;
        const maxX = battlefield?.bases?.eastern?.x ? battlefield.bases.eastern.x - 39 : ((battlefield?.width || 1600) - 85);
        const atHomeWall = (this.team === 'western') ? (this.x <= minX + 4) : (this.x >= maxX - 4);
        const isAirTarget = !!(this.target && (this.target.isAir || this.target.elementType === 'Air'));

        if (this.isRooted) {
          // 𓉐 Immobilized by tether / tentacle (Harvesting Pillar): held stationary in VTOL hover! Cannot kite backward or advance.
          this.pigeonState = 'HOVER_RANGE';
          this.isFlyingBackward = false;
          this.isHovering = true;
          this.isWalking = false;
          this.speed = 0;
          this._pigeonVelX = 0;
          this.nozzleTilt = Math.min(1.0, (this.nozzleTilt || 0) + dt * 8.5);
        } else if (isAirTarget) {
          // ⚔️ Dogfight Stand-and-Fight against enemy air units (like Rocketship):
          // Hold position at weapon range (13) to eliminate start-stop stutter kaskade!
          this.pigeonState = 'DOGFIGHT';
          this.isFlyingBackward = false;
          this.isHovering = true;
          this.isWalking = false;
          this.speed = 0;
          this._pigeonVelX = ((this._pigeonVelX || 0) * Math.max(0, 1 - dt * 12));
          // Vector nozzle at 45° combat hovering angle (0.50)
          this.nozzleTilt += (0.50 - (this.nozzleTilt || 0)) * Math.min(1, dt * 8.5);
        } else if (dist < maxReach - 16 && !atHomeWall) {
          // Fly backward at reduced speed (Speed = 1) while still facing forward against ground targets!
          this.pigeonState = 'KITE_BACK';
          this.isFlyingBackward = true;
          this.isHovering = false;
          this.isWalking = true;
          this.nozzleTilt = Math.min(1.0, (this.nozzleTilt || 0) + dt * 8.5);
          const revSpeed = (this.cardData?.reverseSpeed || 1) * SPEED_MULTIPLIER;
          this.speed = revSpeed;
          const targetVx = -facingDir * revSpeed;
          this._pigeonVelX += (targetVx - (this._pigeonVelX || 0)) * Math.min(1, dt * 10);
          this.x = Math.max(minX, Math.min(maxX, this.x + this._pigeonVelX * dt));

          // Gentle lateral Y alignment while backing up
          const dyLane = (this.laneYTarget || this.y) - this.y;
          if (Math.abs(dyLane) > 2) {
            this.y += Math.sign(dyLane) * Math.min(Math.abs(dyLane), revSpeed * 0.45 * dt);
          }
          this.emitMovementEffects(particles, dt);
        } else {
          // Hover at max firing distance or home baseline boundary against ground targets
          this.pigeonState = 'HOVER_RANGE';
          this.isFlyingBackward = false;
          this.isHovering = true;
          this.isWalking = false;
          this.speed = 0;
          this.nozzleTilt = Math.min(1.0, (this.nozzleTilt || 0) + dt * 8.5);
          this._pigeonVelX = ((this._pigeonVelX || 0) * Math.max(0, 1 - dt * 12));
        }
      } else {
        // Out of range:
        if (this.isRooted) {
          // Immobilized: cannot advance forward!
          this.pigeonState = 'HOVER_RANGE';
          this.isFlyingBackward = false;
          this.isHovering = true;
          this.isWalking = false;
          this.speed = 0;
          this._pigeonVelX = 0;
          this.nozzleTilt = Math.min(1.0, (this.nozzleTilt || 0) + dt * 8.5);
        } else {
          // Jet engine pivots aft (0°) and aircraft flies forward at Speed 3
          this.pigeonState = 'ADVANCE';
          this.isFlyingBackward = false;
          this.isHovering = false;
          this.nozzleTilt = Math.max(0.0, (this.nozzleTilt || 0) - dt * 8.5);
          const aeroPitch = 0.05;
          this.pigeonPitch += (aeroPitch - (this.pigeonPitch || 0)) * Math.min(1, dt * 8);

          const fwdSpeed = (this.cardData?.speed || 3) * SPEED_MULTIPLIER;
          this.speed = fwdSpeed;
          this.baseSpeed = fwdSpeed;
          this._pigeonVelX += (facingDir * fwdSpeed - (this._pigeonVelX || 0)) * Math.min(1, dt * 10);

          const ownBase = battlefield?.bases?.[this.team];
          const isDefendingBase = this.target && !this.target.isStructure && ownBase && ((this.target.x - ownBase.x)*(this.target.x - ownBase.x)+(this.target.y - ownBase.y)*(this.target.y - ownBase.y) <= 420 * 420);
          if (isDefendingBase) {
            this.targetY = this.target.y;
            this.interceptTarget(this.target, dt, battlefield, particles);
          } else {
            this.targetY = this.getObstacleAvoidanceTargetY(battlefield, this.laneYTarget);
            this.marchForward(dt, battlefield, particles);
          }
          this.emitMovementEffects(particles, dt);
        }
      }
      return;
    }

    if (this.damage > 0 || this.isKamikaze || (this.isParasiteVulture && this.hasParasite)) {
      if (this.target && (
        (this.target.isCloaked && !battlefield.isPositionDetected(this.team, this.target.x, this.target.y)) ||
        this.target.isUntargetable ||
        this.target.isGoldenStatue ||
        (this.target.executionTimer && this.target.executionTimer > 0)
      )) {
        this.target = null;
      }

      // Hyper Tesla Walker: 360 Ambush Radar (overrides distant targets if enemy infantry is within 64px)
      const isHyperTeslaWalker = (this.cardData?.unitType === 'HyperTeslaWalker' || this.name === 'Hyper Tesla Walker');
      if (isHyperTeslaWalker && this.executionTimer <= 0 && this.executionCooldown <= 0) {
        let closestRearThreat = null;
        let minThreatDistSq = 64 * 64;
        const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
        if (battlefield?.units) {
          for (let i = 0; i < battlefield.units.length; i++) {
            const eu = battlefield.units[i];
            if (eu.team !== enemyTeam || eu.isDead || eu.isUntargetable || (eu.executionTimer && eu.executionTimer > 0)) continue;
            if (eu.isCloaked && !battlefield.isPositionDetected(this.team, eu.x, eu.y)) continue;
            if (eu.isStructure || eu.isAir) continue;
            const isInf = isInfantryUnit(eu);
            if (!isInf) continue;

            const dSq = (eu.x - this.x) * (eu.x - this.x) + (eu.y - this.y) * (eu.y - this.y);
            if (dSq <= minThreatDistSq) {
              minThreatDistSq = dSq;
              closestRearThreat = eu;
            }
          }
        }
        if (closestRearThreat) {
          this.target = closestRearThreat;
          this.facingDir = Math.sign(closestRearThreat.x - this.x) || (this.team === 'western' ? 1 : -1);
        }
      }

      if (this.hasDualTether && this.dualTetherTarget1Id) {
        const tethered = battlefield?.getUnitOrBaseById?.(this.dualTetherTarget1Id);
        if (!tethered || tethered.isDead || tethered.isGoldenStatue) {
          if (tethered) tethered.isRooted = false;
          this.dualTetherTarget1Id = null;
          this.dualTetherTarget2Id = null;
          this.tether1LockTimer = 0;
          this.target = battlefield.findBestTarget(this);
        } else {
          this.target = tethered;
          this.facingDir = Math.sign(tethered.x - this.x) || (this.team === 'western' ? 1 : -1);
          tethered.isRooted = true;

          // Phase 2 countdown: after 5s with first tentacle locked, second tentacle shoots out
          this.tether1LockTimer += dt;
          if (this.tether1LockTimer >= this.dualTetherPhase2Delay && !this.dualTetherTarget2Id) {
            this.dualTetherTarget2Id = tethered.id;
            particles?.addDamageText?.(tethered.x, tethered.y - 28, 'HARVESTING', 'crit');
            particles?.addHitSparks?.(tethered.x, tethered.y, 'plasma', 8);
          }
        }
      } else if (this.hasTether && this.tetherTargetId) {
        const tethered = battlefield?.getUnitOrBaseById?.(this.tetherTargetId);
        if (!tethered || tethered.isDead || tethered.isGoldenStatue) {
          this.tetherTargetId = null;
          this.target = battlefield.findBestTarget(this);
        } else {
          this.target = tethered;
          this.facingDir = Math.sign(tethered.x - this.x) || (this.team === 'western' ? 1 : -1);
        }
      } else if (this.hasBeamTether && this.beamTargetId) {
        const beamed = battlefield?.getUnitOrBaseById?.(this.beamTargetId);
        if (!beamed || beamed.isDead || beamed.isGoldenStatue) {
          this.beamTargetId = null;
          this.target = battlefield.findBestTarget(this);
        } else {
          this.target = beamed;
          this.facingDir = Math.sign(beamed.x - this.x) || (this.team === 'western' ? 1 : -1);
        }
      } else if (this.isParasiteVulture && this.hasParasite) {
        // Sul Parasite Vulture with active parasite strictly chases eligible overtake targets.
        // It overtakes enemy units, vultures, and field structures; only planetary fortress bases remain immune.
        if (this.target && (this.target.isDead || this.target.isCinematic || this.target.isGoldenStatue
          || this.target.isParasitized || this.target._targetedByParasite
          || this.target.isBase || this.target.name?.includes('Fortress') || this.target.name?.includes('Base'))) {
          this.target = null;
        }
      } else {
        let targetLost = !this.target || this.target.isDead || this.target.isUntargetable || this.target.isGoldenStatue
          || (this.target.executionTimer && this.target.executionTimer > 0)
          || (this.targetsAir === false && (this.target.isAir || this.target.elementType === 'Air'))
          || (!this.targetsGround && !this.target.isAir && this.target.elementType !== 'Air')
          || (this.target.isCloaked && !battlefield.isPositionDetected(this.team, this.target.x, this.target.y) && !(this.isAntiShade && this.target.isAntiShade));
        if (!targetLost && this.isAntiShade && this.target) {
          const dir = this.team === 'western' ? 1 : -1;
          const frontlineX = battlefield?.territoryDivider !== undefined ? battlefield.territoryDivider : ((battlefield?.width || 1600) / 2);
          const maxCrossingDist = 260;
          const targetOvershoot = (this.target.x - frontlineX) * dir;
          const myOvershoot = (this.x - frontlineX) * dir;
          const isShadowDuel = this.isAntiShade && this.target.isAntiShade;
          if (!isShadowDuel && (targetOvershoot > maxCrossingDist || myOvershoot > maxCrossingDist)) {
            targetLost = true;
          }
        }
        if (targetLost) {
          this.target = battlefield.findBestTarget(this);
        } else if (!battlefield.isInRange(this, this.target)) {
          // Out of range: march toward target; periodically check for closer emerging threats (deterministic tick stride)
          if ((battlefield.tickCount + this._targetOffset) % 6 === 0) {
            this.target = battlefield.findBestTarget(this);
          }
        }
      }

      // Grundlinien-Fallback: Einheiten, die an der feindlichen Grundlinie anstehen (maxX / minX)
      // und deren aktuelles Ziel nicht in Reichweite ist, schalten auf die gegnerische Basis um.
      if (!(this.isParasiteVulture && this.hasParasite) && !this.cannotAttackBuildings && (!this.target || !battlefield.isInRange(this, this.target))) {
        const isAtEnemyBaseline = (this.team === 'western')
          ? (this.x >= (battlefield?.bases?.eastern?.x ? battlefield.bases.eastern.x - 39 : 1515))
          : (this.x <= (battlefield?.bases?.western?.x ? battlefield.bases.western.x + 39 : 85));
        if (isAtEnemyBaseline) {
          const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
          const enemyBase = battlefield?.bases?.[enemyTeam];
          if (enemyBase && !enemyBase.isDead) {
            this.target = enemyBase;
            this.facingDir = Math.sign(enemyBase.x - this.x) || (this.team === 'western' ? 1 : -1);
          }
        }
      }

      if (isHyperTeslaWalker && this.target && !this.target.isDead) {
        this.facingDir = Math.sign(this.target.x - this.x) || (this.team === 'western' ? 1 : -1);
      }

      // Hyper Tesla Walker: Pincer Stalk Check (close distance in 1:1 against infantry)
      this.isPincerStalking = false;
      if (isHyperTeslaWalker && this.target && !this.target.isDead && this.executionTimer <= 0 && this.executionCooldown <= 0) {
        const isInf = !this.target.isStructure && !this.target.isAir && isInfantryUnit(this.target);
        if (isInf) {
          const dTargetSq = (this.target.x - this.x) * (this.target.x - this.x) + (this.target.y - this.y) * (this.target.y - this.y);
          if (dTargetSq <= 120 * 120 && dTargetSq > 60 * 60) {
            // Check for heavy enemy threats (vehicles/structures) within 180px
            let heavyThreat = false;
            if (battlefield?.units) {
              for (let i = 0; i < battlefield.units.length; i++) {
                const eu = battlefield.units[i];
                if (eu.team !== this.team && !eu.isDead && !eu.isAir) {
                  if (Math.abs(eu.x - this.x) > 180) continue;
                  const isHeavy = eu.isStructure || (eu.cardData?.elementType === 'Ground' && !isInfantryUnit(eu));
                  if (isHeavy) {
                    const hdSq = (eu.x - this.x) * (eu.x - this.x) + (eu.y - this.y) * (eu.y - this.y);
                    if (hdSq <= 180 * 180) {
                      heavyThreat = true;
                      break;
                    }
                  }
                }
              }
            }
            if (!heavyThreat) {
              this.isPincerStalking = true;
            }
          }
        }
      }

      let inRange = this.target && ((this.tetherTargetId || this.dualTetherTarget1Id) ? true : battlefield.isInRange(this, this.target));
      if (this.isPincerStalking) {
        const dTargetSq = this.target ? (this.target.x - this.x) * (this.target.x - this.x) + (this.target.y - this.y) * (this.target.y - this.y) : 999 * 999;
        if (dTargetSq > 60 * 60) {
          inRange = false;
        }
      }

      if (this.burstDuration > 0) {
        if (inRange) {
          this.outOfCombatTimer = 0;
        } else {
          this.outOfCombatTimer += dt;
          if (this.outOfCombatTimer >= 2.0 && this.burstTimer > 0 && !this.isReloading) {
            this.burstTimer = 0; // Tactical reload out of combat
          }
        }
        if (this.isReloading && this.attackCooldown <= 0) {
          this.isReloading = false;
        }
      }

      if (this.isReloading && this.attackCooldown <= 0) {
        this.isReloading = false;
      }

      const isOrchestra = this.name === 'Orchestra' || this.cardData?.unitType === 'Orchestra';
      if (isOrchestra) {
        if (this.deployProgress === undefined) this.deployProgress = 0.0;
        const deployRate = 1.0 / 1.2; // 1.2s to fully elevate or fold (~0.833 / sec)
        if (inRange) {
          // 1. Erst stehen bleiben & Orgel ausrichten
          if (this.deployProgress < 1.0) {
            this.deployProgress = Math.min(1.0, this.deployProgress + dt * deployRate);
          }
        } else {
          // 2. Kein Ziel mehr in Reichweite: erst wieder einfahren bevor weitergefahren wird
          if (this.deployProgress > 0.0) {
            this.deployProgress = Math.max(0.0, this.deployProgress - dt * deployRate);
          }
        }
      }

      const isBastionLock = this.isBastionLock || this.name === 'Bastion Lock' || this.cardData?.unitType === 'BastionLock';
      if (isBastionLock) {
        if (this.deployProgress === undefined) this.deployProgress = 0.0;
        const prevProg = this.deployProgress;
        const deployRate = 1.0; // 1.0s to fully deploy or redeploy (1.0 / sec)
        if (inRange) {
          if (this.deployProgress < 1.0) {
            this.deployProgress = Math.min(1.0, this.deployProgress + dt * deployRate);
            if (prevProg < 1.0 && this.deployProgress >= 1.0) {
              // "Wums!" Heavy organic shield slam into ground: dust, spall & heavy impact sound
              const fwd = this.facingDir || (this.team === 'western' ? 1 : -1);
              particles?.addHitSparks?.(this.x + fwd * 14, this.y + 4, 'shield', 6);
              if (battlefield?.quality !== 'low') {
                particles?.addVaporPuff?.(this.x + fwd * 14, this.y + 2, 10, 0.35);
              }
              if (audio?.playBastionDeploy) {
                audio.playBastionDeploy({ x: this.x });
              }
            }
          }
        } else {
          if (this.deployProgress > 0.0) {
            this.deployProgress = Math.max(0.0, this.deployProgress - dt * deployRate);
          }
        }
        // Snap deflection rating: 0.2 while moving/deploying, 0.5 when fully deployed
        this.deflectionRating = (this.deployProgress >= 1.0) ? 0.5 : 0.2;
      }

      this.isEngaged = !!(inRange || (isOrchestra && (this.deployProgress || 0) > 0) || (isBastionLock && (this.deployProgress || 0) > 0) || (this.isProcessingPillar && (this.plasmaChargeTimer || 0) > 0));
      if (inRange) {
        if (this.isAntiShade) {
          this.isWalking = false;
          this.isSentinelIdle = false;
        }
        // Within weapon range: stop and engage! (0-damage casters/healers
        // hold position here too, so Psi Storm/Fungal can trigger off
        // this.target, but skip the actual attack visuals/damage.)
        // Orchestra & Bastion Lock: must be fully deployed (deployProgress >= 1.0) before firing!
        // Disarmed / Blinded units cannot fire weapons while disarmTimer > 0.
        const canFire = (!isOrchestra || (this.deployProgress >= 1.0)) && (!isBastionLock || (this.deployProgress >= 1.0)) && (!this.disarmTimer || this.disarmTimer <= 0) && (!this.isGrowing);
        if (canFire && this.attackCooldown <= 0 && this.damage > 0) {
          this.performAttack(this.target, battlefield, particles, audio, attackSpeedMultiplier);
        }
      } else {
        // Orchestra & Bastion Lock: cannot move until shield/launcher is fully folded!
        const canMove = !this.isStructure && (!isOrchestra || this.deployProgress <= 0.0) && (!isBastionLock || this.deployProgress <= 0.0) && (!this.isProcessingPillar || (this.plasmaChargeTimer || 0) <= 0);
        if (canMove) {
          if (this.isPincerStalking && this.target) {
            this.targetY = this.target.y;
            this.interceptTarget(this.target, dt, battlefield, particles);
          } else if (this.isKamikaze && this.target && !this.target.isStructure) {
            // Kamikaze units home straight in on their target instead of
            // marching a lane corridor with the usual slow Y-correction -
            // at Wheelie Bomber's speed/range combo (260px/s, ~50px engage
            // radius), that slow correction let it race past off-lane
            // targets before ever coming into range. See
            // docs/WHEELIE_BOMBER_PLAN.md Nachtrag.
            this.targetY = this.target.y;
            this.interceptTarget(this.target, dt, battlefield, particles);
          } else if (this.isParasiteVulture && this.hasParasite && this.target && !this.target.isBase) {
            this.targetY = this.target.y;
            this.interceptTarget(this.target, dt, battlefield, particles);
          } else if (this.isHotHand && this.target && !this.target.isStructure) {
            this.facingDir = Math.sign(this.target.x - this.x) || (this.team === 'western' ? 1 : -1);
            this.targetY = this.target.y;
            this.interceptTarget(this.target, dt, battlefield, particles);
          } else if (this.isAntiShade && this.target && !this.target.isStructure) {
            const dir = this.team === 'western' ? 1 : -1;
            const frontlineX = battlefield?.territoryDivider !== undefined ? battlefield.territoryDivider : ((battlefield?.width || 1600) / 2);
            const maxCrossingDist = 260;
            const myOvershoot = (this.x - frontlineX) * dir;
            const targetOvershoot = (this.target.x - frontlineX) * dir;
            const isShadowDuel = this.isAntiShade && this.target.isAntiShade;

            if (!isShadowDuel && (myOvershoot > maxCrossingDist || targetOvershoot > maxCrossingDist)) {
              // Beyond frontline leash limit towards enemy base: disengage & return towards indicator!
              this.target = null;
              const distToFront = frontlineX - this.x;
              if (Math.abs(distToFront) > 8) {
                this.isSentinelIdle = false;
                this.facingDir = distToFront > 0 ? 1 : -1;
                const moveStep = Math.sign(distToFront) * Math.min(Math.abs(distToFront), (this.speed || 0) * dt);
                this.x += moveStep;
                this.isWalking = true;
                this.emitMovementEffects(particles, dt);
              }
            } else {
              this.facingDir = Math.sign(this.target.x - this.x) || dir;
              this.isSentinelIdle = false;
              this.isWalking = true;
              this.targetY = this.target.y;
              this.interceptTarget(this.target, dt, battlefield, particles);
              if (!isShadowDuel) {
                const maxForwardX = frontlineX + dir * maxCrossingDist;
                if (dir > 0 && this.x > maxForwardX) this.x = maxForwardX;
                else if (dir < 0 && this.x < maxForwardX) this.x = maxForwardX;
              }
            }
          } else {
            const ownBase = battlefield?.bases?.[this.team];
            const isDefendingBase = this.target && !this.target.isStructure && ownBase && ((this.target.x - ownBase.x)*(this.target.x - ownBase.x)+(this.target.y - ownBase.y)*(this.target.y - ownBase.y) <= 420 * 420);

            if (isDefendingBase) {
              // DEFENSE INTERCEPTION: Direct movement to intercept & eliminate threat attacking home base!
              this.targetY = this.target.y;
              this.interceptTarget(this.target, dt, battlefield, particles);
            } else if (this.target && this.target.isStructure) {
              // Approaching enemy base or structure at the end of the lane
              const forwardDist = Math.abs(this.target.x - this.x);
              if (forwardDist <= 240) {
                const verticalDist = Math.abs(this.y - this.target.y);
                if (verticalDist > (this.target.radius || 48) * 0.9) {
                  this.targetY = this.target.y + Math.sign(this.y - this.target.y) * ((this.target.radius || 48) * 0.9 + this.combatOffset * 0.3);
                } else {
                  this.targetY = this.y;
                }
              } else {
                this.targetY = this.laneYTarget;
              }
              this.targetY = this.getObstacleAvoidanceTargetY(battlefield, this.targetY);
              this.marchForward(dt, battlefield, particles);
            } else if (this.isAntiShade && !this.target) {
              // Frontline Sentinel Navigation: tracks territoryDivider & patrols vertically along frontline
              const frontlineX = battlefield?.territoryDivider !== undefined ? battlefield.territoryDivider : ((battlefield?.width || 1600) / 2);
              const distToFront = frontlineX - this.x;
              if (Math.abs(distToFront) > 8) {
                this.isSentinelIdle = false;
                this.facingDir = distToFront > 0 ? 1 : -1;
                const moveStep = Math.sign(distToFront) * Math.min(Math.abs(distToFront), (this.speed || 0) * dt);
                this.x += moveStep;
                this.isWalking = true;
                this.emitMovementEffects(particles, dt);
                if (Number.isFinite(this.laneYTarget) && Math.abs(this.y - this.laneYTarget) > 2) {
                  const lateralRate = Math.max(2.8, (this.speed || 0) / 30);
                  this.y += (this.laneYTarget - this.y) * lateralRate * dt;
                }
              } else {
                this.x = frontlineX;
                this.isSentinelIdle = true;
                this.isWalking = true;
                this.facingDir = this.team === 'western' ? 1 : -1;
                if (!this._patrolDir) {
                  const charCode = this.id.charCodeAt(this.id.length - 1) || 0;
                  this._patrolDir = (charCode % 2 === 0) ? 1 : -1;
                }
                const laneMinY = (battlefield?.laneY || 176) + 28;
                const laneMaxY = (battlefield?.laneY || 176) + (battlefield?.laneHeight || 320) - 28;
                this.y += this._patrolDir * (this.speed * 0.55) * dt;
                if (this.y <= laneMinY) {
                  this.y = laneMinY;
                  this._patrolDir = 1;
                } else if (this.y >= laneMaxY) {
                  this.y = laneMaxY;
                  this._patrolDir = -1;
                }
                this.emitMovementEffects(particles, dt);
              }
            } else if (this.isAntiShade) {
              // Anti Shade never marches towards a base; navigation is strictly anchored to frontline indicator
              const frontlineX = battlefield?.territoryDivider !== undefined ? battlefield.territoryDivider : ((battlefield?.width || 1600) / 2);
              const distToFront = frontlineX - this.x;
              if (Math.abs(distToFront) > 8) {
                this.isSentinelIdle = false;
                this.facingDir = distToFront > 0 ? 1 : -1;
                const moveStep = Math.sign(distToFront) * Math.min(Math.abs(distToFront), (this.speed || 0) * dt);
                this.x += moveStep;
                this.isWalking = true;
                this.emitMovementEffects(particles, dt);
              } else {
                this.x = frontlineX;
                this.isSentinelIdle = true;
                this.isWalking = true;
                this.facingDir = this.team === 'western' ? 1 : -1;
              }
            } else if (this.isParasiteVulture && this.hasParasite) {
              // Parasite Vulture without an enemy target holds safe position near frontline
              // instead of marching into enemy base cannon fire (0 attack damage)
              const frontlineX = battlefield?.territoryDivider !== undefined ? battlefield.territoryDivider : ((battlefield?.width || 1600) / 2);
              const dir = this.team === 'western' ? 1 : -1;
              const safeHoldX = frontlineX - dir * 80;
              const distToHold = safeHoldX - this.x;
              if (Math.abs(distToHold) > 12) {
                this.facingDir = distToHold > 0 ? 1 : -1;
                const moveStep = Math.sign(distToHold) * Math.min(Math.abs(distToHold), (this.speed || 0) * dt);
                this.x += moveStep;
                this.isWalking = true;
                this.emitMovementEffects(particles, dt);
              } else {
                this.facingDir = dir;
              }
            } else {
              // Tactical engagement proximity check
              const tacticalEngageRange = Math.max(110, this.range + 45);
              const dstToTargetSq = this.target ? (this.target.x - this.x)*(this.target.x - this.x)+(this.target.y - this.y)*(this.target.y - this.y) : 99999 * 99999;

              if (this.target && dstToTargetSq <= tacticalEngageRange * tacticalEngageRange) {
                // Close-range tactical combat with enemy units
                this.targetY = this.laneYTarget * 0.65 + this.target.y * 0.35 + this.combatOffset * 0.4;
              } else {
                // March straight along assigned corridor
                this.targetY = this.laneYTarget;
              }
              this.targetY = this.getObstacleAvoidanceTargetY(battlefield, this.targetY);
              this.marchForward(dt, battlefield, particles);
            }
          }
        }
      }
    } else {
      this.isEngaged = false;
      if (!this.isStructure) {
        if (this.isAntiShade) {
          // Frontline Sentinel Navigation: tracks territoryDivider & patrols vertically
          const frontlineX = battlefield?.territoryDivider !== undefined ? battlefield.territoryDivider : ((battlefield?.width || 1600) / 2);
          const distToFront = frontlineX - this.x;
          if (Math.abs(distToFront) > 8) {
            this.isSentinelIdle = false;
            this.facingDir = distToFront > 0 ? 1 : -1;
            const moveStep = Math.sign(distToFront) * Math.min(Math.abs(distToFront), (this.speed || 0) * dt);
            this.x += moveStep;
            this.isWalking = true;
            this.emitMovementEffects(particles, dt);
            if (Number.isFinite(this.laneYTarget) && Math.abs(this.y - this.laneYTarget) > 2) {
              const lateralRate = Math.max(2.8, (this.speed || 0) / 30);
              this.y += (this.laneYTarget - this.y) * lateralRate * dt;
            }
          } else {
            this.x = frontlineX;
            this.isSentinelIdle = true;
            this.isWalking = true;
            this.facingDir = this.team === 'western' ? 1 : -1;
            if (!this._patrolDir) {
              const charCode = this.id.charCodeAt(this.id.length - 1) || 0;
              this._patrolDir = (charCode % 2 === 0) ? 1 : -1;
            }
            const laneMinY = (battlefield?.laneY || 176) + 28;
            const laneMaxY = (battlefield?.laneY || 176) + (battlefield?.laneHeight || 320) - 28;
            this.y += this._patrolDir * (this.speed * 0.55) * dt;
            if (this.y <= laneMinY) {
              this.y = laneMinY;
              this._patrolDir = 1;
            } else if (this.y >= laneMaxY) {
              this.y = laneMaxY;
              this._patrolDir = -1;
            }
            this.emitMovementEffects(particles, dt);
          }
        } else {
          this.targetY = this.getObstacleAvoidanceTargetY(battlefield, this.laneYTarget);
          this.marchForward(dt, battlefield, particles);
        }
      }
    }
  }

  // Predictive Obstacle Avoidance: scans for blocking structures ahead in the march corridor
  // and steers targetY around the building perimeter. Adheres to Invariants #1 (100% Lockstep
  // Determinism, zero Math.random()) and #7 (Zero-Allocation, no heap objects in frame loop).
  getObstacleAvoidanceTargetY(battlefield, defaultTargetY) {
    if (this.isAir || this.isStructure || !battlefield?.units) {
      return defaultTargetY;
    }
    // Stationary, deploying, or dead units don't need obstacle avoidance
    if (this.speed <= 0 || (this.deployProgress && this.deployProgress > 0) || this.isDead) {
      return defaultTargetY;
    }

    if (battlefield.tickCount !== undefined && this._avoidanceTick === battlefield.tickCount && this._avoidanceInputY === defaultTargetY) {
      return this._cachedAvoidanceTargetY;
    }

    const dir = this.team === 'western' ? 1 : -1;
    let closestObstacle = null;
    let minAheadDist = Infinity;
    // Lookahead distance scales with speed, ensuring fast units begin curving in time
    const lookaheadDist = this.radius + Math.max(55, this.speed * 0.65);

    // 1. Scan for structures and golden statues blocking our corridor (immovable priority)
    const structures = battlefield.structures;
    if (structures && structures.length > 0) {
      for (let i = 0; i < structures.length; i++) {
        const b = structures[i];
        if (!b.isStructure || b.isDead || b === this || b === this.target) continue;

        const aheadDist = (b.x - this.x) * dir;
        // Fast cull: structure must be ahead within lookaheadDist
        if (aheadDist < -this.radius || aheadDist >= lookaheadDist) continue;

        const clearanceRadius = this.radius + b.radius + 8;
        const lateralDist = Math.abs(b.y - this.y);
        if (lateralDist < clearanceRadius) {
          if (aheadDist < minAheadDist) {
            minAheadDist = aheadDist;
            closestObstacle = b;
          }
        }
      }
    }

    const goldenStatues = battlefield.goldenStatues;
    if (goldenStatues && goldenStatues.length > 0) {
      for (let i = 0; i < goldenStatues.length; i++) {
        const b = goldenStatues[i];
        if (!b.isGoldenStatue || b.isDead || b === this || b.isFallingStatue) continue;

        const aheadDist = (b.x - this.x) * dir;
        // Hysteresis: keep steering until safely past (not just -this.radius)
        if (aheadDist < -(this.radius + b.radius + 8) || aheadDist >= lookaheadDist) continue;

        const clearanceRadius = this.radius + b.radius + 8;
        const lateralDist = Math.abs(b.y - this.y);
        if (lateralDist < clearanceRadius) {
          if (aheadDist < minAheadDist) {
            minAheadDist = aheadDist;
            closestObstacle = b;
          }
        }
      }
    }

    const cocoons = battlefield.cocoons;
    if (cocoons && cocoons.length > 0) {
      for (let i = 0; i < cocoons.length; i++) {
        const b = cocoons[i];
        if (!b.isCocoon || b.isDead || b === this || b.isFallingCocoon) continue;

        const aheadDist = (b.x - this.x) * dir;
        if (aheadDist < -(this.radius + b.radius + 8) || aheadDist >= lookaheadDist) continue;

        const clearanceRadius = this.radius + b.radius + 8;
        const lateralDist = Math.abs(b.y - this.y);
        if (lateralDist < clearanceRadius) {
          if (aheadDist < minAheadDist) {
            minAheadDist = aheadDist;
            closestObstacle = b;
          }
        }
      }
    }

    // 2. Scan for friendly units blocking our corridor (Overtaking & Combat Fan-Out).
    // Also opportunistically fills _avoidCongestionScratch with every ally in
    // this same coarse x-window (team match, not air/structure/dead, not
    // self) - a strict superset of what the "Multi-unit congestion check"
    // further below needs (that check's own x-window is [-radius,
    // +allyLookaheadDist), narrower than this scan's symmetric
    // ±allyLookaheadDist cull, and radius <= allyLookaheadDist always).
    // Collected BEFORE the target-exclusion below so it stays a faithful
    // superset (unlike closestObstacle-candidacy, the congestion check does
    // not exclude this.target). This lets that check reuse this list
    // instead of re-scanning all of battlefield.units a second time.
    let hasAllyScanCandidates = false;
    if (!closestObstacle && battlefield.units && !this.isEngaged) {
      const allyLookaheadDist = this.radius + Math.max(45, this.speed * 0.55);
      const units = battlefield.units;
      const len = units.length;
      _avoidCongestionScratch.length = 0;
      for (let i = 0; i < len; i++) {
        const b = units[i];
        if (Math.abs(b.x - this.x) > allyLookaheadDist) continue;

        if (b.team === this.team && !b.isAir && !b.isStructure && !b.isDead && b !== this && !b.isParachuting) {
          _avoidCongestionScratch.push(b);
        }

        if (b.isGoldenStatue && !b.isDead && b !== this && !b.isFallingStatue) {
          const aheadDist = (b.x - this.x) * dir;
          if (aheadDist >= -(this.radius + b.radius + 8) && aheadDist < lookaheadDist) {
            const clearanceRadius = this.radius + b.radius + 8;
            const lateralDist = Math.abs(b.y - this.y);
            if (lateralDist < clearanceRadius && aheadDist < minAheadDist) {
              minAheadDist = aheadDist;
              closestObstacle = b;
            }
          }
          continue;
        }

        if (b.team !== this.team || b.isAir || b.isStructure || b.isDead || b === this || b === this.target || b.isParachuting) continue;

        const aheadDist = (b.x - this.x) * dir;
        // Friendly units only block us if they are ahead of us along the march axis
        if (aheadDist < 0 || aheadDist >= allyLookaheadDist) continue;

        const clearanceRadius = this.radius + b.radius + 8;
        const lateralDist = Math.abs(b.y - this.y);
        if (lateralDist < clearanceRadius) {
          if (aheadDist < minAheadDist) {
            minAheadDist = aheadDist;
            closestObstacle = b;
          }
        }
      }
      hasAllyScanCandidates = true;
    }

    let finalY = defaultTargetY;
    if (closestObstacle) {
      const b = closestObstacle;
      const clearanceRadius = this.radius + b.radius + 8;
      const margin = this.radius + 12;
      const laneMinY = battlefield.laneY + margin;
      const laneMaxY = battlefield.laneY + battlefield.laneHeight - margin;

      // Decide whether to steer above (smaller Y) or below (larger Y) with direction persistence
      let steerUp;
      if (this._avoidObstacleId === b.id && this._avoidSteerUp !== undefined) {
        steerUp = this._avoidSteerUp;
      } else {
        steerUp = this.y < b.y;
        if (Math.abs(this.y - b.y) < 2) {
          // Near dead center: choose the side with more clearance to lane boundaries
          const roomAbove = b.y - laneMinY;
          const roomBelow = laneMaxY - b.y;
          if (roomAbove !== roomBelow) {
            steerUp = roomAbove > roomBelow;
          } else {
            // Deterministic tiebreaker using unit id (Invariants #1 & #9: no Math.random()!)
            const charCode = this.id.charCodeAt(this.id.length - 1) || 0;
            steerUp = (charCode % 2) === 0;
          }
        }
        this._avoidObstacleId = b.id;
        this._avoidSteerUp = steerUp;
      }

      // Candidate bypass Y
      let bypassY = steerUp ? (b.y - clearanceRadius) : (b.y + clearanceRadius);

      // If candidate would breach lane bounds, steer the other way
      if (steerUp && bypassY < laneMinY) {
        bypassY = b.y + clearanceRadius;
        this._avoidSteerUp = false;
      } else if (!steerUp && bypassY > laneMaxY) {
        bypassY = b.y - clearanceRadius;
        this._avoidSteerUp = true;
      }

      // Multi-unit congestion check: if another friendly unit already occupies bypassY, check alternate side.
      // When the ally-obstacle scan above ran this tick, _avoidCongestionScratch
      // already holds every candidate this check could possibly want (see the
      // collection comment above) - reusing it here is exactly equivalent to
      // a fresh battlefield.units scan, just without doing that scan twice.
      // Falls back to the original full scan when that branch didn't run
      // (obstacle came from a structure, or this unit is engaged).
      let isCongested = false;
      const checkRange = this.radius + 14;
      const forwardBound = this.radius + Math.max(45, this.speed * 0.55);
      if (hasAllyScanCandidates) {
        for (let ci = 0; ci < _avoidCongestionScratch.length; ci++) {
          const c = _avoidCongestionScratch[ci];
          if (c === b) continue;
          const cAhead = (c.x - this.x) * dir;
          if (cAhead >= -this.radius && cAhead < forwardBound) {
            if (Math.abs(c.y - bypassY) < checkRange) {
              isCongested = true;
              break;
            }
          }
        }
      } else if (battlefield.units) {
        const units = battlefield.units;
        const len = units.length;
        for (let ci = 0; ci < len; ci++) {
          const c = units[ci];
          if (c === this || c === b || c.isDead || c.isAir || c.isStructure || c.team !== this.team || c.isParachuting) continue;
          const cAhead = (c.x - this.x) * dir;
          if (cAhead >= -this.radius && cAhead < forwardBound) {
            if (Math.abs(c.y - bypassY) < checkRange) {
              isCongested = true;
              break;
            }
          }
        }
      }
      if (isCongested) {
        const altBypassY = (bypassY <= b.y) ? (b.y + clearanceRadius) : (b.y - clearanceRadius);
        const clampedAlt = Math.max(laneMinY, Math.min(laneMaxY, altBypassY));
        if (Math.abs(clampedAlt - b.y) >= this.radius + 6) {
          // Verify alternate side is not also congested before flipping
          let isAltCongested = false;
          if (hasAllyScanCandidates) {
            for (let ci = 0; ci < _avoidCongestionScratch.length; ci++) {
              const c = _avoidCongestionScratch[ci];
              if (c === b) continue;
              const cAhead = (c.x - this.x) * dir;
              if (cAhead >= -this.radius && cAhead < forwardBound) {
                if (Math.abs(c.y - clampedAlt) < checkRange) {
                  isAltCongested = true;
                  break;
                }
              }
            }
          } else if (battlefield.units) {
            const units = battlefield.units;
            const len = units.length;
            for (let ci = 0; ci < len; ci++) {
              const c = units[ci];
              if (c === this || c === b || c.isDead || c.isAir || c.isStructure || c.team !== this.team || c.isParachuting) continue;
              const cAhead = (c.x - this.x) * dir;
              if (cAhead >= -this.radius && cAhead < forwardBound) {
                if (Math.abs(c.y - clampedAlt) < checkRange) {
                  isAltCongested = true;
                  break;
                }
              }
            }
          }
          if (!isAltCongested) {
            bypassY = clampedAlt;
            this._avoidSteerUp = (bypassY < b.y);
          }
        }
      }

      // Strictly clamp within playable lane height
      finalY = Math.max(laneMinY, Math.min(laneMaxY, bypassY));
    } else {
      this._avoidObstacleId = null;
      this._avoidSteerUp = undefined;
    }

    if (battlefield.tickCount !== undefined) {
      this._avoidanceTick = battlefield.tickCount;
      this._avoidanceInputY = defaultTargetY;
      this._cachedAvoidanceTargetY = finalY;
    }
    return finalY;
  }

  interceptTarget(target, dt, battlefield, particles) {
    if (this.isRooted) return;
    if (!target || !Number.isFinite(target.x) || !Number.isFinite(target.y)) return;
    let targetX = target.x;
    let targetY = target.y;

    if (!this.isAir && battlefield?.units) {
      const obstacleTargetY = this.getObstacleAvoidanceTargetY(battlefield, target.y);
      if (obstacleTargetY !== target.y && Number.isFinite(obstacleTargetY)) {
        targetY = obstacleTargetY;
      }
    }

    let dx = targetX - this.x;
    let dy = targetY - this.y;

    if (this.isKamikaze) {
      const dir = this.team === 'western' ? 1 : -1;
      // High-speed vehicle physics: maintain forward momentum in team march direction (no reverse)
      if (dx * dir < 0) {
        dx = dir * 2;
      }
      // Natural steering angle limit / lateral tire slip (~53 degrees max angle)
      const forwardDist = Math.max(1, dx * dir);
      const maxDy = forwardDist * 1.35;
      if (Math.abs(dy) > maxDy) {
        dy = Math.sign(dy) * maxDy;
      }
    } else if (this.isParasiteVulture) {
      this.facingDir = Math.sign(dx) || (this.team === 'western' ? 1 : -1);
    }

    const dist = Math.sqrt((dx)*(dx)+(dy)*(dy));

    if (dist > 2 && Number.isFinite(dist)) {
      const moveDist = (this.speed || 0) * dt;
      this.x += (dx / dist) * moveDist;
      this.y += (dy / dist) * moveDist;
    }

    const interceptProgress = Math.abs(this.x - (this._prevProgressX !== undefined ? this._prevProgressX : this.x));
    if (interceptProgress < 0.15) {
      this._stuckTicks = (this._stuckTicks || 0) + 1;
    } else {
      this._stuckTicks = 0;
    }
    this._prevProgressX = this.x;

    // Emit movement effects (exhaust smoke, thruster plumes, ground dust)
    this.emitMovementEffects(particles, dt);
  }

  marchForward(dt, battlefield, particles) {
    if (this.isRooted) return;
    const dir = this.team === 'western' ? 1 : -1;
    this.x += dir * (this.speed || 0) * dt;

    // Hard boundary: Never allow units to march past the enemy base
    if (battlefield?.bases) {
      if (this.team === 'western') {
        const maxX = battlefield.bases.eastern?.x !== undefined ? battlefield.bases.eastern.x - 38 : this.x;
        if (this.x > maxX) this.x = maxX;
      } else {
        const minX = battlefield.bases.western?.x !== undefined ? battlefield.bases.western.x + 38 : this.x;
        if (this.x < minX) this.x = minX;
      }
    }

    if (Number.isFinite(this.targetY) && Math.abs(this.y - this.targetY) > 2) {
      const lateralRate = Math.max(2.8, (this.speed || 0) / 30);
      this.y += (this.targetY - this.y) * lateralRate * dt;
    }

    const marchProgress = Math.abs(this.x - (this._prevProgressX !== undefined ? this._prevProgressX : this.x));
    if (marchProgress < 0.15) {
      this._stuckTicks = (this._stuckTicks || 0) + 1;
    } else {
      this._stuckTicks = 0;
    }
    this._prevProgressX = this.x;

    // Emit movement effects (exhaust smoke, thruster plumes, ground dust)
    this.emitMovementEffects(particles, dt);
  }

  emitMovementEffects(particles, dt) {
    if (!particles || this.isStructure || (particles.canSpawnCosmetic && !particles.canSpawnCosmetic())) return;
    const dir = this.team === 'western' ? 1 : -1;

    // 0. Gold Edition Midas Trail (subtle, elegant golden sparkle motes)
    if (this.edition === 'gold') {
      this._midasTrailTimer = (this._midasTrailTimer || 0) + dt;
      if (this._midasTrailTimer >= 0.09) {
        this._midasTrailTimer = 0;
        const groundY = this.y + (this.isAir ? 24 : 6);
        particles?.addMidasTrail?.(this.x - dir * (this.radius * 0.5), groundY, dir, this.isAir);
      }
    }

    // Anti Shade Shadow Footprint / Ember Trail
    if (this.isAntiShade) {
      this._footstepTimer = (this._footstepTimer || 0) + dt;
      if (this._footstepTimer >= 0.35) {
        this._footstepTimer = 0;
        particles?.addShadowFootprint?.(this.x, this.y + 6, dir);
      }
    }

    const name = this.cardData?.unitType || this.name || '';
    const isMech = this.attributes?.includes('Mechanical');
    const isMassive = this.attributes?.includes('Massive');

    // Sul Crawling Swarm (Loaf caste & Pulp) Slime Glaze Ground Trail
    if (this.race === 'Sul' && !this.isAir && !this.isStructure && !this.isAntiShade) {
      const isSlimeCrawler = (this.cardData?.caste === 'Loaf' || this.caste === 'Loaf' || name === 'Pulp' || this.cardData?.unitType === 'Pulp');
      if (isSlimeCrawler) {
        this._slimeTrailTimer = (this._slimeTrailTimer || 0) + dt;
        if (this._slimeTrailTimer >= 0.38) {
          this._slimeTrailTimer = 0;
          particles?.addSulSlimeGlaze?.(this.x - dir * (this.radius * 0.4), this.y + (this.radius * 0.45));
        }
      }
    }

    if (particles && (particles.canSpawnCosmetic ? particles.canSpawnCosmetic() : true)) {
      const pushP = (p) => particles.pushParticle ? particles.pushParticle(p) : (particles.particles ? particles.particles.push(p) : null);

      // 1. Human Heavy Tanks & Ground Mech (Diesel smoke puffs, tread ground dust & kickup)
      if (MECH_HEAVY_NAMES.has(name) || (isMech && !this.isAir && this.race === 'Human')) {
        if (Math.random() < 0.25) {
          // Engine exhaust smoke
          pushP({
            x: this.x - dir * (this.radius * 0.7),
            y: this.y - 6 + (Math.random() * 4 - 2),
            vx: -dir * (12 + Math.random() * 15),
            vy: -6 - Math.random() * 10,
            radius: 2 + Math.random() * 2.5,
            color: DIESEL_EXHAUST_COLORS[Math.floor(Math.random() * DIESEL_EXHAUST_COLORS.length)],
            life: 0.35 + Math.random() * 0.2,
            maxLife: 0.55,
            alpha: 0.65
          });
        }
        if (Math.random() < 0.3) {
          particles?.addGroundDustKickup?.(this.x - dir * (this.radius * 0.6), this.y + this.radius * 0.45, dir, this.radius * 0.35);
        }
      }

      // 2. Human Air & Aerospace Fighters (Jet thruster exhaust sparks, blue heat plumes & ground rotorwash)
      else if (AIR_THRUSTER_NAMES.has(name) || (this.isAir && this.race === 'Human')) {
        if (Math.random() < 0.35) {
          pushP({
            x: this.x - dir * (this.radius * 0.8),
            y: this.y + (Math.random() * 6 - 3),
            vx: -dir * (22 + Math.random() * 20),
            vy: (Math.random() * 6 - 3),
            radius: 2 + Math.random() * 2,
            color: JET_PLUME_COLORS[Math.floor(Math.random() * JET_PLUME_COLORS.length)],
            life: 0.2 + Math.random() * 0.12,
            maxLife: 0.32,
            alpha: 0.85
          });
        }
        // Thruster heat shimmer behind aircraft
        if (Math.random() < 0.2) {
          particles?.addHeatShimmer?.(this.x - dir * (this.radius * 0.9), this.y - 12, 14, 10, 0.4);
        }
        // Ground downwash dust beneath low-flying aircraft
        if (this.isAir && Math.random() < 0.25) {
          particles?.addGroundDustKickup?.(this.x + (Math.random() * 12 - 6), this.y + 24, 0, 7);
        }
      }

      // 5. Wheelie Bomber (High-octane wasteland dirt bike: aggressive rear-wheel
      // dust rooster-tail, throttle backfire sparks during wheelies, landing puffs)
      else if (name === 'WheelieBomber') {
        const numId = (typeof this.id === 'string' ? parseInt(this.id.replace(/\D/g, ''), 10) : Number(this.id)) || 0;
        const isWheelie = (this.age < 0.80) || (((this.age + numId * 0.73) % 2.8) < 0.85);
        const dustProb = isWheelie ? 0.75 : 0.40;
        if (Math.random() < dustProb) {
          particles?.addGroundDustKickup?.(this.x - dir * (this.radius * 0.75), this.y + this.radius * 0.48, dir, this.radius * (isWheelie ? 0.7 : 0.45));
        }
        const sparkProb = isWheelie ? 0.45 : 0.15;
        if (Math.random() < sparkProb) {
          pushP({
            x: this.x - dir * (this.radius * 0.6) + (Math.random() * 6 - 3),
            y: this.y + (Math.random() * 6 - 3),
            vx: -dir * (22 + Math.random() * 26) + (Math.random() * 10 - 5),
            vy: -12 - Math.random() * 16,
            radius: 1.2 + Math.random() * 1.4,
            color: WHEELIE_SPARK_PALETTE[Math.floor(Math.random() * WHEELIE_SPARK_PALETTE.length)],
            life: 0.16 + Math.random() * 0.12,
            maxLife: 0.28,
            alpha: 0.95
          });
        }
      } else {
        // Light infantry footstep dust (rare subtle puff)
        if (!this.isAir && Math.random() < 0.06) {
          pushP({
            x: this.x - dir * 4 + (Math.random() * 4 - 2),
            y: this.y + 6,
            vx: -dir * 8 + (Math.random() * 4 - 2),
            vy: -Math.random() * 5,
            radius: 1.5,
            color: '#64748b',
            life: 0.18,
            maxLife: 0.18,
            alpha: 0.4
          });
        }
      }
    }
  }

  getCustomBulletColorHex() {
    if (this._customBulletHex !== undefined) return this._customBulletHex;
    if (this.customBulletHex) {
      this._customBulletHex = this.customBulletHex;
      return this._customBulletHex;
    }
    const playerTeam = this.battlefield?.playerTeam || 'western';
    if (this.team !== playerTeam) {
      this._customBulletHex = null;
      return null;
    }
    const unitName = this.cardData?.name;
    if (!unitName) {
      this._customBulletHex = null;
      return null;
    }
    const styleId = inventory.getEquippedBulletColor(unitName);
    if (!styleId) {
      this._customBulletHex = null;
      return null;
    }
    const style = REEL_3_STYLES_MAP.get(styleId);
    this._customBulletHex = style?.hex || null;
    return this._customBulletHex;
  }

  getCustomAuraColorHex() {
    if (this._customAuraHex !== undefined) return this._customAuraHex;
    if (this.customAuraHex) {
      this._customAuraHex = this.customAuraHex;
      return this._customAuraHex;
    }
    const playerTeam = this.battlefield?.playerTeam || 'western';
    if (this.team !== playerTeam) {
      this._customAuraHex = null;
      return null;
    }
    const unitName = this.cardData?.name;
    if (!unitName) {
      this._customAuraHex = null;
      return null;
    }
    const styleId = inventory.getEquippedUnitAura(unitName);
    if (!styleId) {
      this._customAuraHex = null;
      return null;
    }
    const style = REEL_3_STYLES_MAP.get(styleId);
    this._customAuraHex = style?.hex || null;
    return this._customAuraHex;
  }

  getCustomRocketTrailHex() {
    if (this._customRocketTrailHex !== undefined) return this._customRocketTrailHex;
    const playerTeam = this.battlefield?.playerTeam || 'western';
    if (this.team !== playerTeam) {
      this._customRocketTrailHex = null;
      return null;
    }
    // Strict mapping: Only units that fire rockets or have a rocket trail
    const isRocketUnit = (this.projectileType === 'Rocket') || !!this.dualMissile || (this.cardData?.unitType === 'Rocketship') || (this.cardData?.unitType === 'Orchestra') || (this.name === 'Rocketship') || (this.name === 'Orchestra');
    if (!isRocketUnit) {
      this._customRocketTrailHex = null;
      return null;
    }
    const unitName = this.cardData?.name;
    if (!unitName) {
      this._customRocketTrailHex = null;
      return null;
    }
    const styleId = inventory.getEquippedRocketTrail(unitName);
    if (!styleId) {
      this._customRocketTrailHex = null;
      return null;
    }
    const style = REEL_3_STYLES_MAP.get(styleId);
    this._customRocketTrailHex = style?.hex || null;
    return this._customRocketTrailHex;
  }

  performAttack(target, battlefield, particles, audio, attackSpeedMult = 1.0) {
    const effAttackSpeedMult = attackSpeedMult || 1.0;
    this.attackCooldown = (this.attackSpeed / effAttackSpeedMult);

    const uType = this.unitType || this.cardData?.unitType;
    if (uType === 'Marine' || uType === 'HyperMarine' || uType === 'Panzer' || uType === 'HyperPanzer' || uType === 'DeployableCannon' || uType === 'Deployable_Cannon') {
      this.smokeBarrelTimer = 1.0;
    }

    // Sul Enforcer Vulture: 8-pod independent firing & target distribution
    if (this.isEnforcerVulture && this.vulturePods) {
      const readyPods = [];
      for (let podIdx = 0; podIdx < this.vulturePods.length; podIdx++) {
        const pod = this.vulturePods[podIdx];
        if (pod.reloadTimer > 0) continue;
        let queued = false;
        for (let qIdx = 0; qIdx < this.vultureLaunchQueue.length; qIdx++) {
          if (this.vultureLaunchQueue[qIdx].podIndex === pod.index) { queued = true; break; }
        }
        if (!queued) readyPods.push(pod);
      }
      if (readyPods.length > 0) {
        const candidateTargets = [];
        if (battlefield?.units) {
          for (let i = 0; i < battlefield.units.length; i++) {
            const u = battlefield.units[i];
            if (u.team !== this.team && !u.isDead && !u.isUntargetable && !u.isParachuting && !u.isCinematic && battlefield.isInRange(this, u)) {
              if (!u.isCloaked || battlefield.isPositionDetected(this.team, u.x, u.y)) {
                candidateTargets.push(u);
              }
            }
          }
        }
        if (candidateTargets.length === 0 && target) {
          candidateTargets.push(target);
        }

        if (candidateTargets.length > 0) {
          for (let i = 0; i < readyPods.length; i++) {
            const pod = readyPods[i];
            const chosenTarget = candidateTargets[i % candidateTargets.length];
            this.vultureLaunchQueue.push({
              podIndex: pod.index,
              target: chosenTarget,
              isIntercept: false
            });
          }
        }
      }
      this.attackCooldown = 0.25;
      return;
    }

    // Sul Processing Pillar: 3s Plasma Bubble Channeling & Morphing
    if (this.isProcessingPillar) {
      if (this.plasmaChargeTimer <= 0) {
        this.plasmaChargeTimer = 0.001;
        this.plasmaChargeTarget = target;
        this.plasmaTargetIsAir = !!target.isAir;
        this.plasmaTargetLastX = target.x;
        this.plasmaTargetLastY = target.y;
        this.plasmaMorphed = false;
        this.attackCooldown = (this.attackSpeed / attackSpeedMult);
      }
      return;
    }

    // Hyper Tesla Walker: Melee Infantry Execution Check
    if (this.cardData?.unitType === 'HyperTeslaWalker' || this.name === 'Hyper Tesla Walker') {
      const isInfantry = target && !target.isDead && !target.isStructure && !target.isAir && isInfantryUnit(target);
      const distToTargetSq = target ? (target.x - this.x) * (target.x - this.x) + (target.y - this.y) * (target.y - this.y) : 999999;
      if (isInfantry && distToTargetSq <= 60 * 60 && (this.executionCooldown <= 0 || !this.executionCooldown)) {
        // Melee Execution: 1.45s dramatic grab-hoist-cleave
        this.executionTimer = 1.45;
        this.executionDuration = 1.45;
        this.executionTargetId = target.id;
        this.executionCooldown = 8.0; // 8.0s internal cooldown
        this.isPincerStalking = false;
        this.isUntargetable = true;
        this.facingDir = Math.sign(target.x - this.x) || (this.team === 'western' ? 1 : -1);
        target.isHeld = true;
        target.groundY = target.y;
        target.heldByUnitId = this.id;
        audio?.playGauss ? audio.playGauss({ x: this.x }) : null;
        return;
      }
    }

    if (this.burstDuration > 0) {
      this.burstTimer += (this.attackSpeed / attackSpeedMult);
      if (this.burstTimer >= this.burstDuration) {
        this.burstTimer = 0;
        this.attackCooldown = (this.reloadDuration || 1.0) / attackSpeedMult;
        this.isReloading = true;
        if (uType === 'A9Piranha') {
          this.smokeBarrelTimer = 1.0;
        }
        particles?.addDamageText?.(this.x, this.y - this.radius - 12, 'RELOAD', 'status');
      }
    }

    if (this.cardData.isCloaked && !this.isAntiShade) {
      this.decloakTimer = 1.5; // Temporarily visible for 1.5 seconds after attacking
    }

    // Accuracy % / Deflection Rating % / Projectile Type / Element Type:
    // cards migrated to this schema (hasNewCombatSchema, set in the
    // constructor from cardData.projectileType) resolve their attack here
    // instead of through the legacy per-unitType visual switch below -
    // that switch is only reachable by cards NOT yet migrated (dormant
    // while disabled) and is otherwise unchanged.
    if (this.hasNewCombatSchema) {
      this.resolveNewCombatAttack(target, battlefield, particles, audio);
      if (this.isKamikaze) {
        audio?.stopWheelieEngine?.(this.driveAudioSoundId);
        this.driveAudioSoundId = null;
        battlefield?.handleUnitDeath?.(this, particles);
        particles?.addUnitDeathShatter?.(this);
        particles?.addExplosion?.(this.x, this.y, 35, '#88ff00', 25);
        audio?.playExplosion?.('small', { x: this.x });
        this.isDead = true;
        battlefield?.stats?.markUnitDeath(this.id);
      }
      return;
    }

    let baseDmg = this.damage;

    // Emperor's Will Morale Aura: friendly damage boost / enemy demoralize
    baseDmg *= this.buffDamageMult;
    if (this.damage > 0 && this.battlefield?.moduleSystem?.hasCongressBuff(this.team, 'advanced_ammunition')) {
      baseDmg += 2;
    }

    // Charge Impact Bonus
    if (this.isCharging) {
      baseDmg += 8; // Charge impact bonus
      this.isCharging = false;
    }

    // Void Ray Prismatic Beam Charge Mechanic: steps up one tier per
    // completed shot on the same target (1.0x -> 2.0x -> 3.0x), matching the
    // authentic mod's charge-chain tick running on the same 1s period as the
    // weapon itself. update() resets it if the beam drops the target for >1s.
    if (this.cardData.unitType === 'VoidRay' || this.cardData.unitType === 'Mohandar') {
      const VOID_RAY_CHARGE_TIERS = [1.0, 2.0, 3.0];
      if (this.voidRayTarget === target) {
        this.voidRayChargeLevel = Math.min(VOID_RAY_CHARGE_TIERS.length - 1, this.voidRayChargeLevel + 1);
      } else {
        this.voidRayTarget = target;
        this.voidRayChargeLevel = 0;
      }
      this.voidRayCharge = VOID_RAY_CHARGE_TIERS[this.voidRayChargeLevel];
      this.voidRayIdleTime = 0;
      baseDmg *= this.voidRayCharge;
    }

    const applyDamage = () => {
      for (let c = 0; c < this.attackCount; c++) {
        if (this.splashLine) {
          // Linear sweeping/piercing splash (Hellion, Lurker, Colossus)
          const startX = this.x;
          const startY = this.y;
          const dirX = target.x - startX;
          const dirY = target.y - startY;
          const dist = Math.sqrt((dirX)*(dirX)+(dirY)*(dirY)) || 1;
          const reach = Math.max(dist + 60, this.range + 40);
          const endX = startX + (dirX / dist) * reach;
          const endY = startY + (dirY / dist) * reach;
          battlefield.dealLineSplashDamage(startX, startY, endX, endY, 28, baseDmg, this.team, this.targetsAir, this.targetsGround, particles, this);
          if (this.cardData.unitType === 'Colossus') {
            particles?.addBeam?.(startX, startY - 15, endX, endY, '#ffaa00', 3.5, 0.18);
          } else if (this.cardData.unitType === 'Hellion') {
            particles?.addFire?.(startX, startY, endX, endY);
          } else if (this.cardData.unitType === 'Lurker') {
            particles?.addShockwave?.(target.x, target.y, 35, '#a855f7', 0.2);
          }
        } else if (this.splashRadius > 0) {
          battlefield.dealSplashDamage(target.x, target.y, this.splashRadius, baseDmg, this.team, this.targetsAir, this.targetsGround, particles, 'Bullet', target, false, this);
          particles?.addExplosion?.(target.x, target.y, 24, '#ff8800', 12);
          particles?.addScorchMark?.(target.x, target.y, 14);
          particles?.addArtillerySpall?.(target.x, target.y);
        } else {
          if (!target.isDead) target.takeDamage(baseDmg, particles, 'Bullet', true, this.edition === 'gold', this);
        }
      }
    };

    const customBulletColor = this.getCustomBulletColorHex();
    const nowAge = this.age;
    const projType = this.projectileType;
    const effectiveAccuracy = this.effectiveAccuracy;

    // Visual/audio effects & damage timing
    let isProjectile = false;
    const isMarineLegacy = (this.cardData.unitType === 'Marine');
    const isHyperMarineLegacy = (this.cardData.unitType === 'HyperMarine');
    const isA9PiranhaLegacy = (this.cardData.unitType === 'A9Piranha');
    const isPanzerLegacy = (this.cardData.unitType === 'Panzer');
    const isDeployableCannonLegacy = (this.cardData.unitType === 'DeployableCannon' || this.cardData.unitType === 'Deployable_Cannon');
    const isHyperDevilPatrolLegacy = (this.cardData.unitType === 'HyperDevilPatrol');
    const fireDirLegacy = (target ? (Math.sign(target.x - this.x) || (this.team === 'western' ? 1 : -1)) : null) || this.facingDir || (this.team === 'western' ? 1 : -1);
    const dTargetYLegacy = target ? (target.y - this.y) : 0;
    const gunAimYLegacy = (isMarineLegacy || isHyperMarineLegacy || isPanzerLegacy || isDeployableCannonLegacy) ? Math.max(-3.5, Math.min(3.5, dTargetYLegacy * 0.035)) : 0;
    const muzzleX = (isMarineLegacy || isHyperMarineLegacy)
      ? (this.x + fireDirLegacy * Math.round(this.radius * 0.85))
      : (isA9PiranhaLegacy
        ? (this.x + fireDirLegacy * Math.round(this.radius * 0.88))
        : ((isPanzerLegacy || isDeployableCannonLegacy || isHyperDevilPatrolLegacy)
          ? (this.x + fireDirLegacy * Math.round(this.radius * 1.0))
          : (this.x + (this.team === 'western' ? this.radius : -this.radius))));
    const muzzleY = (isMarineLegacy || isHyperMarineLegacy)
      ? (this.y + (isHyperMarineLegacy ? HYPER_MARINE_MUZZLE_OFFSET_Y : MARINE_MUZZLE_OFFSET_Y) + gunAimYLegacy)
      : (isA9PiranhaLegacy
        ? (this.y + A9_PIRANHA_MUZZLE_OFFSET_Y)
        : (isPanzerLegacy
          ? (this.y + PANZER_MUZZLE_OFFSET_Y + gunAimYLegacy)
          : (isDeployableCannonLegacy
            ? (this.y + DEPLOYABLE_CANNON_MUZZLE_OFFSET_Y + gunAimYLegacy)
            : (isHyperDevilPatrolLegacy
              ? (this.y + HYPER_DEVIL_PATROL_MUZZLE_OFFSET_Y)
              : this.y))));

    battlefield?.addFrameEvent?.({
      type: 'ATTACK',
      unitType: this.cardData.unitType,
      race: this.race,
      x: muzzleX,
      y: muzzleY,
      tx: target.x,
      ty: target.y,
      team: this.team,
      // Lets the online client resolve attacker/target directly
      // (Battlefield.getUnitById()) instead of an O(n) proximity scan over
      // every live unit per event (js/net/stateStream.js) - target may be
      // a base (no `id`), in which case there's no unit to hit-flash
      // anyway, same as today.
      attackerUnitId: this.id,
      targetUnitId: target?.id ?? null
    });

    if (this.race === 'Human') {
      if (isMarineLegacy) {
        audio?.playGauss?.({ x: this.x });
        particles?.addMuzzleFlash?.(muzzleX, muzzleY, customBulletColor || '#facc15', 5);
        particles?.addShellCasing?.(muzzleX, muzzleY, fireDirLegacy);
        particles?.addTracer?.(muzzleX, muzzleY, target.x, target.y, customBulletColor || '#ffdd44');
      } else if (this.cardData.unitType === 'Shade' || this.cardData.unitType === 'HyperShade') {
        // Direkter Nutzer-Fund (docs/SOUND_BATCH_PLAN.md section 1.1) -
        // Shade/HyperShade teilten sich bisher playGauss() mit Marine & Co.;
        // playSuppressedShot() (gun_silencer.m4a) grenzt die Stealth-
        // Einheit akustisch ab, Partikel bleiben identisch zum Gauss-Blitz.
        audio?.playSuppressedShot?.({ x: this.x });
        particles?.addMuzzleFlash?.(muzzleX, this.y, customBulletColor || '#facc15', 5);
        particles?.addShellCasing?.(muzzleX, this.y, this.team === 'western' ? 1 : -1);
        particles?.addTracer?.(muzzleX, this.y, target.x, target.y, customBulletColor || '#ffdd44');
      } else if (this.cardData.unitType === 'Panzer') {
        audio?.playExplosion?.('large', { x: this.x });
        particles?.addMuzzleFlash?.(muzzleX, muzzleY, customBulletColor || '#ff5500', 12);
        particles?.addShellCasing?.(muzzleX, muzzleY, this.team === 'western' ? 1 : -1);
        particles?.addShockwave?.(muzzleX, muzzleY, 25, '#f97316', 0.15);
        particles?.addScreenShake?.(4.5, 0.16);
        isProjectile = true;
        particles?.addProjectile?.(muzzleX, muzzleY, target, customBulletColor || '#ff7700', 6, 600, applyDamage);
      } else if (this.range <= 40) {
        particles?.addExplosion?.(target.x, target.y, 15, '#ffaa00', 6);
      } else {
        audio?.playLaser?.('normal', { x: this.x });
        particles?.addMuzzleFlash?.(muzzleX, this.y, customBulletColor || '#ffff66', 4);
        particles?.addTracer?.(this.x, this.y, target.x, target.y, customBulletColor || '#ffff66');
      }
    } else {
      if (this.range <= 40) {
        particles?.addHitSparks?.(target.x, target.y, 'bio', 7);
      } else {
        particles?.addTracer?.(this.x, this.y, target.x, target.y, '#88ff33');
      }
    }

    if (!isProjectile) {
      applyDamage();
    }

    if (this.isKamikaze) {
      battlefield?.handleUnitDeath?.(this, particles);
      particles?.addUnitDeathShatter?.(this);
      particles?.addExplosion?.(this.x, this.y, 35, '#88ff00', 25);
      audio?.playExplosion?.('small', { x: this.x });
      this.isDead = true;
      battlefield?.stats?.markUnitDeath(this.id);
    }
  }

  _executeVulturePodLaunch(item, battlefield, particles, audio) {
    if (this.isDead || !item || !item.target) return;
    // Launch origins aligned with the 4 symmetrical dual-pod dorsal columns (ascending raked angle)
    const VULTURE_POD_OFFSETS = [
      { x: -16, y: -20 }, // Pod 0 (far rear, elevated crest)
      { x: -16, y: -14 }, // Pod 1 (near rear)
      { x:  -6, y: -18 }, // Pod 2 (far mid-rear)
      { x:  -6, y: -12 }, // Pod 3 (near mid-rear)
      { x:   4, y: -17 }, // Pod 4 (far mid-front)
      { x:   4, y: -11 }, // Pod 5 (near mid-front)
      { x:  14, y: -16 }, // Pod 6 (far front, neck base)
      { x:  14, y: -10 }  // Pod 7 (near front)
    ];
    const podOffset = VULTURE_POD_OFFSETS[item.podIndex % 8];
    const fwd = this.facingDir || (this.team === 'western' ? 1 : -1);
    const launchX = this.x + fwd * podOffset.x;
    const launchY = this.y + podOffset.y;

    this._vultureRecoil = 5.0;
    if (this.vulturePods && this.vulturePods[item.podIndex]) {
      this.vulturePods[item.podIndex].lastLaunchFlash = 0.25;
      this.vulturePods[item.podIndex].reloadTimer = this.vulturePods[item.podIndex].reloadDuration;
    }

    audio?.playVultureBallLaunch?.({ x: this.x });

    if (item.isIntercept) {
      particles?.addVultureBioMissile?.(launchX, launchY, item.target, '#ff0033', 560, () => {
        audio?.playVultureIntercept?.({ x: item.target.x });
      }, true, this.id, item.podIndex);
    } else {
      const targetUnit = item.target;
      particles?.addVultureBioMissile?.(launchX, launchY, targetUnit, '#ff0033', 520, () => {
        particles?.addExplosion?.(targetUnit.x, targetUnit.y, 24, '#ff0033', 12);
        particles?.addScorchMark?.(targetUnit.x, targetUnit.y, 14);
      }, false, this.id, item.podIndex);

      if (battlefield?.addProjectileSim) {
        const dist = Math.hypot(targetUnit.x - launchX, targetUnit.y - launchY);
        const duration = Math.max(0.90, Math.min(1.5, 0.50 + dist / 520));
        const flightTicks = Math.max(1, Math.round(duration * 60));
        battlefield.addProjectileSim({
          flightTicks,
          targetX: targetUnit.x,
          targetY: targetUnit.y,
          targetUnitId: targetUnit.id,
          targetBaseTeam: targetUnit.isStructure && (targetUnit.id === 'base_western' || targetUnit.id === 'base_eastern') ? targetUnit.team : null,
          isStructureTarget: targetUnit.isStructure,
          damage: this.damage + ((this.damage > 0 && battlefield?.moduleSystem?.hasCongressBuff(this.team, 'advanced_ammunition')) ? 2 : 0),
          splashRadius: 1.5 * 24,
          team: this.team,
          projectileType: 'Rocket',
          sourceUnit: this,
          isGoldAttacker: this.edition === 'gold'
        });
      }
    }
  }

  // Neustart "Deflection & Accuracy": the combat resolution for any card on
  // the new schema (see hasNewCombatSchema in the constructor). One shot
  // per attack cycle (Attack Count is gone), unified Damage (no more
  // Ground/Air split), no per-attribute Bonus Damage (the attribute/tag
  // system is gone). Two independent rolls decide the outcome:
  //   1. Accuracy (this unit's own stat, rolled here): a miss never
  //      reaches the target at all - no takeDamage() call, just a cosmetic
  //      impact near/beside/behind the target's position.
  //   2. Deflection Rating (the TARGET's own stat, rolled inside
  //      takeDamage()): a hit that connects can still bounce off for zero
  //      damage - Energy-type attacks are exempt from this entirely.
  // Splash Damage (Rocket/Grenade) still applies per NEUSTART_PLAN.html's
  // "Fähigkeiten/Mechaniken" catch-all - splashRadius is unaffected by
  // this schema change.

  // Sul Parasite Vulture: High-speed parasite leap, delayed mind control on arrival, 100% heal & friendly-fire defusal
  _executeParasiteOvertake(victim, battlefield, particles, audio) {
    this.hasParasite = false;
    this.isInsane = true;
    this.insaneSeed = (this.id ? parseInt(this.id, 10) || 1 : 1);
    this.insaneTime = 0;
    this.target = null;
    victim._targetedByParasite = true;

    const newTeam = this.team;
    const startX = this.x;
    const startY = this.y - 20;
    const flightTimeSec = 0.40;
    const flightTicks = Math.max(1, Math.round(flightTimeSec * 60)); // 24 ticks

    // Visual leap presentation
    if (particles?.addParasiteLeap) {
      particles.addParasiteLeap(startX, startY, victim);
    }

    const applyOvertakeLanding = (fx, snd) => {
      victim._targetedByParasite = false;

      // Target must be alive and not a golden statue to be overtaken
      if (victim.isDead || victim.isGoldenStatue || victim.hp <= 0) {
        return;
      }

      if (battlefield?.transferUnit) {
        battlefield.transferUnit(victim, newTeam);
      } else {
        victim.team = newTeam;
      }

      // Unburrow mines/hidden units (e.g. Forgotten Loaf) so they act as active units for their new team
      if (victim.isBurrowed || victim.mineState === 'burrowed') {
        victim.isBurrowed = false;
        victim.mineState = 'idle';
        victim.isUntargetable = false;
      }

      // 100% health & shield restore
      victim.hp = victim.maxHp;
      if (victim.maxShields > 0) victim.shields = victim.maxShields;
      victim.isParasitized = true;
      victim._overtakeAnimTime = 0.45;
      victim.target = null;
      victim.facingDir = (newTeam === 'western' ? 1 : -1);

      // Stop own allies from continuing to target the new ally
      if (battlefield?.units) {
        for (let i = 0; i < battlefield.units.length; i++) {
          const u = battlefield.units[i];
          if (u.target === victim && u.team === newTeam) {
            u.target = null;
          }
        }
      }

      // Neutralize in-flight friendly projectiles targeted at victim
      if (battlefield?.pendingProjectiles) {
        for (let i = 0; i < battlefield.pendingProjectiles.length; i++) {
          const p = battlefield.pendingProjectiles[i];
          if (p.targetUnitId === victim.id && p.team === newTeam) {
            p.isIntercepted = true;
          }
        }
      }

      const activeSnd = snd || audio;
      const activeFx = fx || particles;
      activeSnd?.playParasiteOvertake?.({ x: victim.x });
      activeFx?.addDamageText?.(victim.x, victim.y - 25, "PARASITIZED!", 'crit', '#ef4444');

      battlefield?.addFrameEvent?.({
        type: 'PARASITE_OVERTAKE',
        vultureId: this.id,
        targetId: victim.id,
        team: newTeam,
        x: Math.round(victim.x),
        y: Math.round(victim.y)
      });
    };

    if (battlefield?.addProjectileSim) {
      battlefield.addProjectileSim({
        flightTicks: flightTicks,
        targetUnitId: victim.id,
        targetX: victim.x,
        targetY: victim.y,
        team: newTeam,
        projectileType: 'ParasiteLeap',
        onImpact: (impactParticles, impactAudio) => {
          applyOvertakeLanding(impactParticles, impactAudio);
        }
      });
    } else {
      applyOvertakeLanding(particles, audio);
    }
  }

  // Sul Parasite Vulture: Insane Lissajous flight path (Decoy mode after releasing parasite)
  _updateInsaneFlight(dt, battlefield) {
    this.insaneTime = (this.insaneTime || 0) + dt;
    const t = this.insaneTime;
    const seed = (this.insaneSeed || 1);

    const omegaX = 1.35 + (seed % 5) * 0.12;
    const omegaY = 2.10 + (seed % 7) * 0.15;
    const phaseX = (seed * 1.7) % Math.PI;
    const phaseY = (seed * 2.3) % Math.PI;

    const baseSpeed = this.speed || (5 * SPEED_MULTIPLIER);
    let vx = Math.sin(t * omegaX + phaseX) * baseSpeed;
    let vy = Math.cos(t * omegaY + phaseY) * (baseSpeed * 0.65);

    const minX = (battlefield?.bases?.western?.x || 120) + 70;
    const maxX = (battlefield?.bases?.eastern?.x || 1480) - 70;
    const minY = (battlefield?.laneY || 176) + 30;
    const maxY = (battlefield?.laneY || 176) + (battlefield?.laneHeight || 320) - 30;

    if (this.x < minX) vx += (minX - this.x) * 4.0;
    if (this.x > maxX) vx -= (this.x - maxX) * 4.0;
    if (this.y < minY) vy += (minY - this.y) * 4.0;
    if (this.y > maxY) vy -= (this.y - maxY) * 4.0;

    const currentSpeed = Math.hypot(vx, vy);
    if (currentSpeed > 0.001) {
      this.x += (vx / currentSpeed) * baseSpeed * dt;
      this.y += (vy / currentSpeed) * baseSpeed * dt;
      this.facingDir = vx >= 0 ? 1 : -1;
    }

    this.x = Math.max(minX - 20, Math.min(maxX + 20, this.x));
    this.y = Math.max(minY - 10, Math.min(maxY + 10, this.y));
  }

  // 6. Neuer Angriff nach COMBAT_SCHEMA (Invariante #11: Vollständige Migration)
  resolveNewCombatAttack(target, battlefield, particles, audio) {
    if (this.isEnforcerVulture) {
      return;
    }

    const projType = this.projectileType;
    let dmg = this.damage * (this.buffDamageMult || 1.0);
    if (this.damage > 0 && battlefield?.moduleSystem?.hasCongressBuff(this.team, 'advanced_ammunition')) {
      dmg += 2;
    }
    const customBulletColor = this.getCustomBulletColorHex();
    const customRocketTrail = this.getCustomRocketTrailHex();

    // Orchestra Multi-Rocket Random Targeting:
    // Orchestra darf nur Bodeneinheiten und Gebäude (inkl. gegnerische Basis) angreifen;
    // keine fliegenden Einheiten (kein Angriff auf Einheiten mit isAir == true oder elementType == 'Air')!
    let attackTarget = target;
    const isOrchestra = (this.name === 'Orchestra' || this.cardData?.unitType === 'Orchestra');
    if (isOrchestra) {
      const isEligibleOrchestraTarget = (t) => {
        if (!t || t.isDead || t.isUntargetable || t.isParachuting || t.isCinematic || t.isGoldenStatue) return false;
        if (t.isAir || t.elementType === 'Air') return false;
        if (t.team === this.team) return false;
        if (battlefield?.isInRange && !battlefield.isInRange(this, t)) return false;
        if (t.isCloaked && battlefield?.isPositionDetected && !battlefield.isPositionDetected(this.team, t.x, t.y)) return false;
        return true;
      };

      if (battlefield) {
        const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
        const enemyBase = battlefield.bases ? battlefield.bases[enemyTeam] : null;
        const bUnits = battlefield.units;
        const bLen = bUnits ? bUnits.length : 0;
        let count = 0;
        for (let i = 0; i < bLen; i++) {
          if (isEligibleOrchestraTarget(bUnits[i])) {
            count++;
          }
        }
        if (isEligibleOrchestraTarget(enemyBase)) {
          count++;
        }

        if (count > 1) {
          const chosenIdx = Math.floor(this._rng.next() * count);
          let cur = 0;
          let chosen = null;
          for (let i = 0; i < bLen; i++) {
            const u = bUnits[i];
            if (isEligibleOrchestraTarget(u)) {
              if (cur === chosenIdx) {
                chosen = u;
                break;
              }
              cur++;
            }
          }
          if (!chosen && isEligibleOrchestraTarget(enemyBase)) {
            if (cur === chosenIdx) {
              chosen = enemyBase;
            }
          }
          attackTarget = chosen || attackTarget;
        } else if (count === 1) {
          let chosen = null;
          for (let i = 0; i < bLen; i++) {
            const u = bUnits[i];
            if (isEligibleOrchestraTarget(u)) {
              chosen = u;
              break;
            }
          }
          if (!chosen && isEligibleOrchestraTarget(enemyBase)) {
            chosen = enemyBase;
          }
          attackTarget = chosen;
        } else {
          attackTarget = isEligibleOrchestraTarget(target) ? target : null;
        }
      } else {
        attackTarget = isEligibleOrchestraTarget(target) ? target : null;
      }

      if (!isEligibleOrchestraTarget(attackTarget)) {
        if (this.target && (this.target.isAir || this.target.elementType === 'Air')) {
          this.target = null;
        }
        return;
      }
    }

    const isMarine = (
      this.cardData?.unitType === 'Marine' ||
      this.name === 'Marine' ||
      this.cardData?.unitType === 'WarPig' ||
      this.cardData?.unitType === 'Ghost' ||
      this.cardData?.unitType === 'Nova' ||
      this.cardData?.unitType === 'Raynor01' ||
      this.cardData?.unitType === 'Reaper'
    );
    const isHyperMarine = (
      this.cardData?.unitType === 'HyperMarine' ||
      this.name === 'Hyper Marine' ||
      this.name === 'HyperMarine'
    );
    const isA9Piranha = (
      this.cardData?.unitType === 'A9Piranha' ||
      this.name === 'A9 Piranha' ||
      this.name === 'A9Piranha'
    );
    const isA9Grizzly = (
      this.cardData?.unitType === 'A9Grizzly' ||
      this.name === 'A9 Grizzly' ||
      this.name === 'A9Grizzly' ||
      this.isA9Grizzly
    );
    const isA9Pigeon = (
      this.cardData?.unitType === 'A9Pigeon' ||
      this.name === 'A9 Pigeon' ||
      this.name === 'A9Pigeon' ||
      this.isA9Pigeon
    );
    const isPanzer = (
      this.cardData?.unitType === 'Panzer' ||
      this.cardData?.unitType === 'HyperPanzer' ||
      this.name === 'Panzer' ||
      this.name === 'Hyper Panzer' ||
      this.name === 'HyperPanzer'
    );
    const isDeployableCannon = (
      this.cardData?.unitType === 'DeployableCannon' ||
      this.cardData?.unitType === 'Deployable_Cannon' ||
      this.name === 'Deployable Cannon' ||
      this.name === 'DeployableCannon'
    );
    const isHyperDevilPatrol = (
      this.cardData?.unitType === 'HyperDevilPatrol' ||
      this.name === 'Hyper Devil Patrol'
    );
    const isPinkBeret = (
      this.cardData?.unitType === 'PinkBeret' ||
      this.name === 'Pink Beret' ||
      this.isPinkBeret
    );
    const isStickman = (
      this.cardData?.unitType === 'Stickman' ||
      this.name === 'Stickman' ||
      this.isStickman
    );
    const isBastionLock = (
      this.cardData?.unitType === 'BastionLock' ||
      this.cardData?.unitType === 'Bastion_Lock' ||
      this.name === 'Bastion Lock' ||
      this.name === 'BastionLock' ||
      this.isBastionLock
    );
    const fireDir = (attackTarget ? (Math.sign(attackTarget.x - this.x) || (this.team === 'western' ? 1 : -1)) : null) || this.facingDir || (this.team === 'western' ? 1 : -1);
    const dTargetY = attackTarget ? (attackTarget.y - this.y) : (this.target ? (this.target.y - this.y) : 0);
    const gunAimY = (isMarine || isHyperMarine || isPanzer || isDeployableCannon) ? Math.max(-3.5, Math.min(3.5, dTargetY * 0.035)) : 0;
    let muzzleX = this.x + (this.team === 'western' ? this.radius : -this.radius);
    if (isOrchestra) {
      muzzleX = this.x + fireDir * 8;
    } else if (isBastionLock) {
      muzzleX = this.x + fireDir * Math.round(this.radius * 0.38);
    } else if (isPinkBeret) {
      muzzleX = this.x + fireDir * 18;
    } else if (isMarine || isHyperMarine) {
      muzzleX = this.x + fireDir * Math.round(this.radius * 0.85);
    } else if (isA9Piranha || isA9Grizzly || isA9Pigeon) {
      muzzleX = this.x + fireDir * Math.round(this.radius * 0.88);
    } else if (isPanzer || isDeployableCannon || isHyperDevilPatrol || isStickman) {
      muzzleX = this.x + fireDir * Math.round(this.radius * 1.0);
    }

    let muzzleY = this.y;
    if (isOrchestra) {
      muzzleY = this.y - 32;
    } else if (isBastionLock) {
      muzzleY = this.y + 2.0;
    } else if (isPinkBeret) {
      muzzleY = this.y - 4;
    } else if (isMarine || isHyperMarine) {
      muzzleY = this.y + (isHyperMarine ? HYPER_MARINE_MUZZLE_OFFSET_Y : MARINE_MUZZLE_OFFSET_Y) + gunAimY;
    } else if (isA9Piranha) {
      muzzleY = this.y + A9_PIRANHA_MUZZLE_OFFSET_Y;
    } else if (isA9Pigeon) {
      muzzleY = this.y + A9_PIGEON_MUZZLE_OFFSET_Y + Math.sin(this.pigeonPitch || 0) * 16;
      this.smokeBarrelTimer = 0.65;
      this.barrelHeat = 1.0;
    } else if (isA9Grizzly) {
      muzzleY = this.y + A9_GRIZZLY_MUZZLE_OFFSET_Y;
    } else if (isPanzer) {
      muzzleY = this.y + PANZER_MUZZLE_OFFSET_Y + gunAimY;
    } else if (isDeployableCannon) {
      muzzleY = this.y + DEPLOYABLE_CANNON_MUZZLE_OFFSET_Y + gunAimY;
    } else if (isHyperDevilPatrol) {
      muzzleY = this.y + HYPER_DEVIL_PATROL_MUZZLE_OFFSET_Y;
    }

    // Sul Preserver Lock: continuous plasma beam lock-on. Deliberately
    // resolved BEFORE the generic addFrameEvent('ATTACK', ...) below, and
    // returns without reaching it - at attackSpeed 0.1s that call would add
    // 10 network events/s per unit to the 30Hz WORLD_STATE broadcast
    // (js/net/stateStream.js consumeFrameEvents()), pushing toward the
    // 256-event battlefield.addFrameEvent() cap for no benefit, since the
    // beam itself is rendered continuously off unit.beamTargetId, not off
    // replayed ATTACK events. Only a genuine new lock gets a one-off event.
    if (this.hasBeamTether) {
      if (attackTarget.isGoldenStatue) {
        this.beamTargetId = null;
        this.target = null;
        return;
      }
      const isNewLock = this.beamTargetId !== attackTarget.id;
      this.beamTargetId = attackTarget.id;
      this.facingDir = Math.sign(attackTarget.x - this.x) || (this.team === 'western' ? 1 : -1);

      if (isNewLock) {
        battlefield?.addFrameEvent?.({
          type: 'BEAM_LOCK',
          unitType: this.cardData?.unitType,
          x: this.x,
          y: this.y,
          team: this.team,
          customColor: this.getCustomBulletColorHex() || null
        });
      }

      attackTarget.takeDamage(dmg, particles, 'Energy', true, this.edition === 'gold', this);

      // PERFORMANCE_AND_STABILITY_AUDIT.md Regel 1: bei attackSpeed <= 0.15s
      // niemals pro Zyklus eigene Schadenszahl/Funken/Sound - hier gebündelt
      // per Timer geflusht. takeDamage() selbst lief soeben bit-genau pro
      // Zyklus, nur die Präsentation wird gedrosselt.
      this._beamDmgAccum += dmg;
      this._beamFlushTimer -= this.attackCooldown; // attackCooldown was just (re)set to the interval until the next cycle
      if (this._beamFlushTimer <= 0) {
        particles?.addDamageText?.(attackTarget.x, attackTarget.y - 20, Math.round(this._beamDmgAccum), 'normal');
        particles?.addHitSparks?.(attackTarget.x, attackTarget.y, 'plasma', 3);
        audio?.playPreserverBeamPulse?.({ x: this.x });
        this._beamDmgAccum = 0;
        this._beamFlushTimer = 0.4;
      }
      return;
    }

    // Sul Looting Vulture: Apex Pyramid Solar Energy Beam
    if (this.isLootingVulture || this.name === 'Looting Vulture' || this.cardData?.unitType === 'LootingVulture') {
      const apexX = this.x + fireDir * 16;
      const apexY = this.y - 24;
      this.facingDir = fireDir;
      this._lootingAttackFlash = 0.18;

      audio?.playLootingApexBeam?.({ x: this.x });
      particles?.addTracer?.(apexX, apexY, attackTarget.x, attackTarget.y, '#fde047', 0.16);
      particles?.addTracer?.(apexX, apexY, attackTarget.x, attackTarget.y, '#ffffff', 0.08);
      particles?.addLight?.(apexX, apexY, 40, '#facc15', 0.3);
      particles?.addHitSparks?.(attackTarget.x, attackTarget.y, 'plasma', 5);

      battlefield?.addFrameEvent?.({
        type: 'ATTACK',
        unitType: 'LootingVulture',
        race: 'Sul',
        x: Math.round(apexX),
        y: Math.round(apexY),
        tx: Math.round(attackTarget.x),
        ty: Math.round(attackTarget.y),
        team: this.team,
        projType: 'Energy',
        attackerUnitId: this.id,
        targetUnitId: attackTarget?.id ?? null
      });

      attackTarget.takeDamage(dmg, particles, 'Energy', true, this.edition === 'gold', this);
      return;
    }

    battlefield?.addFrameEvent?.({
      type: 'ATTACK',
      unitType: this.cardData?.unitType || this.name,
      race: this.race,
      x: muzzleX,
      y: muzzleY,
      tx: attackTarget.x,
      ty: attackTarget.y,
      team: this.team,
      projType: projType,
      edition: this.edition || null,
      customColor: customBulletColor || null,
      // See the other addFrameEvent('ATTACK', ...) above for why.
      attackerUnitId: this.id,
      targetUnitId: attackTarget?.id ?? null
    });

    // Sul Harvesting Pillar: Dual Tentacle Lock-On, DoT & Life Siphon
    if (this.hasDualTether) {
      if (attackTarget.isGoldenStatue) {
        if (this.dualTetherTarget1Id === attackTarget.id) this.dualTetherTarget1Id = null;
        if (this.dualTetherTarget2Id === attackTarget.id) this.dualTetherTarget2Id = null;
        this.target = null;
        return;
      }
      audio?.playHarvestingTether?.({ x: this.x });
      if (!this.dualTetherTarget1Id) {
        this.dualTetherTarget1Id = attackTarget.id;
        this.tether1LockTimer = 0;
      }
      this.facingDir = Math.sign(attackTarget.x - this.x) || (this.team === 'western' ? 1 : -1);
      attackTarget.isRooted = true;

      // Phase 2 check: if 5s reached or target2 already locked, double damage
      const phase2 = !!this.dualTetherTarget2Id;
      const effectiveDmg = dmg * (phase2 ? 2.0 : 1.0);

      attackTarget.takeDamage(effectiveDmg, particles, this.projectileType || 'Melee', true, this.edition === 'gold', this);

      // 50% of damage dealt restored to Harvesting Pillar's health
      const lifesteal = effectiveDmg * this.dualTetherLifestealRatio;
      if (lifesteal > 0 && this.hp < this.maxHp) {
        this.hp = Math.min(this.maxHp, this.hp + lifesteal);
        particles?.addDamageText?.(this.x, this.y - this.radius - 10, `+${Math.round(lifesteal)}`, 'heal');
      }

      particles?.addHitSparks?.(attackTarget.x, attackTarget.y, 'plasma', phase2 ? 8 : 4);
      if (phase2) {
        particles?.addAcidSplash?.(attackTarget.x, attackTarget.y, 4);
      }
      return;
    }

    // Sul Defective Loaf: Tentacle Tether Lock-On & bio-damage channel
    if (this.hasTether) {
      if (attackTarget.isGoldenStatue) {
        this.tetherTargetId = null;
        this.target = null;
        return;
      }
      this.tetherTargetId = attackTarget.id;
      this.facingDir = Math.sign(attackTarget.x - this.x) || (this.team === 'western' ? 1 : -1);
      attackTarget.takeDamage(dmg, particles, this.projectileType || 'Melee', true, this.edition === 'gold', this);
      particles?.addHitSparks?.(attackTarget.x, attackTarget.y, 'plasma', 4);
      return;
    }

    // Reload skip mechanic (e.g. Deployable Cannon: skips firing 1 projectile every 4 projectiles due to reloading)
    if (this.reloadSkipCount > 0) {
      this.shotCount = (this.shotCount || 0) + 1;
      if (this.shotCount % this.reloadSkipCount === 0) {
        particles?.addDamageText?.(this.x, this.y - this.radius - 12, 'RELOAD', 'status');
        audio?.playGauss?.({ x: this.x });
        return;
      }
    }

    // Deflection Scaling Ability: increases damage by 10% per 10% deflection rating of opposing enemy unit
    if (this.deflectionScaling && attackTarget && attackTarget.deflectionRating) {
      dmg *= (1.0 + attackTarget.deflectionRating);
    }

    // Anti-Marine Bonus (Devil Patrol): deals bonus damage vs Marine and Hyper Marine
    if (this.cardData.antiMarineBonus && attackTarget) {
      const uType = attackTarget.cardData?.unitType || attackTarget.unitType;
      const uName = attackTarget.cardData?.name || attackTarget.name;
      if (uType === 'Marine' || uType === 'HyperMarine' || uName === 'Marine' || uName === 'Hyper Marine') {
        dmg *= this.cardData.antiMarineBonus;
      }
    }

    // Sul Monitor Lock: Snapping Melee Strike
    if (this.isMonitorLock) {
      this.snapStrikeTimer = 1.2;
      this.snapTargetId = attackTarget.id;
      this.facingDir = Math.sign(attackTarget.x - this.x) || (this.team === 'western' ? 1 : -1);
      if (audio?.playMonitorLockSnap) audio.playMonitorLockSnap({ x: this.x });
      else audio?.playGauss?.({ x: this.x });
      particles?.addHitSparks?.(attackTarget.x, attackTarget.y, 'plasma', 6);
      attackTarget.takeDamage(dmg, particles, this.projectileType || 'Melee', true, this.edition === 'gold', this);
      return;
    }

    // Sul Anti Shade: Void Cleave Melee Strike
    if (this.isAntiShade) {
      this.facingDir = Math.sign(attackTarget.x - this.x) || (this.team === 'western' ? 1 : -1);
      if (audio?.playUnitSound) {
        audio.playUnitSound('AntiShade', 'attack', { x: this.x });
      } else if (audio?.playSulAttack) {
        audio.playSulAttack({ x: this.x });
      }
      particles?.addHitSparks?.(attackTarget.x, attackTarget.y, 'plasma', 6);
      attackTarget.takeDamage(dmg, particles, this.projectileType || 'Melee', true, this.edition === 'gold', this);
      return;
    }

    // Human Hot Hand: Boxer Melee Strike (First hit deals 75 damage, subsequent hits deal 15)
    if (this.isHotHand) {
      this.firstHitDone = true;
      this.hasDealtFirstHit = true;
      this._punchAnimTime = 0.38;
      this._punchVariant = this._chooseHotHandPunch(attackTarget);
      this.facingDir = Math.sign(attackTarget.x - this.x) || (this.team === 'western' ? 1 : -1);
      audio?.playHotHandPunch?.({ x: this.x });
      particles?.addHitSparks?.(attackTarget.x, attackTarget.y, 'sparks', 6);
      attackTarget.takeDamage(dmg, particles, 'Melee', true, this.edition === 'gold', this);
      return;
    }

    if (isStickman) {
      this.shotCount = (this.shotCount || 0) + 1;
      this._lastShotVariant = (this.shotCount % 3 === 0) ? 'clumsy' : 'braced';
    }

    // Wheelie Bomber-style evasion: a flat accuracy penalty read off the
    // TARGET's own card data (targetAccuracyDebuff), applied to the
    // shooter's accuracy before the roll below. Deterministic - reads only
    // target.cardData (battlefield simulation state, identical on both
    // peers), never battlefield.selectedUnit or anything UI-local.
    const targetAccuracyDebuff = attackTarget?.cardData?.targetAccuracyDebuff || 0;
    const stickmanAirPenalty = (isStickman && (attackTarget?.isAir || attackTarget?.elementType === 'Air')) ? 0.2 : 0;
    const effectiveAccuracy = Math.max(0.05, this.accuracy - targetAccuracyDebuff - (this.buffAccuracyPenalty || 0) - stickmanAirPenalty);

    // Dual-Weapon Salvo (e.g. A9 Piranha): fires two separate weapons at the same time,
    // each dealing its own damage and having its own independent accuracy roll.
    if (this.dualWeapon) {
      const dir = fireDir || (this.team === 'western' ? 1 : -1);
      this.barrelHeat = 1.0;
      const nowAge = this.age;
      const shouldPlayAudio = (nowAge - (this._lastSalvoAudioAge || 0)) >= 0.08;
      if (shouldPlayAudio) {
        this._lastSalvoAudioAge = nowAge;
        audio?.playA9PiranhaShot ? audio.playA9PiranhaShot({ x: this.x }) : audio?.playGauss?.({ x: this.x });
      }
      particles?.addShellCasing?.(muzzleX, muzzleY, dir);

      const tracerColor = customBulletColor || '#ffdd44';
      const flashColor = customBulletColor || '#ffdd44';

      // Throttle floating text spam for rapid fire (20 shots/sec): only render floating text every ~0.25s
      const shouldShowText = (nowAge - (this._lastDmgTextAge || 0)) >= 0.25;
      if (shouldShowText) {
        this._lastDmgTextAge = nowAge;
      }

      for (let i = 0; i < 2; i++) {
        const pylonY = this.y + PYLON_OFFSETS[i];
        const weaponWillMiss = this._rng.next() >= Math.min(1, Math.max(0, effectiveAccuracy));
        battlefield?.stats?.recordShot(this.team, !weaponWillMiss);

        if (weaponWillMiss) {
          const missMode = this._rng.range(0, 3);
          let missX, missY;
          if (missMode < 1) {
            missX = target.x + dir * (24 + this._rng.range(12, 24));
            missY = target.y + this._rng.range(-14, 14);
          } else if (missMode < 2) {
            missX = target.x - dir * (24 + this._rng.range(12, 24));
            missY = target.y + this._rng.range(-14, 14);
          } else {
            missX = target.x + this._rng.range(-14, 18) * dir;
            missY = target.y + (this._rng.next() > 0.5 ? 1 : -1) * (20 + this._rng.range(10, 18));
          }
          particles?.addMuzzleFlash?.(muzzleX, pylonY, flashColor, 4);
          particles?.addTracer?.(muzzleX, pylonY, missX, missY, tracerColor);
          particles?.addProjectileMiss?.(missX, missY, 'Bullet');
          if (shouldShowText && i === 0) {
            particles?.addDamageText?.(target.x, target.y - 14, 'MISS', 'miss');
          }
        } else {
          particles?.addMuzzleFlash?.(muzzleX, pylonY, flashColor, 5);
          particles?.addTracer?.(muzzleX, pylonY, target.x, target.y, tracerColor);
          if (!target.isDead) {
            target.takeDamage(dmg, particles, projType, shouldShowText && i === 0, this.edition === 'gold', this);
          }
        }
      }
      return;
    }

    // Rolled now, by the shooter - a miss means the shot never gets a
    // chance to reach (and therefore never gets deflected by) the target.
    // Kamikaze units skip the roll entirely: there is no "shot" to miss,
    // the unit physically collides with whatever it reaches - see
    // docs/WHEELIE_BOMBER_PLAN.md Nachtrag.
    const isGold = this.edition === 'gold';
    const hotHandFirstHitDodge = !!(attackTarget?.isHotHand && !attackTarget?.firstHitDone && !this.isKamikaze && !(this.splashRadius > 0) && projType !== 'Grenade' && projType !== 'Rocket');
    if (hotHandFirstHitDodge) {
      attackTarget._dodgeAnimTime = 0.45;
    }
    const willMiss = hotHandFirstHitDodge ? true : (this.isKamikaze ? false : (this._rng.next() >= Math.min(1, Math.max(0, effectiveAccuracy))));
    battlefield?.stats?.recordShot(this.team, !willMiss);

    if (willMiss) {
      const dir = this.team === 'western' ? 1 : -1;
      const missMode = this._rng.range(0, 3);
      let missX, missY;
      if (missMode < 1) {
        // Overshoot behind target
        missX = attackTarget.x + dir * (42 + this._rng.range(20, 40));
        missY = attackTarget.y + this._rng.range(-18, 18);
      } else if (missMode < 2) {
        // Undershoot in front of target
        missX = attackTarget.x - dir * (36 + this._rng.range(18, 32));
        missY = attackTarget.y + this._rng.range(-18, 18);
      } else {
        // Stray above or below target
        missX = attackTarget.x + this._rng.range(-18, 24) * dir;
        missY = attackTarget.y + (this._rng.next() > 0.5 ? 1 : -1) * (34 + this._rng.range(14, 24));
      }

      const isSentinal = (this.cardData?.unitType === 'SentinalLock');
      if (isSentinal) {
        // Toxic-neon-yellow bio-glob spit, lobbed like Apostate Loaf's
        // spore mortar (see SENTINAL_LOCK_LOB_OPTIONS below) rather than an
        // instant bullet tracer - a hiss instead of a gunshot/explosion.
        const globColor = customBulletColor || SENTINAL_LOCK_GLOB_COLOR;
        const lobOptions = customBulletColor
          ? { ...SENTINAL_LOCK_LOB_OPTIONS, palette: buildSentinalGlobPalette(customBulletColor) }
          : SENTINAL_LOCK_LOB_OPTIONS;
        const mouthY = this.y + SENTINAL_LOCK_MUZZLE_OFFSET_Y;
        if (audio?.playSentinalSpit) audio.playSentinalSpit({ x: this.x });
        else audio?.playHiss?.({ x: this.x });
        particles?.addMuzzleFlash?.(muzzleX, mouthY, globColor, 5);
        if (particles?.addSporeLob) {
          particles.addSporeLob(muzzleX, mouthY, missX, missY, () => {
            particles.addProjectileMiss(missX, missY, this.projectileType || 'Bullet');
            particles.addDamageText(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
          }, globColor, lobOptions);
        } else if (particles?.addBallisticBaseShell) {
          particles.addBallisticBaseShell(muzzleX, mouthY, missX, missY, () => {
            particles.addProjectileMiss(missX, missY, this.projectileType || 'Bullet');
            particles.addDamageText(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
          }, globColor);
        } else {
          particles?.addProjectileMiss?.(missX, missY, this.projectileType || 'Bullet');
          particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
        }
      } else if (projType === 'Bullet') {
        const isShade = (this.cardData?.unitType === 'Shade' || this.cardData?.unitType === 'HyperShade' || this.name === 'Shade' || this.name === 'Hyper Shade');
        if (isA9Grizzly) {
          if (audio?.playA9GrizzlyShot) {
            audio.playA9GrizzlyShot({ x: this.x });
          } else if (audio?.playUnitSound) {
            audio.playUnitSound('A9Grizzly', 'attack', { x: this.x });
          } else {
            audio?.playGauss?.({ x: this.x, unitType: 'A9Grizzly' });
          }
        } else if (isA9Pigeon) {
          if (audio?.playA9PigeonShot) {
            audio.playA9PigeonShot({ x: this.x });
          } else if (audio?.playUnitSound) {
            audio.playUnitSound('A9Pigeon', 'attack', { x: this.x });
          } else {
            audio?.playGauss?.({ x: this.x, unitType: 'A9Pigeon' });
          }
        } else if (isShade && audio?.playSuppressedShot) {
          audio.playSuppressedShot({ x: this.x });
        } else if (isPinkBeret) {
          if (audio?.playPinkBeretPistol) {
            audio.playPinkBeretPistol({ x: this.x });
          } else if (audio?.playUnitSound) {
            audio.playUnitSound('PinkBeret', 'attack', { x: this.x });
          } else {
            audio?.playGauss?.({ x: this.x });
          }
        } else if (isBastionLock) {
          const nowAge = this.age;
          if ((nowAge - (this._lastBastionShotAudio || 0)) >= 0.08) {
            this._lastBastionShotAudio = nowAge;
            if (audio?.playBastionShot) audio.playBastionShot({ x: this.x });
            else audio?.playGauss?.({ x: this.x });
          }
        } else {
          audio?.playGauss?.({ x: this.x });
        }
        if (isBastionLock) {
          if (this._lastBeamTarget) {
            this._lastBeamTarget.x = missX;
            this._lastBeamTarget.y = missY;
          } else {
            this._lastBeamTarget = { x: missX, y: missY };
          }
          this._lastBeamTime = this.age;
          particles?.addBeam?.(muzzleX, muzzleY, missX, missY, '#38bdf8', 3.2, 0.12, { isBastionBeam: true });
          particles?.addBeam?.(muzzleX, muzzleY, missX, missY, '#ffffff', 1.2, 0.12, { isBastionBeam: true });
          particles?.addMuzzleFlash?.(muzzleX, muzzleY, '#38bdf8', 5, { isBastionFlash: true });
          particles?.addAnamorphicFlare?.(muzzleX, muzzleY, 24, 1.5, 0.08, '#38bdf8', { isBastionFlare: true });
          particles?.addProjectileMiss?.(missX, missY, 'Bullet');
          particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
        } else {
          const bulletColor = customBulletColor || (isPinkBeret ? '#ff2a85' : '#ffdd44');
          particles?.addMuzzleFlash?.(muzzleX, muzzleY, bulletColor, 5);
          particles?.addShellCasing?.(muzzleX, muzzleY, fireDir);
          particles?.addTracer?.(muzzleX, muzzleY, missX, missY, bulletColor, 0.08);
          particles?.addProjectileMiss?.(missX, missY, 'Bullet');
          particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
        }
      } else if (projType === 'Rocket') {
        if (isOrchestra) {
          audio?.playRocketLaunch?.('heavy', { x: this.x, unitType: 'Orchestra' });
          const fwd = this.team === 'western' ? 1 : -1;
          const launchX = this.x + fwd * 8;
          const launchY = this.y - 32;
          particles?.addMuzzleFlash?.(launchX, launchY, customBulletColor || '#ffffff', 14);
          const missDest = { x: missX, y: missY };
          const speed = 480;
          const dist = Math.hypot(missX - launchX, missY - launchY);
          const flightTicks = Math.max(1, Math.round((dist / speed) * 60));

          if (particles?.addOrchestraRocket) {
            particles.addOrchestraRocket(launchX, launchY, missDest, customBulletColor, speed, () => {
              particles?.addProjectileMiss?.(missX, missY, 'Rocket');
              particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
            }, customRocketTrail);
          } else if (particles?.addProjectile) {
            particles.addProjectile(launchX, launchY, missDest, customBulletColor || '#ff8800', 5, speed, () => {
              particles?.addProjectileMiss?.(missX, missY, 'Rocket');
              particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
            });
          } else {
            particles?.addProjectileMiss?.(missX, missY, 'Rocket');
            particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
          }

          // Option B: Artillery scatter - regular splash damage applies at impact crater!
          battlefield.addProjectileSim({
            flightTicks,
            targetX: missX,
            targetY: missY,
            targetUnitId: null,
            damage: dmg,
            splashRadius: this.splashRadius || 0,
            team: this.team,
            projectileType: projType,
            sourceUnit: this,
            isGoldAttacker: isGold,
            customBulletColor: customBulletColor || '#ffffff',
            hasDisarm: !!this.inflictsDisarm,
            disarmDuration: this.disarmInflictDuration || 3
          });
        } else {
          // Rocketship (this.dualMissile ist exklusiv für Rocketship gesetzt): 30% leiser auf Nutzer-Feedback
          audio?.playRocketLaunch?.('normal', { x: this.x, ...(this.dualMissile ? { volume: 0.7 } : null) });
          particles?.addMuzzleFlash?.(muzzleX, this.y, customBulletColor || '#ff7700', 8);
          particles?.addAnamorphicFlare?.(muzzleX, this.y, 130, 3.5, 0.15, customBulletColor || '#f97316');
          const missDest = { x: missX, y: missY };
          if (this.dualMissile && particles?.addDualCurvedMissiles) {
            particles.addDualCurvedMissiles(muzzleX, this.y, missDest, customBulletColor || '#ff8800', 5, 480, () => {
              particles.addProjectileMiss(missX, missY, 'Rocket');
              particles.addDamageText(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
            }, customRocketTrail);
          } else if (particles?.addProjectile) {
            particles.addProjectile(muzzleX, this.y, missDest, customBulletColor || '#ff8800', 5, 480, () => {
              particles?.addProjectileMiss?.(missX, missY, 'Rocket');
              particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
            });
          } else {
            particles?.addProjectileMiss?.(missX, missY, 'Rocket');
            particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
          }
        }
      } else if (projType === 'Grenade') {
        const isApostate = (this.name === 'Apostate Loaf' || this.cardData?.unitType === 'ApostateLoaf');
        if (isApostate) {
          const sporeColor = customBulletColor || '#84cc16';
          const launchY = this.y - 8;
          if (audio?.playSporeLob) audio.playSporeLob({ x: this.x });
          else audio?.playExplosion?.('small', { x: this.x });
          particles?.addMuzzleFlash?.(muzzleX, launchY, sporeColor, 5);
          particles?.addVaporPuff?.(muzzleX, launchY - 4, 12, 0.4);
          particles?.addRisingEmbers?.(muzzleX, launchY, 4, 8);
          if (particles?.addSporeLob) {
            particles.addSporeLob(muzzleX, launchY, missX, missY, () => {
              particles.addProjectileMiss(missX, missY, 'Grenade');
              particles.addDamageText(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
            }, sporeColor);
          } else if (particles?.addBallisticBaseShell) {
            particles.addBallisticBaseShell(muzzleX, launchY, missX, missY, () => {
              particles.addProjectileMiss(missX, missY, 'Grenade');
              particles.addDamageText(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
            }, sporeColor);
          } else {
            particles?.addProjectileMiss?.(missX, missY, 'Grenade');
            particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
          }
        } else {
          if (isPanzer) {
            audio?.playPanzerCannon ? audio.playPanzerCannon({ x: this.x }) : audio?.playExplosion?.('small', { x: this.x });
          } else if (this.name === 'Hyper Devil Patrol' || this.cardData?.unitType === 'HyperDevilPatrol') {
            audio?.playHyperDevilShot ? audio.playHyperDevilShot({ x: this.x }) : audio?.playExplosion?.('small', { x: this.x });
          } else {
            audio?.playExplosion?.('small', { x: this.x });
          }
          particles?.addMuzzleFlash?.(muzzleX, muzzleY, customBulletColor || '#c9c96b', 6);
          particles?.addAnamorphicFlare?.(muzzleX, muzzleY, 140, 3.5, 0.16, customBulletColor || '#fbbf24');
          if (particles?.addBallisticBaseShell) {
            particles.addBallisticBaseShell(muzzleX, muzzleY, missX, missY, () => {
              particles.addProjectileMiss(missX, missY, 'Grenade');
              particles.addDamageText(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
            }, customBulletColor);
          } else {
            particles?.addProjectileMiss?.(missX, missY, 'Grenade');
            particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
          }
        }
      } else if (projType === 'Energy') {
        if (this.name === 'Devil Patrol' || this.cardData?.unitType === 'DevilPatrol') {
          const flameColor = customBulletColor || '#ff4400';
          if (audio?.playFlamethrower) audio.playFlamethrower(0.7, { x: this.x });
          else audio?.playExplosion?.('small', { x: this.x });
          particles?.addMuzzleFlash?.(muzzleX, this.y, flameColor, 8);
          particles?.addFire?.(muzzleX, this.y, missX, missY, customBulletColor);
        } else if (this.name === 'Tesla Walker' || this.cardData?.unitType === 'TeslaWalker' || this.name === 'Hyper Tesla Walker' || this.cardData?.unitType === 'HyperTeslaWalker') {
          const isHyper = (this.name === 'Hyper Tesla Walker' || this.cardData?.unitType === 'HyperTeslaWalker');
          const fireDir = (attackTarget ? (Math.sign(attackTarget.x - this.x) || (this.team === 'western' ? 1 : -1)) : null) || this.facingDir || (this.team === 'western' ? 1 : -1);
          const teslaOriginX = isHyper ? (this.x + fireDir * 26) : muzzleX;
          const teslaOriginY = isHyper ? (this.y - 22) : (this.y - 14);
          if (isHyper) {
            audio?.playHyperTesla ? audio.playHyperTesla({ x: this.x }) : (audio?.playTesla ? audio.playTesla({ x: this.x }) : audio?.playLaser?.('normal', { x: this.x }));
          } else {
            audio?.playTesla ? audio.playTesla({ x: this.x }) : audio?.playLaser?.('normal', { x: this.x });
          }
          particles?.addMuzzleFlash?.(teslaOriginX, teslaOriginY, '#00f0ff', isHyper ? 10 : 8);
          if (particles?.addTeslaArc) {
            particles.addTeslaArc(teslaOriginX, teslaOriginY, missX, missY, '#00f0ff');
          } else {
            particles?.addBeam?.(teslaOriginX, teslaOriginY, missX, missY, '#00f0ff', isHyper ? 4.2 : 3.5, 0.16);
          }
        } else {
          audio?.playLaser?.('normal', { x: this.x });
          particles?.addMuzzleFlash?.(muzzleX, this.y, '#38bdf8', 6);
          particles?.addBeam?.(this.x, this.y, missX, missY, '#38bdf8', 3, 0.14);
        }
        particles?.addProjectileMiss?.(missX, missY, 'Energy');
        particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
      } else {
        particles?.addDamageText?.(attackTarget.x, attackTarget.y - 14, 'MISS', 'miss');
      }
      if (isOrchestra) {
        this.rocketsFired = (this.rocketsFired || 0) + 1;
        if (this.rocketsFired >= 10) {
          this.rocketsFired = 0;
          this.isReloading = true;
          const reloadSec = this.reloadDuration || 10.0;
          this.attackCooldown = reloadSec;
          particles?.addDamageText?.(this.x, this.y - this.radius - 12, `RELOAD (${Math.round(reloadSec)}s)`, 'status');
        }
      }
      return;
    }

    const resolveHit = () => {
      if (attackTarget.isDead) return;
      attackTarget.takeDamage(dmg, particles, projType, true, isGold, this);
      if (this.hasIncinerate) {
        attackTarget.applyIncinerate?.(this.incinerateDuration, this.incinerateDps, this.team);
      }
      if (this.inflictsSporeSlow) {
        attackTarget.applySporeSlow?.(this.sporeSlowDuration, this.sporeSlowReduction);
      }
      if (this.inflictsDisarm) {
        attackTarget.applyDisarm?.(this.disarmInflictDuration);
      }
      if (this.splashRadius > 0) {
        const hitAir = this.targetsAir !== false;
        const hitGround = this.targetsGround !== false;
        const effectiveSplashDmg = this.cardData?.splashDamage !== undefined ? this.cardData.splashDamage : dmg;
        if (isBastionLock) {
          particles?.addBastionBurstingParticles?.(attackTarget.x, attackTarget.y, this.splashRadius || 28);
          particles?.addHitSparks?.(attackTarget.x, attackTarget.y, 'plasma', 3);
          if (battlefield?.quality !== 'low') {
            particles?.addVaporPuff?.(attackTarget.x, attackTarget.y - 4, 8, 0.25);
          }
          // Delayed splash damage: bursting particles fly outward for ~8 ticks (~0.13s) before impacting surrounding ground units
          const flightTicks = 8;
          const impactX = attackTarget.x;
          const impactY = attackTarget.y;
          const sRad = this.splashRadius || 28;
          const sourceTeam = this.team;
          const pType = projType;
          if (battlefield?.addProjectileSim) {
            battlefield.addProjectileSim({
              flightTicks,
              targetX: impactX,
              targetY: impactY,
              team: sourceTeam,
              projectileType: pType,
              sourceUnit: this,
              isGoldAttacker: isGold,
              onImpact: (pParticles, pAudio) => {
                battlefield.dealSplashDamage(impactX, impactY, sRad, effectiveSplashDmg, sourceTeam, false, true, pParticles, pType, attackTarget, false, this);
                pParticles?.addHitSparks?.(impactX, impactY, 'metal', 2);
              }
            });
          } else {
            battlefield.dealSplashDamage(impactX, impactY, sRad, effectiveSplashDmg, sourceTeam, false, true, particles, pType, attackTarget, false, this);
          }
        } else {
          battlefield.dealSplashDamage(attackTarget.x, attackTarget.y, this.splashRadius, effectiveSplashDmg, this.team, hitAir, hitGround, particles, projType, attackTarget, false, this, this.hasIncinerate, this.incinerateDuration, this.incinerateDps);
          if (projType !== 'Grenade' && projType !== 'Rocket') {
            particles?.addExplosion?.(attackTarget.x, attackTarget.y, 22, '#ff8800', 12);
          }
          particles?.addScorchMark?.(attackTarget.x, attackTarget.y, 14);
          particles?.addArtillerySpall?.(attackTarget.x, attackTarget.y);
        }
      }
    };

    this.barrelHeat = 1.0;

    const isSentinal = (this.cardData?.unitType === 'SentinalLock');
    if (isSentinal) {
      // Toxic-neon-yellow bio-glob bite, lobbed like Apostate Loaf's
      // spore mortar (see "die eine Regel" in CLAUDE.md - this is purely
      // cosmetic, damage/splash below already resolved from server-side
      // hand/stat state, not from anything the client asserts).
      const globColor = customBulletColor || SENTINAL_LOCK_GLOB_COLOR;
      const lobOptions = customBulletColor
        ? { ...SENTINAL_LOCK_LOB_OPTIONS, palette: buildSentinalGlobPalette(customBulletColor) }
        : SENTINAL_LOCK_LOB_OPTIONS;
      const mouthY = this.y + SENTINAL_LOCK_MUZZLE_OFFSET_Y;
      if (audio?.playSentinalSpit) audio.playSentinalSpit({ x: this.x });
      else audio?.playHiss?.({ x: this.x });
      particles?.addMuzzleFlash?.(muzzleX, mouthY, globColor, 5);

      const dist = Math.hypot(attackTarget.x - muzzleX, attackTarget.y - mouthY);
      const flightTimeSec = Math.max(0.48, Math.min(0.76, 0.38 + dist * 0.0016));
      const flightTicks = Math.max(1, Math.round(flightTimeSec * 60));

      if (particles?.addSporeLob) {
        particles.addSporeLob(muzzleX, mouthY, attackTarget.x, attackTarget.y, null, globColor, lobOptions);
      } else if (particles?.addBallisticBaseShell) {
        particles.addBallisticBaseShell(muzzleX, mouthY, attackTarget.x, attackTarget.y, null, globColor);
      }

      battlefield.addProjectileSim({
        flightTicks,
        targetX: attackTarget.x,
        targetY: attackTarget.y,
        targetUnitId: attackTarget.id,
        isStructureTarget: attackTarget.isStructure,
        damage: dmg,
        splashRadius: this.splashRadius || 0,
        team: this.team,
        projectileType: this.projectileType || 'Bullet',
        sourceUnit: this,
        isGoldAttacker: isGold,
        hasDisarm: !!this.inflictsDisarm,
        disarmDuration: this.disarmInflictDuration || 3
      });
      return;
    }

    if (projType === 'Bullet') {
      const isShade = (this.cardData?.unitType === 'Shade' || this.cardData?.unitType === 'HyperShade' || this.name === 'Shade' || this.name === 'Hyper Shade');
      if (isA9Grizzly) {
        if (audio?.playA9GrizzlyShot) {
          audio.playA9GrizzlyShot({ x: this.x });
        } else if (audio?.playUnitSound) {
          audio.playUnitSound('A9Grizzly', 'attack', { x: this.x });
        } else {
          audio?.playGauss?.({ x: this.x, unitType: 'A9Grizzly' });
        }
      } else if (isA9Pigeon) {
        if (audio?.playA9PigeonShot) {
          audio.playA9PigeonShot({ x: this.x });
        } else if (audio?.playUnitSound) {
          audio.playUnitSound('A9Pigeon', 'attack', { x: this.x });
        } else {
          audio?.playGauss?.({ x: this.x, unitType: 'A9Pigeon' });
        }
      } else if (isShade && audio?.playSuppressedShot) {
        audio.playSuppressedShot({ x: this.x });
      } else if (isPinkBeret) {
        if (audio?.playPinkBeretPistol) {
          audio.playPinkBeretPistol({ x: this.x });
        } else if (audio?.playUnitSound) {
          audio.playUnitSound('PinkBeret', 'attack', { x: this.x });
        } else {
          audio?.playGauss?.({ x: this.x });
        }
      } else if (isBastionLock) {
        const nowAge = this.age;
        if ((nowAge - (this._lastBastionShotAudio || 0)) >= 0.08) {
          this._lastBastionShotAudio = nowAge;
          if (audio?.playBastionShot) audio.playBastionShot({ x: this.x });
          else audio?.playGauss?.({ x: this.x });
        }
      } else {
        audio?.playGauss?.({ x: this.x });
      }
      if (isBastionLock) {
        if (this._lastBeamTarget) {
          this._lastBeamTarget.x = attackTarget.x;
          this._lastBeamTarget.y = attackTarget.y;
        } else {
          this._lastBeamTarget = { x: attackTarget.x, y: attackTarget.y };
        }
        this._lastBeamTime = this.age;
        particles?.addBeam?.(muzzleX, muzzleY, attackTarget.x, attackTarget.y, '#38bdf8', 3.2, 0.12, { isBastionBeam: true });
        particles?.addBeam?.(muzzleX, muzzleY, attackTarget.x, attackTarget.y, '#ffffff', 1.2, 0.12, { isBastionBeam: true });
        particles?.addMuzzleFlash?.(muzzleX, muzzleY, '#38bdf8', 5, { isBastionFlash: true });
        particles?.addAnamorphicFlare?.(muzzleX, muzzleY, 24, 1.5, 0.08, '#38bdf8', { isBastionFlare: true });
      } else {
        const bulletColor = customBulletColor || (isPinkBeret ? '#ff2a85' : '#ffdd44');
        particles?.addMuzzleFlash?.(muzzleX, muzzleY, bulletColor, 5);
        particles?.addShellCasing?.(muzzleX, muzzleY, fireDir);
        particles?.addTracer?.(muzzleX, muzzleY, attackTarget.x, attackTarget.y, bulletColor, 0.08);
        particles?.addAnamorphicFlare?.(muzzleX, muzzleY, 70, 2.0, 0.10, isPinkBeret ? '#ff69b4' : (customBulletColor || '#fef08a'));
      }
      resolveHit();
    } else if (projType === 'Rocket') {
      const speed = 480;
      const dist = Math.hypot(attackTarget.x - muzzleX, attackTarget.y - this.y);
      const flightTicks = Math.max(1, Math.round((dist / speed) * 60));
      const rocketColor = customBulletColor || (this.race === 'Sul' ? '#c084fc' : '#ff8800');

      if (isOrchestra) {
        audio?.playRocketLaunch?.('heavy', { x: this.x, unitType: 'Orchestra' });
        const fwd = this.team === 'western' ? 1 : -1;
        const launchX = this.x + fwd * 8;
        const launchY = this.y - 32;
        particles?.addMuzzleFlash?.(launchX, launchY, customBulletColor || '#ffffff', 14);
        if (particles?.addOrchestraRocket) {
          particles.addOrchestraRocket(launchX, launchY, attackTarget, customBulletColor, speed, null, customRocketTrail);
        } else if (particles?.addProjectile) {
          particles.addProjectile(launchX, launchY, attackTarget, customBulletColor || '#ff8800', 5, speed);
        }
      } else {
        // Rocketship (this.dualMissile ist exklusiv für Rocketship gesetzt): 30% leiser auf Nutzer-Feedback
        audio?.playRocketLaunch?.('normal', { x: this.x, ...(this.dualMissile ? { volume: 0.7 } : null) });
        particles?.addMuzzleFlash?.(muzzleX, this.y, rocketColor, 8);
        particles?.addAnamorphicFlare?.(muzzleX, this.y, 130, 3.5, 0.15, this.race === 'Sul' ? '#e879f9' : '#f97316');
        particles?.addRisingEmbers?.(muzzleX, this.y, 3, 10);

        if (this.dualMissile && particles?.addDualCurvedMissiles) {
          particles.addDualCurvedMissiles(muzzleX, this.y, attackTarget, rocketColor, 5, speed, null, customRocketTrail);
        } else if (particles?.addProjectile) {
          particles.addProjectile(muzzleX, this.y, attackTarget, rocketColor, 5, speed);
        }
      }

      battlefield.addProjectileSim({
        flightTicks,
        targetX: attackTarget.x,
        targetY: attackTarget.y,
        targetUnitId: attackTarget.id,
        isStructureTarget: attackTarget.isStructure,
        damage: dmg,
        splashRadius: this.splashRadius || 0,
        team: this.team,
        projectileType: projType,
        sourceUnit: this,
        isGoldAttacker: isGold,
        customBulletColor: rocketColor,
        hasDisarm: !!this.inflictsDisarm,
        disarmDuration: this.disarmInflictDuration || 3
      });
    } else if (projType === 'Grenade') {
      const isApostate = (this.name === 'Apostate Loaf' || this.cardData?.unitType === 'ApostateLoaf');
      if (isApostate) {
        if (audio?.playSporeLob) audio.playSporeLob({ x: this.x });
        else audio?.playExplosion?.('small', { x: this.x });
        const sporeColor = customBulletColor || '#84cc16';
        const launchY = this.y - 8;
        particles?.addMuzzleFlash?.(muzzleX, launchY, sporeColor, 5);
        particles?.addAnamorphicFlare?.(muzzleX, launchY, 140, 3.5, 0.18, '#bef264');
        particles?.addDistortionRing?.(muzzleX, launchY, 24, 0.35, '#65a30d');
        particles?.addRisingEmbers?.(muzzleX, launchY, 5, 10);
        particles?.addVaporPuff?.(muzzleX, launchY - 4, 12, 0.4);

        const dist = Math.hypot(target.x - muzzleX, target.y - launchY);
        // High-arc parabolic trajectory flight time (0.48s to 0.76s)
        const flightTimeSec = Math.max(0.48, Math.min(0.76, 0.38 + dist * 0.0016));
        const flightTicks = Math.max(1, Math.round(flightTimeSec * 60));

        if (particles?.addSporeLob) {
          particles.addSporeLob(muzzleX, launchY, target.x, target.y, null, sporeColor);
        } else if (particles?.addBallisticBaseShell) {
          particles.addBallisticBaseShell(muzzleX, launchY, target.x, target.y, null, sporeColor);
        }

        battlefield.addProjectileSim({
          flightTicks,
          targetX: target.x,
          targetY: target.y,
          targetUnitId: target.id,
          isStructureTarget: target.isStructure,
          damage: dmg,
          splashRadius: this.splashRadius || 0,
          team: this.team,
          projectileType: projType,
          sourceUnit: this,
          isGoldAttacker: isGold,
          hasIncinerate: !!this.hasIncinerate,
          incinerateDuration: this.incinerateDuration,
          incinerateDps: this.incinerateDps,
          hasSporeSlow: !!this.inflictsSporeSlow,
          sporeSlowDuration: this.sporeSlowDuration || 10,
          sporeSlowAmount: this.sporeSlowReduction || 2
        });
      } else {
        if (isPanzer) {
          audio?.playPanzerCannon ? audio.playPanzerCannon({ x: this.x }) : audio?.playExplosion?.('small', { x: this.x });
        } else if (this.name === 'Hyper Devil Patrol' || this.cardData?.unitType === 'HyperDevilPatrol') {
          audio?.playHyperDevilShot ? audio.playHyperDevilShot({ x: this.x }) : audio?.playExplosion?.('small', { x: this.x });
        } else {
          audio?.playExplosion?.('small', { x: this.x });
        }
        const grenadeColor = customBulletColor || '#c9c96b';
        particles?.addMuzzleFlash?.(muzzleX, muzzleY, grenadeColor, 6);
        particles?.addAnamorphicFlare?.(muzzleX, muzzleY, 160, 4.0, 0.18, '#fbbf24');
        particles?.addDistortionRing?.(muzzleX, muzzleY, 28, 0.35, '#f59e0b');
        particles?.addRisingEmbers?.(muzzleX, muzzleY, 4, 12);

        const dist = Math.hypot(target.x - muzzleX, target.y - muzzleY);
        const flightTimeSec = Math.max(0.20, Math.min(0.38, dist / 950));
        const flightTicks = Math.max(1, Math.round(flightTimeSec * 60));

        if (particles?.addBallisticBaseShell) {
          particles.addBallisticBaseShell(muzzleX, muzzleY, target.x, target.y, null, grenadeColor);
        }

        battlefield.addProjectileSim({
          flightTicks,
          targetX: target.x,
          targetY: target.y,
          targetUnitId: target.id,
          isStructureTarget: target.isStructure,
          damage: dmg,
          splashRadius: this.splashRadius || 0,
          team: this.team,
          projectileType: projType,
          sourceUnit: this,
          isGoldAttacker: isGold,
          hasIncinerate: !!this.hasIncinerate,
          incinerateDuration: this.incinerateDuration,
          incinerateDps: this.incinerateDps,
          hasSporeSlow: !!this.inflictsSporeSlow,
          sporeSlowDuration: this.sporeSlowDuration || 10,
          sporeSlowAmount: this.sporeSlowReduction || 2
        });
      }
    } else if (projType === 'Energy' && this.cardData?.unitType === 'WheelieBomber') {
      // Contact detonation, not a fired shot - this unit crashes into its
      // target and explodes.
      if (!target.isDead) {
        target.takeDamage(dmg, particles, projType, true, isGold, this);
        battlefield.dealSplashDamage(target.x, target.y, this.splashRadius, dmg, this.team, true, true, particles, projType, target, true, this);
      }
      particles?.addScreenShake?.(7, 0.28);
      particles?.addShockwave?.(target.x, target.y, 90, '#f97316', 0.35);
      particles?.addExplosion?.(target.x, target.y, 66, '#ff8800', 36);
      particles?.addExplosion?.(target.x, target.y, 40, '#fde047', 20);
      particles?.addScorchMark?.(target.x, target.y, 42);
      particles?.addArtillerySpall?.(target.x, target.y);
      audio?.stopWheelieEngine?.(this.driveAudioSoundId);
      this.driveAudioSoundId = null;
      audio?.triggerCombatDucking?.(450, 0.55);
      audio?.playExplosion?.('heavy', { x: target.x });
    } else if (projType === 'Energy') {
      if (this.name === 'Devil Patrol' || this.cardData?.unitType === 'DevilPatrol') {
        const flameColor = customBulletColor || '#ff4400';
        if (audio?.playFlamethrower) audio.playFlamethrower(0.7, { x: this.x });
        else audio?.playExplosion?.('small', { x: this.x });
        particles?.addMuzzleFlash?.(muzzleX, this.y, flameColor, 8);
        particles?.addAnamorphicFlare?.(muzzleX, this.y, 140, 3.5, 0.16, flameColor);
        particles?.addFire?.(muzzleX, this.y, target.x, target.y, customBulletColor);
      } else if (this.name === 'Tesla Walker' || this.cardData?.unitType === 'TeslaWalker' || this.name === 'Hyper Tesla Walker' || this.cardData?.unitType === 'HyperTeslaWalker') {
        const isHyper = (this.name === 'Hyper Tesla Walker' || this.cardData?.unitType === 'HyperTeslaWalker');
        const fireDir = (target ? (Math.sign(target.x - this.x) || (this.team === 'western' ? 1 : -1)) : null) || this.facingDir || (this.team === 'western' ? 1 : -1);
        const teslaOriginX = isHyper ? (this.x + fireDir * 26) : muzzleX;
        const teslaOriginY = isHyper ? (this.y - 22) : (this.y - 14);
        const teslaColor = customBulletColor || '#00f0ff';
        particles?.addAnamorphicFlare?.(teslaOriginX, teslaOriginY, isHyper ? 220 : 180, isHyper ? 5.5 : 4.5, 0.22, teslaColor);
        if (isHyper) {
          audio?.playHyperTesla ? audio.playHyperTesla({ x: this.x }) : (audio?.playTesla ? audio.playTesla({ x: this.x }) : audio?.playLaser?.('normal', { x: this.x }));
        } else {
          audio?.playTesla ? audio.playTesla({ x: this.x }) : audio?.playLaser?.('normal', { x: this.x });
        }
        particles?.addMuzzleFlash?.(teslaOriginX, teslaOriginY, teslaColor, isHyper ? 10 : 8);
        particles?.addDistortionRing?.(target.x, target.y, isHyper ? 44 : 36, 0.35, teslaColor);
        if (particles?.addTeslaArc) {
          particles.addTeslaArc(teslaOriginX, teslaOriginY, target.x, target.y, teslaColor);
        } else {
          particles?.addBeam?.(teslaOriginX, teslaOriginY, target.x, target.y, teslaColor, isHyper ? 4.2 : 3.5, 0.16);
        }
        particles?.addTeslaGroundArcs?.(target.x, target.y + (target.radius || 12) * 0.8);
      } else {
        particles?.addAnamorphicFlare?.(muzzleX, this.y, 180, 4.5, 0.22, '#00f0ff');
        audio?.playLaser?.('normal', { x: this.x });
        particles?.addMuzzleFlash?.(muzzleX, this.y, '#38bdf8', 6);
        particles?.addBeam?.(this.x, this.y, target.x, target.y, '#38bdf8', 3, 0.14);
      }
      resolveHit();

      // Hyper Tesla Walker Voltage Jump (Chain Lightning): jumps to another target within 10 range (240px)
      if (this.cardData?.unitType === 'HyperTeslaWalker' || this.name === 'Hyper Tesla Walker') {
        const maxJumpDist = 10 * 24; // 240 px
        const maxJumpDistSq = maxJumpDist * maxJumpDist;
        let bestSecondary = null;
        let bestDistSq = maxJumpDistSq + 1;

        if (battlefield?.units) {
          for (let i = 0; i < battlefield.units.length; i++) {
            const u = battlefield.units[i];
            if (u === target || u.team === this.team || u.isDead) continue;
            if (u.isCloaked && !battlefield.isPositionDetected(this.team, u.x, u.y)) continue;
            if (u.isUntargetable || (u.executionTimer && u.executionTimer > 0)) continue;

            const dx = u.x - target.x;
            const dy = u.y - target.y;
            const dSq = dx * dx + dy * dy;
            if (dSq <= maxJumpDistSq) {
              if (dSq < bestDistSq) {
                bestDistSq = dSq;
                bestSecondary = u;
              } else if (dSq === bestDistSq && bestSecondary) {
                const idA = parseInt(String(u.id).replace(/\D/g, ''), 10) || 0;
                const idB = parseInt(String(bestSecondary.id).replace(/\D/g, ''), 10) || 0;
                if (idA < idB) bestSecondary = u;
              }
            }
          }
        }

        if (!bestSecondary && battlefield?.bases) {
          const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
          const enemyBase = battlefield.bases[enemyTeam];
          if (enemyBase && enemyBase.hp > 0) {
            const dx = enemyBase.x - target.x;
            const dy = enemyBase.y - target.y;
            if (dx * dx + dy * dy <= maxJumpDistSq) {
              bestSecondary = enemyBase;
            }
          }
        }

        if (bestSecondary) {
          const jumpDmg = Math.round(dmg * 0.5);
          if (particles?.addTeslaArc) {
            particles.addTeslaArc(target.x, target.y, bestSecondary.x, bestSecondary.y, '#00f0ff');
          } else {
            particles?.addBeam?.(target.x, target.y, bestSecondary.x, bestSecondary.y, '#00f0ff', 2.8, 0.14);
          }
          particles?.addDistortionRing?.(bestSecondary.x, bestSecondary.y, 26, 0.3, '#00f0ff');
          particles?.addTeslaGroundArcs?.(bestSecondary.x, bestSecondary.y + (bestSecondary.radius || 12) * 0.8);
          if (bestSecondary.takeDamage) {
            bestSecondary.takeDamage(jumpDmg, particles, 'Energy', true, isGold, this);
          } else if (typeof bestSecondary.hp === 'number') {
            bestSecondary.hp -= jumpDmg;
            battlefield?.stats?.recordDamage(this.team, jumpDmg);
          }
        }
      }
    } else {
      resolveHit();
    }

    if (isOrchestra) {
      this.rocketsFired = (this.rocketsFired || 0) + 1;
      if (this.rocketsFired >= 10) {
        this.rocketsFired = 0;
        this.isReloading = true;
        const reloadSec = this.reloadDuration || 10.0;
        this.attackCooldown = reloadSec;
        particles?.addDamageText?.(this.x, this.y - this.radius - 12, `RELOAD (${Math.round(reloadSec)}s)`, 'status');
      }
    }
  }

  fireHyperPanzerCannon(target, battlefield, particles, audio, attackSpeedMultiplier = 1.0) {
    if (!target || target.isDead) return;
    const dir = this.facingDir || (this.team === 'western' ? 1 : -1);
    let muzzleX = this.x + dir * 26;
    let muzzleY = this.y - 4;
    if (this.turretAimState === 'up') {
      muzzleX = this.x - dir * 2;
      muzzleY = this.y - 24;
    } else if (this.turretAimState === 'down') {
      muzzleX = this.x - dir * 2;
      muzzleY = this.y + 20;
    } else if (this.turretAimState === 'left') {
      muzzleX = this.x + dir * 26;
      muzzleY = this.y - 16;
    } else if (this.turretAimState === 'right') {
      muzzleX = this.x + dir * 26;
      muzzleY = this.y + 6;
    }
    const isGold = (this.edition === 'gold');
    const customBulletColor = this.getCustomBulletColorHex ? this.getCustomBulletColorHex() : null;
    const grenadeColor = customBulletColor || '#f59e0b';
    const dmg = 60 * (this.buffDamageMult || 1.0);
    const splashRadius = (this.cardData?.splashRadius || 3.0) * 24; // 72px

    this.barrelHeat = 1.0;
    if (audio?.playPanzerCannon) {
      audio.playPanzerCannon({ x: this.x });
    } else {
      audio?.playExplosion?.('medium', { x: this.x });
    }

    particles?.addMuzzleFlash?.(muzzleX, muzzleY, grenadeColor, 7);
    particles?.addAnamorphicFlare?.(muzzleX, muzzleY, 150, 4.0, 0.18, '#fbbf24');
    particles?.addDistortionRing?.(muzzleX, muzzleY, 26, 0.3, '#f59e0b');
    particles?.addRisingEmbers?.(muzzleX, muzzleY, 4, 10);

    const targetAccuracyDebuff = target?.cardData?.targetAccuracyDebuff || 0;
    const effectiveAccuracy = Math.max(0.05, (this.accuracy || 0.9) - targetAccuracyDebuff - (this.buffAccuracyPenalty || 0));
    const willMiss = this._rng.next() >= Math.min(1, Math.max(0, effectiveAccuracy));
    battlefield?.stats?.recordShot?.(this.team, !willMiss);

    if (willMiss) {
      const missMode = this._rng.range(0, 3);
      let missX, missY;
      if (missMode < 1) {
        missX = target.x + dir * (24 + this._rng.range(12, 24));
        missY = target.y + this._rng.range(-14, 14);
      } else if (missMode < 2) {
        missX = target.x - dir * (24 + this._rng.range(12, 24));
        missY = target.y + this._rng.range(-14, 14);
      } else {
        missX = target.x + this._rng.range(-14, 18) * dir;
        missY = target.y + (this._rng.next() > 0.5 ? 1 : -1) * (20 + this._rng.range(10, 18));
      }
      if (particles?.addBallisticBaseShell) {
        particles.addBallisticBaseShell(muzzleX, muzzleY, missX, missY, () => {
          particles?.addProjectileMiss?.(missX, missY, 'Grenade');
          particles?.addDamageText?.(target.x, target.y - 14, 'MISS', 'miss');
        }, grenadeColor);
      } else {
        particles?.addProjectileMiss?.(missX, missY, 'Grenade');
        particles?.addDamageText?.(target.x, target.y - 14, 'MISS', 'miss');
      }
      return;
    }

    const dist = Math.hypot(target.x - muzzleX, target.y - muzzleY);
    const flightTimeSec = Math.max(0.20, Math.min(0.38, dist / 950));
    const flightTicks = Math.max(1, Math.round(flightTimeSec * 60));

    if (particles?.addBallisticBaseShell) {
      particles.addBallisticBaseShell(muzzleX, muzzleY, target.x, target.y, null, grenadeColor);
    }

    battlefield?.addProjectileSim?.({
      flightTicks,
      targetX: target.x,
      targetY: target.y,
      targetUnitId: target.id,
      isStructureTarget: target.isStructure,
      damage: dmg,
      splashRadius: splashRadius,
      team: this.team,
      projectileType: 'Grenade',
      sourceUnit: this,
      isGoldAttacker: isGold
    });
  }

  fireHyperPanzerRockets(target, battlefield, particles, audio, attackSpeedMultiplier = 1.0) {
    if (!target || target.isDead) return;
    const dir = this.facingDir || (this.team === 'western' ? 1 : -1);
    const drumX = this.x - dir * 4;
    const drumY1 = this.y - 12;
    const drumY2 = this.y - 18;
    const isGold = (this.edition === 'gold');
    const customBulletColor = this.getCustomBulletColorHex ? this.getCustomBulletColorHex() : null;
    const rocketColor = customBulletColor || '#e879f9';
    const rocketTrail = '#c026d3';
    const singleDmg = 30 * (this.buffDamageMult || 1.0);

    if (audio?.playRocketLaunch) {
      audio.playRocketLaunch('normal', { x: this.x, volume: 0.85 });
    } else {
      audio?.playExplosion?.('small', { x: this.x });
    }

    particles?.addMuzzleFlash?.(drumX, drumY1, '#d946ef', 8);
    particles?.addMuzzleFlash?.(drumX, drumY2, '#c026d3', 8);
    particles?.addAnamorphicFlare?.(drumX, drumY1, 120, 3.0, 0.15, '#e879f9');
    particles?.addRisingEmbers?.(drumX, drumY1, 3, 8);

    const speed = 480;
    const dist = Math.hypot(target.x - drumX, target.y - this.y);
    const flightTicks = Math.max(1, Math.round((dist / speed) * 60));

    if (particles?.addDualCurvedMissiles) {
      particles.addDualCurvedMissiles(drumX, drumY1, target, rocketColor, 5, speed, null, rocketTrail);
    } else if (particles?.addProjectile) {
      particles.addProjectile(drumX, drumY1, target, rocketColor, 5, speed);
    }

    const targetAccuracyDebuff = target?.cardData?.targetAccuracyDebuff || 0;
    const effectiveAccuracy = Math.max(0.05, (this.accuracy || 0.9) - targetAccuracyDebuff - (this.buffAccuracyPenalty || 0));

    // Dual synchronized rockets (2 × 30 dmg = 60 total)
    for (let i = 0; i < 2; i++) {
      const willMiss = this._rng.next() >= Math.min(1, Math.max(0, effectiveAccuracy));
      battlefield?.stats?.recordShot?.(this.team, !willMiss);

      if (willMiss) {
        const missOffX = (this._rng.next() > 0.5 ? 1 : -1) * this._rng.range(16, 28);
        const missOffY = (this._rng.next() > 0.5 ? 1 : -1) * this._rng.range(12, 24);
        particles?.addProjectileMiss?.(target.x + missOffX, target.y + missOffY, 'Rocket');
        if (i === 0) {
          particles?.addDamageText?.(target.x, target.y - 14, 'MISS', 'miss');
        }
      } else {
        battlefield?.addProjectileSim?.({
          flightTicks: flightTicks + i * 2,
          targetX: target.x,
          targetY: target.y,
          targetUnitId: target.id,
          isStructureTarget: target.isStructure,
          damage: singleDmg,
          splashRadius: 0,
          team: this.team,
          projectileType: 'Rocket',
          sourceUnit: this,
          isGoldAttacker: isGold,
          customBulletColor: rocketColor
        });
      }
    }
  }

  _triggerHyperPanzerDeathExplosion(battlefield, particles, audio) {
    if (this._hyperPanzerExploded) return;
    this._hyperPanzerExploded = true;

    // Catastrophic death explosion: 100 splash damage within radius 120px (radius 5)
    const splashR = this.deathExplosionRadius || 120;
    const splashDmg = this.deathExplosionDamage || 100;
    if (battlefield) {
      battlefield.dealSplashDamage(
        this.x, this.y, splashR, splashDmg, this.team,
        true, true, particles, 'Grenade', null, false, this
      );
    }
    particles?.addHyperPanzerDeath?.(this, audio);
  }

  // attackerProjectileType: what kind of projectile this damage came from
  // (Bullet | Rocket | Grenade | Energy) - decides whether this unit's own
  // Deflection Rating gets a chance to negate it entirely. Defaults to
  // 'Bullet' for any call site that doesn't know/care (e.g. the legacy
  // per-unitType visual pipeline, DoT ticks, nukes) - deflectionRating is
  // 0 on every unit unless its card explicitly sets one, so that default
  // is a no-op for anything not on the new combat schema.
  _detonateForgottenLoaf(battlefield, particles, audio) {
    if (this.isDead) return;
    this.mineState = 'detonated';
    this.isMineRising = false;
    this.isBurrowed = false;
    this.isBurrowing = false;
    this.isIndestructible = false;

    const splashR = this.splashRadius || (4 * 24);
    const dmg = (this.damage || 70) * (this.buffDamageMult || 1.0);
    if (battlefield?.dealSplashDamage) {
      battlefield.dealSplashDamage(
        this.x, this.y, splashR, dmg, this.team,
        true, true, particles, this.projectileType || 'Grenade',
        null, false, this, false, 5, 2, splashR, true
      );
    }

    const effAudio = audio || (typeof window !== 'undefined' ? window.tcgGame?.audio : null);
    effAudio?.playExplosion?.('medium', { x: this.x });
    particles?.addExplosion?.(this.x, this.y - 28, 42, '#00f0ff', 22);
    particles?.addShockwave?.(this.x, this.y - 28, 65, '#00f0ff', 0.32);
    particles?.addAcidSplash?.(this.x, this.y - 20, 14);
    particles?.addScreenShake?.(4.5, 0.22);

    this.hp = 0;
    this.isDead = true;
    battlefield?.handleUnitDeath?.(this, particles, null);
  }

  takeDamage(amount, particles, attackerProjectileType = 'Bullet', showDamageText = true, isGoldAttacker = false, attacker = null, isReflected = false, options = null) {
    if (this.isDead || this.isParachuting || this.isCinematic || this.isGoldenStatue) return;
    if ((this.isIndestructible || this.isMineRising || this.mineState === 'rising') && !options?.isSuperweapon) return;

    // Sul Forgotten Loaf: When burrowed underground, completely immune to direct non-splash attacks (only damaged by splash or superweapons)
    if (this.isForgottenLoaf && (this.isBurrowed || this.mineState === 'burrowed') && !this.isCocoon && !options?.isSuperweapon && amount < 9999) {
      const isSplashHit = attackerProjectileType === 'Grenade' || attackerProjectileType === 'Rocket' || !!(attacker && attacker.splashRadius > 0);
      if (!isSplashHit) {
        return;
      }
    }

    // Anti Shade: Shadow Phase Shift / Evasion against direct non-splash shots while cloaked and undetected
    if (this.isAntiShade && this.isCloaked && attacker && !attacker.isAntiShade && !attacker.splashRadius && attackerProjectileType !== 'Grenade' && attackerProjectileType !== 'Rocket' && amount < 9999) {
      const bf = this.battlefield || attacker.battlefield;
      const isDetected = bf?.isPositionDetected?.(attacker.team, this.x, this.y);
      if (!isDetected) {
        particles?.addDamageText?.(this.x, this.y - 14, 'MISS', 'miss');
        return;
      }
    }

    // Human Hot Hand: Evades all direct non-splash attacks until she has landed her first strike
    if (this.isHotHand && !this.firstHitDone && !this.isCocoon && !options?.isSuperweapon && amount < 9999) {
      const isSplashHit = attackerProjectileType === 'Grenade' || attackerProjectileType === 'Rocket' || !!(attacker && attacker.splashRadius > 0);
      if (!isSplashHit) {
        this._dodgeAnimTime = 0.45;
        particles?.addDamageText?.(this.x, this.y - 14, 'MISS', 'miss');
        return;
      }
    }

    this.hitFlashTimer = 0.12; // 120ms white arcade hit-flash

    if (this.cardData.unitType === 'Stalker' && this.blinkCooldown <= 0) {
      this.blinkCooldown = 5.0; // Blink once every 5 seconds
      const dir = this.team === 'western' ? -1 : 1;
      this.x += dir * 80; // Blink backwards by 80 pixels
      particles?.addExplosion?.(this.x, this.y, 24, '#00e5ff', 18, 2);
      if (showDamageText && amount < 9999) {
        particles?.addDamageText?.(this.x, this.y - 20, "BLINK!", 'shield');
      }
    }

    const hitDir = this.team === 'western' ? -1 : 1;

    // 🛡️ Bastion Lock: Impacts glance off the massive curved fore-shields rather than the recessed central pod
    let hitX = this.x;
    let hitY = this.y;
    if (this.isBastionLock) {
      this.underFireTimer = 1.2;
      const fwd = this.facingDir || (this.team === 'western' ? 1 : -1);
      const incomingDir = attacker ? (Math.sign(attacker.x - this.x) || fwd) : fwd;
      const shieldForwardDist = (this.deployProgress >= 1.0) ? 16 : 14;
      hitX = this.x + incomingDir * shieldForwardDist;
      const rngY = this._rng ? (this._rng.next() - 0.5) : (Math.random() - 0.5);
      hitY = this.y - 5.0 + rngY * 4.0;
    }

    // Deflection Rating: replaces Armor. A percentage chance this hit
    // bounces off doing zero damage - rolled per hit, on the TARGET's own
    // stat. Energy-type attacks always ignore it (Deflection Rating has no
    // effect against Energy - see js/data/cards.js's Projectile Type
    // documentation).
    if (this.isBastionLock) {
      this.deflectionRating = (this.deployProgress >= 1.0) ? 0.5 : 0.2;
    }
    let effectiveDeflection = Math.min(0.95, this.deflectionRating + (this.deflectionBonus || 0));
    if (this.battlefield?.moduleSystem?.hasCongressBuff(this.team, 'quality_steel')) {
      effectiveDeflection = Math.min(0.95, effectiveDeflection + 0.10);
    }
    if (attackerProjectileType !== 'Energy' && effectiveDeflection > 0 && this._rng.next() < effectiveDeflection) {
      particles?.addDeflection?.(hitX, hitY, hitDir, attackerProjectileType, attacker);
      if (this.isBastionLock) {
        particles?.addBastionShieldRicochet?.(hitX, hitY, hitDir, 2);
      }
      this.battlefield?.addFrameEvent?.({
        type: 'DEFLECTION',
        x: Math.round(hitX),
        y: Math.round(hitY),
        dirX: hitDir,
        projectileType: attackerProjectileType,
        isHumanShooter: !!(attacker?.race === 'Human' || attacker?.cardData?.race === 'Human'),
        isBastionLock: !!this.isBastionLock
      });
      return;
    }

    const effectiveDamage = Math.max(0.5, amount);

    // Ghost Damage Bar: record starting HP before damage and delay catch-up
    if (this.ghostHp === undefined || this.ghostHp < this.hp) {
      this.ghostHp = this.hp;
    }
    this.ghostHpTimer = 0.20;

    // Heavy hit flinch & squash (only on heavy absolute damage, crits, or explosives; continuous fire stays calm)
    const isCritHit = !!(options?.isCrit || attackerProjectileType === 'Rocket' || attackerProjectileType === 'Grenade');
    const isHeavyHit = effectiveDamage >= 25 || isCritHit || !!options?.isSuperweapon;
    if (isHeavyHit) {
      const impactDir = attacker ? (Math.sign(this.x - attacker.x) || hitDir) : hitDir;
      this.hitFlinchTimer = 0.09;
      this.hitFlinchDir = impactDir;
      this.hitFlinchDist = Math.min(4.5, 2.0 + effectiveDamage * 0.04);
      if (isCritHit || effectiveDamage >= 45) {
        this.hitSquashTimer = 0.08;
      }
    }

    // Sul Bio-Lumineszente Sporen & Chitin-Splatter (dezent bei schweren Treffern oder Tod)
    const isSulUnit = (this.race === 'Sul' || this.cardData?.race === 'Sul');
    if (isSulUnit && (isHeavyHit || (this.hp - effectiveDamage <= 0))) {
      particles?.addSulBioSplatter?.(this.x, this.y, hitDir);
    }

    this.hp -= effectiveDamage;

    // Stats only ever count real damage, never overkill - instant-kill
    // effects (nuke/Bio-Plasma/Hyper Tesla Walker) pass amount=99999+ purely
    // to guarantee a kill regardless of HP/armor, not as an actual damage
    // figure. Recording the raw amount would inflate damageDealt/damageTaken
    // (the MVP Spotlight's "TANK" axis) to five-plus digits from a single hit.
    const statDamage = Math.max(0, Math.min(effectiveDamage, this.hp + effectiveDamage));

    const attackerTeam = this.team === 'western' ? 'eastern' : 'western';
    this.battlefield?.stats?.recordDamage(attackerTeam, statDamage);
    this.battlefield?.stats?.recordUnitDamageTaken(this.id, statDamage);
    // Unit MVP Spotlight (docs/UNIT_MVP_PLAN.md 4.1/4.2): only populated when
    // the caller passed an actual attacker reference (DoT ticks, nukes and a
    // few splash paths don't) - a best-effort addition on top of the
    // pre-existing team-level recordDamage() above, not a replacement.
    if (attacker) {
      this.battlefield?.stats?.recordUnitDamage(attacker.id, statDamage);
      if (attacker.team !== this.team) {
        if (!this.damageContributors) this.damageContributors = new Map();
        this.damageContributors.set(attacker.id, (this.damageContributors.get(attacker.id) || 0) + statDamage);
      }
    }

    // Sul Rejected Loaf: Passive 50% damage reflection back to attacker as chitin shards
    if (!isReflected && this.reflectDamageRatio > 0 && effectiveDamage > 0 && attacker && !attacker.isDead && attacker !== this) {
      const reflectedDmg = Math.round(effectiveDamage * this.reflectDamageRatio);
      if (reflectedDmg > 0 && this.battlefield) {
        const speed = 420;
        const dist = Math.hypot(attacker.x - this.x, attacker.y - this.y);
        const flightTicks = Math.max(1, Math.round((dist / speed) * 60));
        if (particles?.addChitinShard) {
          particles.addChitinShard(this.x, this.y, attacker, '#c084fc', speed);
        } else {
          particles?.addTracer?.(this.x, this.y, attacker.x, attacker.y, '#c084fc', 0.12);
        }
        particles?.addHitSparks?.(this.x, this.y, 'plasma', 5, hitDir);
        this.battlefield.addFrameEvent?.({
          type: 'REFLECT_SHARD',
          x: Math.round(this.x),
          y: Math.round(this.y),
          tx: Math.round(attacker.x),
          ty: Math.round(attacker.y),
          targetUnitId: attacker.id,
          attackerUnitId: this.id
        });
        this.battlefield.addProjectileSim({
          flightTicks,
          targetX: attacker.x,
          targetY: attacker.y,
          targetUnitId: attacker.id,
          isStructureTarget: !!attacker.isStructure,
          damage: reflectedDmg,
          team: this.team,
          projectileType: 'Bullet', // shards are bullets and can be deflected!
          sourceUnit: this,
          isReflected: true
        });
      }
    }

    // Spawn rich archetype hit sparks (budgeted: small rapid hits scale down particle count & throttle frequency)
    const sparkType = this.race === 'Sul' ? 'bio' : 'metal';
    const sparkCount = effectiveDamage > 25 ? 14 : (effectiveDamage <= 1.0 ? 2 : 7);
    const nowAge = this.age || 0;
    const shouldSpark = effectiveDamage > 1.5 || (nowAge - (this._lastHitSparkAge || 0)) >= 0.16;
    if (shouldSpark) {
      this._lastHitSparkAge = nowAge;
      particles?.addHitSparks?.(hitX, hitY, sparkType, sparkCount, hitDir);
      if (this.isBastionLock && attackerProjectileType === 'Bullet') {
        particles?.addBastionShieldRicochet?.(hitX, hitY, hitDir, 1);
      }
    }

    if (showDamageText && amount < 9999) {
      if (isGoldAttacker) {
        particles?.addDamageText?.(this.x, this.y, Math.round(effectiveDamage), 'gold');
      } else if (effectiveDamage > 25) {
        particles?.addScreenShake?.(3.5, 0.15);
        particles?.addDamageText?.(this.x, this.y, Math.round(effectiveDamage), 'crit');
      } else {
        particles?.addDamageText?.(this.x, this.y, Math.round(effectiveDamage), 'normal');
      }
    }

    if (this.hp <= 0) {
      // Sul Forgotten Loaf: When shot down while sprinting to the frontline, does NOT die on the spot!
      // Instead, immediately inflates its hot-air balloon and rises for 1.0s before detonating for 70 splash.
      if (this.isForgottenLoaf && this.mineState === 'sprinting' && !this.isCocoon && !options?.isSuperweapon && amount < 9999) {
        this.hp = 1;
        this.mineState = 'rising';
        this.isMineRising = true;
        this.mineRiseTimer = 1.0;
        this.isBurrowed = false;
        this.isBurrowing = false;
        this.wasBurrowedBeforeRising = false;
        this.speed = 0;
        this.baseSpeed = 0;
        this.isWalking = false;
        this.isUntargetable = true;
        this.isIndestructible = true;
        this.mass = Infinity;
        this.burrowAnchorX = this.x;
        this.burrowAnchorY = this.y;
        return;
      }

      if (this.isStickman && !this.isCocoon && !options?.isSuperweapon) {
        const isSplash = (attackerProjectileType === 'Rocket' || attackerProjectileType === 'Grenade') || !!(attacker && ((attacker.splashRadius > 0) || attacker.splashLine || attacker.isKamikaze));
        this.battlefield?.stats?.recordKill(attackerTeam, this.cardData.cost || 0, this.isAir && !this.isStructure, isSplash);
        this.distributeKillCredit(this.battlefield);
        const deathAudio = (typeof window !== 'undefined') ? window.tcgGame?.audio : null;
        this._collapseIntoStickmanObstacle(this.battlefield, particles, deathAudio);
        return;
      }

      if (this.isCocoon) {
        this.isCocoon = false;
        if (this.battlefield?.cocoons) {
          const cIdx = this.battlefield.cocoons.indexOf(this);
          if (cIdx !== -1) this.battlefield.cocoons.splice(cIdx, 1);
        }
      }

      this.hp = 0;
      this.isDead = true;
      const isSplash = (attackerProjectileType === 'Rocket' || attackerProjectileType === 'Grenade') || !!(attacker && ((attacker.splashRadius > 0) || attacker.splashLine || attacker.isKamikaze));
      this.battlefield?.stats?.recordKill(attackerTeam, this.cardData.cost || 0, this.isAir && !this.isStructure, isSplash);
      this.distributeKillCredit(this.battlefield);
      this.battlefield?.handleUnitDeath?.(this, particles, attacker);

      // Heavy vehicle / mech dynamic wreckage debris (1 of 3 archetypes randomly, overkill kills >= 25 dmg)
      const uType = this.unitType || this.cardData?.unitType;
      const isHeavyMech = (uType === 'Panzer' || uType === 'HyperPanzer' || uType === 'A9Grizzly' || uType === 'A9Piranha' || uType === 'A9Pigeon' || uType === 'DeployableCannon' || uType === 'Deployable_Cannon' || uType === 'TeslaWalker' || uType === 'HyperTeslaWalker');
      if (isHeavyMech && isHeavyHit) {
        particles?.addVehicleWreckage?.(this.x, this.y, this.team, hitDir);
      }

      // takeDamage() bekommt (anders als die()) kein audio-Parameter
      // durchgereicht - Zugriff daher wie in particles.js über den globalen
      // window.tcgGame-Fallback. Dies ist der Sterbe-Pfad, den gewöhnliche
      // Kampf-Kills tatsächlich nehmen (resolveNewCombatAttack() ruft
      // target.takeDamage() direkt auf, nie target.die()) - vorher spielte
      // hier gar kein Sterbegeräusch, siehe _playDeathSound() oben.
      const deathAudio = (typeof window !== 'undefined') ? window.tcgGame?.audio : null;
      this._playDeathSound(deathAudio);

      // A9 Piranha: Motor-Loop stoppen.
      if (this.motorLoopActive) {
        deathAudio?.stopA9PiranhaMotorLoop?.();
        this.motorLoopActive = false;
      }

      // Anti Shade: Atem-Loop stoppen.
      if (this.isAntiShade) {
        const otherAntiShade = this.battlefield?.units?.some(u => u !== this && !u.isDead && u.isAntiShade);
        if (!otherAntiShade) {
          deathAudio?.stopAntiShadeBreath?.();
        }
        this.breathAudioActive = false;
      }

      // Sul Defective Loaf: Inject dissolving acid into locked target upon death
      if (this.hasTether && this.tetherTargetId) {
        const tethered = this.battlefield?.getUnitOrBaseById?.(this.tetherTargetId);
        if (tethered && !tethered.isDead && !tethered.isGoldenStatue) {
          tethered.acidStacks = (tethered.acidStacks || 0) + 1;
          particles?.addDamageText?.(tethered.x, tethered.y - 20, 'ACID DISSOLVE', 'crit');
          particles?.addAcidSplash?.(tethered.x, tethered.y, 16);
        }
        this.tetherTargetId = null;
      }

      // Sul Harvesting Pillar: Release locked rooted target upon death
      if (this.hasDualTether) {
        if (this.dualTetherTarget1Id) {
          const t1 = this.battlefield?.getUnitOrBaseById?.(this.dualTetherTarget1Id);
          if (t1) t1.isRooted = false;
          this.dualTetherTarget1Id = null;
        }
        if (this.dualTetherTarget2Id) {
          const t2 = this.battlefield?.getUnitOrBaseById?.(this.dualTetherTarget2Id);
          if (t2) t2.isRooted = false;
          this.dualTetherTarget2Id = null;
        }
        this.tether1LockTimer = 0;
      }

      // Human Hacker: Release hacked disabled target upon death
      if (this.isHacker) {
        this._releaseHackerTarget(this.battlefield, particles);
      }

      // Bunker crew evacuation
      if (this.cardData.isBunker) {
        this.spawnBunkerCrew(this.battlefield);
      }
      if (this.ejectPilotOnDeath || this.cardData.unitType === 'A9Piranha') {
        this.spawnParachutePilot(this.battlefield);
      }
      if (this.isEnforcerVulture) {
        this.spawnVultureBrood(this.battlefield);
      }
      if (this.isClaimingPillar) {
        this._triggerClaimingPillarDeathBurst(this.battlefield, particles, deathAudio);
      }

      if (this.isHyperPanzer) {
        this._triggerHyperPanzerDeathExplosion(this.battlefield, particles, deathAudio);
      } else if (this.cardData?.unitType === 'HyperDevilPatrol' || this.name === 'Hyper Devil Patrol') {
        particles?.addWildRocketDeath?.(this);
      } else if (this.cardData?.unitType === 'HarvestingPillar' || this.name === 'Harvesting Pillar') {
        particles?.addHarvestingBloodPlasmaExplosion?.(this);
      } else if (this.isClaimingPillar) {
        // Handled above by _triggerClaimingPillarDeathBurst
      } else if (this.isPulp) {
        particles?.addPulpPencilTrickDeath?.(this, deathAudio);
      } else if (this.cardData?.unitType === 'SentinalLock') {
        particles?.addSentinalLockTripleShatter?.(this);
      } else if (this.isParasiteVulture || this.cardData?.unitType === 'ParasiteVulture' || this.name === 'Parasite Vulture') {
        particles?.addParasiteVultureDeath?.(this, deathAudio);
      } else if (this.isPinkBeret) {
        particles?.addPinkBeretKissDeath?.(this, deathAudio);
      } else if (this.isLootingVulture || this.cardData?.unitType === 'LootingVulture' || this.name === 'Looting Vulture') {
        particles?.addLootingVultureDeath?.(this, deathAudio);
      } else if (this.isHacker) {
        particles?.addHackerDeath?.(this, deathAudio);
      } else if (this.isA9Pigeon || this.cardData?.unitType === 'A9Pigeon' || this.name === 'A9 Pigeon') {
        particles?.addA9PigeonDeathFlock?.(this, deathAudio);
      } else if (this.edition === 'gold') {
        particles?.addGoldUnitDeath?.(this);
      } else {
        // 1. Shatter entire unit into its individual pixel voxel shards that settle and lie on the ground!
        particles?.addUnitDeathShatter?.(this);
        if (this.isStructure) {
          particles?.addStructureDebris?.(this.x, this.y, this.radius);
        }

        // 2. Rising localized smoke column (lingers and merges into frontline battlefield haze/smog)
        particles?.addLingeringSmokeColumn?.(this.x, this.y, this.isMassive ? 4.5 : 2.8, this.isMassive ? 2.0 : 1.0);

        // 3. Spectacular Faction-Specific Death Explosions & Particle Bursts
        if (this.race === 'Sul') {
          particles?.addAcidSplash?.(this.x, this.y, 18);
          particles?.addExplosion?.(this.x, this.y, this.radius * 1.8, '#84cc16', 24, 3);
          particles?.addHitSparks?.(this.x, this.y, 'bio', 16);
        } else {
          // Human (and default)
          particles?.addExplosion?.(this.x, this.y, this.radius * 2.0, '#f97316', 26, 4);
          particles?.addShellCasing?.(this.x, this.y, 1);
          particles?.addShellCasing?.(this.x, this.y, -1);
        }
      }
    }
  }

  _findClosestEnemyMobileUnit(battlefield) {
    if (!battlefield?.units) return null;
    const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
    let closest = null;
    let minDistSq = Infinity;
    for (let i = 0; i < battlefield.units.length; i++) {
      const u = battlefield.units[i];
      if (!u || u.isDead || u.hp <= 0 || u.state === 'DEAD' || u.team !== enemyTeam) continue;
      if (u.isStructure || u.isBuilding || u.isBunker || u.isTurret || u.cardData?.isStructure || u.cardData?.isBuilding) continue;
      if (u.isUntargetable || u.isCinematic || u.isGoldenStatue || u.isIndestructible || u.isParachuting) continue;
      if (u.isHacker && u.isTranscendent) continue;

      const dx = u.x - this.x;
      const dy = u.y - this.y;
      const dSq = dx * dx + dy * dy;
      if (dSq < minDistSq) {
        minDistSq = dSq;
        closest = u;
      }
    }
    return closest;
  }

  _findFurthestEnemyUnit(battlefield) {
    if (!battlefield?.units) return null;
    const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
    let furthest = null;
    let maxDistSq = -1;
    for (let i = 0; i < battlefield.units.length; i++) {
      const u = battlefield.units[i];
      if (!u || u.isDead || u.hp <= 0 || u.state === 'DEAD' || u.team !== enemyTeam) continue;
      if (u.isStructure || u.isBuilding || u.isBunker || u.isTurret || u.cardData?.isStructure || u.cardData?.isBuilding) continue;
      if (u.isUntargetable || u.isCinematic || u.isGoldenStatue || u.isParachuting) continue;

      const dx = u.x - this.x;
      const dy = u.y - this.y;
      const dSq = dx * dx + dy * dy;
      if (dSq > maxDistSq) {
        maxDistSq = dSq;
        furthest = u;
      }
    }
    return furthest;
  }

  // A9 Pigeon Bomb Target Priority:
  // 1. Mobile enemy ground unit in range (forward hemisphere)
  // 2. Enemy field structure in range (forward hemisphere) — NEVER main base!
  // Eligible ONLY if NO enemy air unit is closer to A9 Pigeon than the chosen ground target.
  _findPigeonBombTarget(battlefield) {
    if (!battlefield?.units) return null;
    const enemyTeam = this.team === 'western' ? 'eastern' : 'western';
    const facingDir = this.team === 'western' ? 1 : -1;

    let minAirDistSq = Infinity;
    let bestGroundUnit = null;
    let minGroundUnitDistSq = Infinity;
    let bestStructure = null;
    let minStructDistSq = Infinity;

    for (let i = 0; i < battlefield.units.length; i++) {
      const u = battlefield.units[i];
      if (!u || u.isDead || u.hp <= 0 || u.state === 'DEAD' || u.team !== enemyTeam) continue;
      if (u.isUntargetable || u.isCinematic || u.isGoldenStatue || u.isParachuting || u.isCocoon) continue;
      if (u.executionTimer && u.executionTimer > 0) continue;
      if (u.isCloaked && !battlefield.isPositionDetected(this.team, u.x, u.y)) continue;
      if (u.isBase || u.name?.includes('Base') || u.name?.includes('Fortress')) continue;

      const dx = u.x - this.x;
      const dy = u.y - this.y;
      const dSq = dx * dx + dy * dy;
      const isAirUnit = !!(u.isAir || u.elementType === 'Air');

      if (isAirUnit) {
        if (dSq < minAirDistSq) {
          minAirDistSq = dSq;
        }
        continue;
      }

      // Must be in forward hemisphere (aircraft cannot turn around) and within attack range
      const forwardDist = dx * facingDir;
      if (forwardDist < -14) continue;
      if (!battlefield.isInRange(this, u)) continue;

      const isFieldStructure = !!(u.isStructure || u.cardData?.isStructure || u.cardData?.type === 'Structure');
      if (!isFieldStructure) {
        if (dSq < minGroundUnitDistSq) {
          minGroundUnitDistSq = dSq;
          bestGroundUnit = u;
        }
      } else {
        if (dSq < minStructDistSq) {
          minStructDistSq = dSq;
          bestStructure = u;
        }
      }
    }

    const candidate = bestGroundUnit || bestStructure;
    const candDistSq = bestGroundUnit ? minGroundUnitDistSq : minStructDistSq;
    if (!candidate) return null;

    // Bomb run triggers ONLY if no enemy air unit is closer than the ground target!
    if (minAirDistSq <= candDistSq) return null;

    return candidate;
  }

  _enterTranscendentState(battlefield, particles, audio) {
    this._releaseHackerTarget(battlefield, particles, audio);
    this.isTranscendent = true;
    this.isIndestructible = true;
    this.transcendentTimer = 10.0;
    this._transcendentHpGrace = true;
    this.handRevealTimer = 0;
    this.hackerLockTimer = 0;
    this.hackerState = 'TRANSCENDENT';
    this.speed = 0;
    this.baseSpeed = 0;
    audio?.playHackerTranscendence?.({ x: this.x });
    particles?.addDamageText?.(this.x, this.y - 24, 'HYPER TRANSCENDENT', 'crit');
    particles?.addShockwave?.(this.x, this.y, 45, '#38bdf8', 0.5);
  }

  reactivateFromHack(battlefield = null, particles = null, audio = null) {
    if (!this.isDisabled && !this.disabledByHackerId) return;
    this.isDisabled = false;
    this.disabledByHackerId = null;

    if (!this.isStructure) {
      const origSpeed = (this.cardData?.speed || 2.25) * SPEED_MULTIPLIER;
      this.baseSpeed = origSpeed;
      this.speed = origSpeed;
    }
    this.attackCooldown = Math.min(this.attackCooldown || 0, 0.2);

    if (particles) {
      particles.addDamageText?.(this.x, this.y - 20, 'REACTIVATED', 'shield');
      particles.addHitSparks?.(this.x, this.y, 'cyan', 6);
      particles.addShockwave?.(this.x, this.y, 22, '#00f0ff', 0.2);
    }
    const effAudio = audio || (typeof window !== 'undefined' ? window.tcgGame?.audio : null);
    effAudio?.playHackerHarpoon?.({ x: this.x, volume: 0.5 });

    const bf = battlefield || this.battlefield || (typeof window !== 'undefined' ? window.tcgGame?.battlefield : null);
    if (bf?.findBestTarget) {
      this.target = bf.findBestTarget(this);
    }
  }

  _releaseHackerTarget(battlefield = null, particles = null, audio = null) {
    if (!this.hackerTargetId) return;
    const targetId = this.hackerTargetId;
    this.hackerTargetId = null;
    this.isHacking = false;
    this._harpoonLatched = false;

    const bf = battlefield || this.battlefield || (typeof window !== 'undefined' ? window.tcgGame?.battlefield : null);
    const target = bf?.getUnitOrBaseById?.(targetId) || bf?.getUnitById?.(targetId) || bf?.units?.find(u => u.id === targetId);
    if (target && !target.isDead) {
      if (typeof target.reactivateFromHack === 'function') {
        target.reactivateFromHack(bf, particles, audio);
      } else {
        target.isDisabled = false;
        target.disabledByHackerId = null;
        if (!target.isStructure) {
          const origSpeed = (target.cardData?.speed || 2.25) * SPEED_MULTIPLIER;
          target.baseSpeed = origSpeed;
          target.speed = origSpeed;
        }
        target.attackCooldown = Math.min(target.attackCooldown || 0, 0.2);
        particles?.addDamageText?.(target.x, target.y - 20, 'REACTIVATED', 'shield');
        particles?.addHitSparks?.(target.x, target.y, 'cyan', 6);
      }
    }
  }
}
